import { Controller } from "@hotwired/stimulus"
import { savePlan, loadPlan, replacePlan, isSectionIncluded, isNotAttendingSchool, matchesServiceLink } from "plan_storage"
import { renderIcons } from "lucide_icons"
import { normCosName } from "who_lives_link"
import { COS_GROUPS } from "cos_groups"
import { resolvePrintVendor } from "vendor_display"
import { scheduleTimeText, scheduleUnitText, scheduleHasContent } from "schedule_fields"
import { findOpenQuestions } from "tbd_questions"

// Print-preview controller — assembles the print-page stack from the
// plan in localStorage. Native counterpart of pcp_builder.html's
// renderPrintView(true, false) (the iframe editor's Review tab).
//
// Each ported section has its own renderer that mirrors the visual
// structure of the iframe editor's getPrintSection branch. Sections
// without a dedicated renderer fall back to a generic listed card so
// the section's data is still visible.
//
// Deferred (tracked in SCOP-004D playbook docs/native-editor-port.md):
//   - Auto-pagination via DOM probe so sections that overflow a page
//     split cleanly across multiple .print-page wrappers
//   - At-a-Glance Summary, Life Photo page
//   - Page-footer side strip and page header / nav rail markup
//     (CSS in editor_print.css already; the controller just doesn't
//     emit the markup yet)
//   - Cross-reference badges ("Goal N.M", "Barrier N.M") inside
//     Current Services / Unmet Needs
//   - Theme polish per channel (banners, bg patterns)
//   - resourceCalendar timeline grid

const STORAGE_KEY = "pcp_plan_data"

// EDIT_THEME is the fixed indigo theme the production pcp_builder.html
// applies to the root document (line 4525). The PDF helper calls
// applyTheme() BEFORE replacing body.innerHTML, so the print render
// effectively inherits EDIT_THEME's gradient — not the plan's chosen
// theme. We mirror that behavior here so the cover matches.
const EDIT_THEME = {
  primary:  "#6366f1",
  gradient: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)"
}

const THEMES = {
  celebration: { label: "Celebration",  primary: "#D946EF", gradient: "linear-gradient(135deg, #D946EF 0%, #8B5CF6 50%, #EC4899 100%)", bgIcons: ["party-popper","sparkles","gift","heart","star","cake","music","smile"] },
  sunshine:    { label: "Sunshine",     primary: "#F59E0B", gradient: "linear-gradient(135deg, #F59E0B 0%, #FBBF24 50%, #F59E0B 100%)", bgIcons: ["sun","sunrise","sunset","cloud-sun","flower","smile","heart","rainbow"] },
  mountain:    { label: "Mountain",     primary: "#059669", gradient: "linear-gradient(135deg, #059669 0%, #34D399 100%)", bgIcons: ["mountain","mountain-snow","trees","compass","flag","map","footprints","tent"] },
  ocean:       { label: "Ocean",        primary: "#0EA5E9", gradient: "linear-gradient(135deg, #0284C7 0%, #38BDF8 100%)", bgIcons: ["waves","ship","anchor","fish","droplet","cloud-drizzle","wind","umbrella"] },
  blue:        { label: "Blue",         primary: "#2563EB", gradient: "linear-gradient(135deg, #1D4ED8 0%, #3B82F6 40%, #60A5FA 100%)", bgIcons: ["pen-tool","pencil","palette","brush","figma","image","frame","sparkles"] },
  contrast:    { label: "Contrast",     primary: "#B91C1C", gradient: "linear-gradient(135deg, #B91C1C 0%, #DC2626 40%, #991B1B 100%)", bgIcons: ["train-front","train-track","map-pin","compass","luggage","ticket","clock","signpost"] },
  carrace:     { label: "Car Race",     primary: "#DC2626", gradient: "linear-gradient(135deg, #DC2626 0%, #111827 50%, #DC2626 100%)", bgIcons: ["car-front","flag","gauge","trophy","timer","fuel","route","traffic-cone"] },
  forest:      { label: "Forest",       primary: "#166534", gradient: "linear-gradient(135deg, #166534 0%, #15803D 100%)", bgIcons: ["trees","tree-pine","leaf","sprout","bird","squirrel","flower-2","bug"] },
  sunset:      { label: "Sunset",       primary: "#C2410C", gradient: "linear-gradient(135deg, #C2410C 0%, #EA580C 100%)", bgIcons: ["sunset","sunrise","cloud-sun","palmtree","umbrella","flame","wind","feather"] },
  berry:       { label: "Berry",        primary: "#BE185D", gradient: "linear-gradient(135deg, #BE185D 0%, #DB2777 100%)", bgIcons: ["cherry","apple","grape","citrus","flower","flower-2","heart","candy"] },
  lavender:    { label: "Lavender",     primary: "#7C3AED", gradient: "linear-gradient(135deg, #7C3AED 0%, #8B5CF6 100%)", bgIcons: ["flower-2","flower","sparkles","cloud","moon","star","wind","feather"] },
  midnight:    { label: "Midnight",     primary: "#1E3A8A", gradient: "linear-gradient(135deg, #1E3A8A 0%, #172554 100%)", bgIcons: ["star","stars","moon","sparkles","zap","sun-moon","cloud-moon","eclipse"] },
  mint:        { label: "Mint",         primary: "#0D9488", gradient: "linear-gradient(135deg, #0D9488 0%, #14B8A6 100%)", bgIcons: ["leaf","leaves","sprout","flower","droplet","snowflake","circle","clover"] }
}

// Colorful section glyphs — mirrors the editor sidebar's
// native_section_icon helper exactly so the right-edge nav rail on
// each preview page reads with the same brand-mark aesthetic as the
// editor rail. (Plain Lucide line icons made the preview rail look
// monochrome / unrelated to the editor.)
const NATIVE_ICONS = {
  cover:            `<svg viewBox="0 0 24 24" fill="none"><rect x="5" y="3" width="14" height="18" rx="2" fill="#a358df"/><rect x="8" y="6" width="8" height="1.6" rx="0.6" fill="#fff"/><rect x="8" y="9" width="8" height="1.6" rx="0.6" fill="#fff" opacity="0.8"/><rect x="8" y="12" width="6" height="1.6" rx="0.6" fill="#fff" opacity="0.6"/></svg>`,
  toc:              `<svg viewBox="0 0 24 24" fill="none"><circle cx="6" cy="7" r="1.5" fill="#0073ea"/><circle cx="6" cy="12" r="1.5" fill="#fdab3d"/><circle cx="6" cy="17" r="1.5" fill="#00c875"/><rect x="10" y="6" width="11" height="2" rx="0.6" fill="#a358df"/><rect x="10" y="11" width="11" height="2" rx="0.6" fill="#0073ea"/><rect x="10" y="16" width="9" height="2" rx="0.6" fill="#ff158a"/></svg>`,
  client:           `<svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="4" fill="#0073ea"/><path d="M4 21c0-4 4-7 8-7s8 3 8 7" fill="#66ccff"/></svg>`,
  about:            `<svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" fill="#a358df"/><circle cx="12" cy="8" r="1.4" fill="#fff"/><rect x="10.6" y="10.4" width="2.8" height="7" rx="1.4" fill="#fff"/></svg>`,
  circleOfSupport:  `<svg viewBox="0 0 24 24" fill="none"><circle cx="8" cy="9" r="3" fill="#00c875"/><circle cx="16.5" cy="10" r="2.6" fill="#fdab3d"/><circle cx="12" cy="16" r="2.8" fill="#ff158a"/><path d="M3.5 21c.5-3 3-4.5 5-4.5M21 21c-.5-2.7-2.5-4-4.5-4" stroke="#e2445c" stroke-width="1.6" stroke-linecap="round"/></svg>`,
  communication:    `<svg viewBox="0 0 24 24" fill="none"><path d="M4 6c0-1.1.9-2 2-2h12c1.1 0 2 .9 2 2v9c0 1.1-.9 2-2 2h-9l-4 4v-4H6c-1.1 0-2-.9-2-2V6z" fill="#66ccff"/><circle cx="9" cy="10" r="1" fill="#fff"/><circle cx="13" cy="10" r="1" fill="#fff"/><circle cx="17" cy="10" r="1" fill="#fff"/></svg>`,
  wonderfulThings:  `<svg viewBox="0 0 24 24" fill="none"><polygon points="12,3 14.6,9.4 21.5,9.9 16.2,14.4 17.8,21 12,17.3 6.2,21 7.8,14.4 2.5,9.9 9.4,9.4" fill="#fdab3d"/><circle cx="19.5" cy="4.5" r="1.2" fill="#ff158a"/><circle cx="4.5" cy="6" r="0.9" fill="#a358df"/></svg>`,
  notSoFavorites:   `<svg viewBox="0 0 24 24" fill="none"><path d="M12 4c-1.5-2-4-3-6-2.5-3 .8-4 4-2 7l8 9 8-9c2-3 1-6.2-2-7-2-.5-4.5.5-6 2.5z" fill="#c4c4c4"/><line x1="6" y1="6" x2="18" y2="18" stroke="#e2445c" stroke-width="2.5" stroke-linecap="round"/></svg>`,
  dayInLife:        `<svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="13" r="4.2" fill="#fdab3d"/><path d="M6.5 8.5L8 10M17.5 8.5L16 10M3 13h2M19 13h2M11 4v2.5" stroke="#ff158a" stroke-width="1.8" stroke-linecap="round"/><path d="M2.5 19h19" stroke="#0073ea" stroke-width="1.8" stroke-linecap="round"/></svg>`,
  resourceCalendar: `<svg viewBox="0 0 24 24" fill="none"><rect x="3" y="5" width="18" height="16" rx="2.5" fill="#0086c0"/><rect x="3" y="5" width="18" height="4" fill="#005a85"/><rect x="6" y="11.5" width="3" height="2.4" rx="0.6" fill="#fff"/><rect x="11" y="11.5" width="3" height="2.4" rx="0.6" fill="#fdab3d"/><rect x="16" y="11.5" width="3" height="2.4" rx="0.6" fill="#fff"/><rect x="6" y="15.5" width="3" height="2.4" rx="0.6" fill="#00c875"/><rect x="11" y="15.5" width="3" height="2.4" rx="0.6" fill="#fff"/><rect x="16" y="15.5" width="3" height="2.4" rx="0.6" fill="#ff158a"/></svg>`,
  important:        `<svg viewBox="0 0 24 24" fill="none"><path d="M12 21s-7-4.5-9-9.5C1.5 7 4.5 3 8 3c2 0 3 1 4 2.5C13 4 14 3 16 3c3.5 0 6.5 4 5 8.5-2 5-9 9.5-9 9.5z" fill="#e2445c"/><circle cx="9" cy="9" r="1.2" fill="#fff" opacity="0.7"/></svg>`,
  bigDreams:        `<svg viewBox="0 0 24 24" fill="none"><path d="M12 2c2 2 4 4 4 8 0 3-1 5-2 6h-4c-1-1-2-3-2-6 0-4 2-6 4-8z" fill="#9d50dd"/><path d="M9 18l-2 4M15 18l2 4M12 18v4" stroke="#fdab3d" stroke-width="1.6" stroke-linecap="round"/><circle cx="12" cy="10" r="1.5" fill="#fff"/></svg>`,
  supportMatch:     `<svg viewBox="0 0 24 24" fill="none"><circle cx="9" cy="9" r="3.5" fill="#037f4c"/><circle cx="16" cy="13" r="3" fill="#00c875"/><path d="M2 21c.5-3.5 3.5-5.5 7-5.5" stroke="#0073ea" stroke-width="1.8" stroke-linecap="round" fill="none"/><path d="M11 21c.5-2.5 2.5-4 5-4" stroke="#0073ea" stroke-width="1.8" stroke-linecap="round" fill="none"/></svg>`,
  working:          `<svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" fill="#00c875"/><path d="M8 12l3 3 5-6" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>`,
  barriers:         `<svg viewBox="0 0 24 24" fill="none"><path d="M3 18h18l-2.5-4H5.5z" fill="#ff642e"/><path d="M5.5 14l1.5-3h10l1.5 3" stroke="#ff158a" stroke-width="1.8" stroke-linecap="round" fill="none"/><path d="M9 11V7M15 11V7" stroke="#0f172a" stroke-width="1.8" stroke-linecap="round"/></svg>`,
  goals:            `<svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="8.5" fill="none" stroke="#e2445c" stroke-width="2"/><circle cx="12" cy="12" r="5" fill="none" stroke="#fdab3d" stroke-width="2"/><circle cx="12" cy="12" r="2" fill="#00c875"/></svg>`,
  currentServices:  `<svg viewBox="0 0 24 24" fill="none"><rect x="3" y="6" width="18" height="14" rx="2" fill="#579bfc"/><path d="M8 6V4c0-1 .5-1.5 1.5-1.5h5c1 0 1.5.5 1.5 1.5v2" stroke="#0073ea" stroke-width="1.8" fill="none"/><rect x="6" y="9" width="12" height="2" rx="0.5" fill="#fff"/><rect x="6" y="13" width="8" height="2" rx="0.5" fill="#fff" opacity="0.8"/></svg>`,
  unmetNeeds:       `<svg viewBox="0 0 24 24" fill="none"><polygon points="12,3 21,20 3,20" fill="#f59e0b"/><rect x="11" y="10" width="2" height="5" rx="1" fill="#fff"/><rect x="11" y="16.5" width="2" height="2" rx="1" fill="#fff"/></svg>`,
  review:           `<svg viewBox="0 0 24 24" fill="none"><rect x="5" y="3" width="14" height="18" rx="2" fill="#1f76c2"/><path d="M9 11l2 2 4-5" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" fill="none"/><line x1="8" y1="17" x2="16" y2="17" stroke="#fff" stroke-width="1.6" opacity="0.6" stroke-linecap="round"/></svg>`,
  facilitator:      `<svg viewBox="0 0 24 24" fill="none"><circle cx="7" cy="7" r="2.5" fill="#444444"/><circle cx="17" cy="7" r="2.5" fill="#0073ea"/><circle cx="12" cy="18" r="2.5" fill="#a358df"/><path d="M7 9.5v3M17 9.5v3M9 12c2 1 4 3 5 5M15 12c-2 1-4 3-5 5" stroke="#a0a3aa" stroke-width="1.4" stroke-linecap="round" fill="none"/></svg>`,
  lifePhoto:        `<svg viewBox="0 0 24 24" fill="none"><rect x="3" y="5" width="18" height="14" rx="2" fill="#fdab3d"/><circle cx="9" cy="11" r="2" fill="#fff"/><path d="M3 17l5-4 4 3 4-5 5 5" stroke="#fff" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>`
}

const SECTIONS = {
  client:           { icon: "book-marked",     label: "Client",                            iconColor: "#94a3b8" },
  about:            { icon: "user",            label: "Basic Details About [FirstName]",    iconColor: "#f472b6" },
  circleOfSupport:  { icon: "custom-ring-5-center", label: "Circle of Support",             iconColor: "#a78bfa" },
  communication:    { icon: "message-square",  label: "Behavior Communication Chart",      iconColor: "#60a5fa" },
  wonderfulThings:  { icon: "thumbs-up",       label: "Strengths & more",                  iconColor: "#f472b6" },
  notSoFavorites:   { icon: "thumbs-down",     label: "Things That Can Be Challenging",    iconColor: "#f87171" },
  dayInLife:        { icon: "clock",           label: "A Day in the Life",                 iconColor: "#60a5fa" },
  important:        { icon: "scale",           label: "Important To vs. For",              iconColor: "#a78bfa" },
  bigDreams:        { icon: "cloud-snow",      label: "[FirstName]'s Hopes and Big Dreams", iconColor: "#a78bfa" },
  supportMatch:     { icon: "user-check",      label: "Matching Support Tool",             iconColor: "#fb923c" },
  working:          { icon: "check-circle",    label: "What Works & Doesn't",              iconColor: "#22c55e" },
  barriers:         { icon: "construction",    label: "Current Barriers",                  iconColor: "#facc15" },
  goals:            { icon: "target",          label: "SMART Goals",                       iconColor: "#4ade80", iconFill: false },
  currentServices:  { icon: "briefcase",       label: "Current Services",                  iconColor: "#60a5fa" },
  unmetNeeds:       { icon: "alert-triangle",  label: "Unmet Needs",                       iconColor: "#818cf8", iconFill: false },
  resourceCalendar: { icon: "calendar-range",  label: "Service Calendar",                  iconColor: "#2dd4bf" },
  review:           { icon: "clipboard-check", label: "Review",                            iconColor: "#94a3b8" },
  brochures:        { icon: "file-text",       label: "Brochure",                          iconColor: "#10b981" }
}

// ── Section order ─────────────────────────────────────────────────
// The editor's Section Tree is the source of truth for section order.
// It lives in localStorage under "pcpm.sidebar_order_v1" (written by
// sidebar_reorder_controller when the user drags a row), defaulting to
// the server's SECTION_KEYS. The preview / Table of Contents follows the
// SAME order so the two always read identically.
//
// NOTE: the legacy plan.meta.sectionOrder (from the old iframe editor) is
// intentionally NOT used — it drifted from the native editor (Service
// Calendar mid-document, Barriers before Goals) and made the TOC disagree
// with the Section Tree.
const SIDEBAR_ORDER_KEY = "pcpm.sidebar_order_v1"
const DEFAULT_SECTION_ORDER = [
  "client", "about", "circleOfSupport", "communication", "wonderfulThings",
  "notSoFavorites", "dayInLife", "important", "bigDreams", "supportMatch",
  "working", "goals", "barriers", "currentServices", "unmetNeeds",
  "resourceCalendar", "review", "facilitator"
]
function pcpSectionOrder() {
  let saved = null
  try { saved = JSON.parse(localStorage.getItem(SIDEBAR_ORDER_KEY) || "null") } catch (_) { /* ignore */ }
  const base = (Array.isArray(saved) && saved.length) ? saved : DEFAULT_SECTION_ORDER
  const known = Object.keys(SECTIONS)
  // Keep only sections the preview knows, in the editor's order, then
  // append any known section the saved order didn't mention (e.g. a
  // newly-shipped one, or "brochures") so nothing silently drops.
  const ordered = base.filter(k => known.includes(k))
  known.forEach(k => { if (!ordered.includes(k)) ordered.push(k) })
  return ordered
}

// Helpers ------------------------------------------------------------

const esc = (s) => String(s == null ? "" : s)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;").replace(/'/g, "&#39;")

// Display name for a resource's service. When the service is "Other" the
// real name lives in r.comment (the editor's "Specify the service" box),
// so show that instead of the literal "Other" — e.g. "Karate Class".
function ppServiceName(r) {
  const svc = ((r && (r.type || r.text)) || "").trim()
  if (svc && svc !== "Other") return svc
  // "Other": show the specified name (comment); if blank, fall back to a real
  // text value (imported / AI data that left type="Other" but named the
  // service in text) before the literal "Other".
  const spec = ((r && r.comment) || "").trim()
  const alt  = ((r && r.text) || "").trim()
  return spec || (alt && alt !== "Other" ? alt : "") || "Other"
}
// Trailing comment to show after the service name. Suppressed for "Other"
// since its comment has already become the name (no "Other — Karate Class").
function ppServiceComment(r) {
  const svc = (r && (r.type || r.text)) || ""
  return svc === "Other" ? "" : ((r && r.comment) || "")
}

// Custom (non-Lucide) icon glyphs the rail / TOC / section H2 may
// reference. Mirrors CUSTOM_ICON_INNER in app/helpers/lucide_helper.rb
// (server) and CUSTOM_ICON_SVG in wizard_controller.js so every surface
// draws the same glyph regardless of who's rendering.
const PP_CUSTOM_ICON_SVG = {
  "custom-ring-5-center": `<g fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="4.5" r="2.8"/><circle cx="5" cy="17.5" r="2.8"/><circle cx="19" cy="17.5" r="2.8"/><path d="M 14 6.3 Q 22.5 9.5 17.5 15.5"/><path d="M 16.6 18.7 Q 12 21 7.4 18.7"/><path d="M 6.5 15.5 Q 1.5 9.5 10 6.3"/></g>`
}

// Explicit width/height on the wrapper mirrors what Lucide injects
// when it hydrates `<i data-lucide>` placeholders. Without dims the
// SVG falls into Chromium's intrinsic-sizing path which can collapse
// the slot in PDF print mode → mid-rail void.
const sectionIconHtml = (name) => {
  const custom = PP_CUSTOM_ICON_SVG[name]
  if (custom) {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">${custom}</svg>`
  }
  return `<i data-lucide="${name}"></i>`
}

const getAge = (dob) => {
  if (!dob) return null
  const [y, m, d] = dob.split("-").map(n => parseInt(n, 10))
  if (!y || !m || !d) return null
  const t = new Date()
  let age = t.getFullYear() - y
  const md = t.getMonth() - (m - 1)
  if (md < 0 || (md === 0 && t.getDate() < d)) age--
  return age
}

const itemText = (it) => (typeof it === "string" ? it : (it && (it.text || it.description || ""))) || ""

// Escape a string for literal use inside a RegExp (redaction name matching).
const escapeRegExp = (s) => String(s == null ? "" : s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

// Service Calendar helpers — mirror pcp_builder.html so the native
// section 7 renders pixel-for-pixel like the legacy Review tab.

// Canonical service abbreviations (pcp_builder.html#2127 SERVICE_ABBREV).
// Falls back to the first 4 letters for anything not listed so chips
// stay narrow inside the 7-day grid cells.
const SERVICE_ABBREV = {
  "IHSS - In-Home Supportive Services": "IH",
  "Personal assistance Services":       "PA",
  "Respite Services - In-Home":         "RS",
  "Respite Services - Out-of-Home":     "RO",
  "Day Program":                        "DP",
  "Transportation":                     "Tr",
  "Behavioral Therapy":                 "BT",
  "Speech Therapy":                     "ST",
  "Occupational Therapy":               "OT",
  "Physical Therapy":                   "PT",
  "Social Recreation":                  "SR",
  "Adaptive Swimming":                  "AS",
  "Tutoring Services":                  "Tutr",
  "Service Coordination":               "SC",
  "In Parental Care":                   "PC",
  "Parental Care":                      "PC",
  "School":                             "SCH",
  "Charter Home School":                "SCH",
  "Music Class":                        "MC",
  "Music Lessons":                      "MC",
  "Art Class":                          "AC",
  "Dance Class":                        "DC",
  "Dance class":                        "DC",
  "Dance Lessons":                      "DC",
  "Art Lessons":                        "AC",
  "Swimming Lessons":                   "SW",
  "Day Camp":                           "DYC",
  "Overnight Camp":                     "ONC",
  "Respite":                            "RS",
  "Personal Assistance":                "PA",
  "Personal Assistance Services":       "PA",
  "Other":                              "Othr"
}
// School name from About → School Details (first non-trivial entry), used
// to abbreviate ANY school — "Pine Elementary", "Charter Home School", … —
// to SCH. Mirrors resource_calendar_controller#schoolNameFor.
const pcpSchoolName = (plan) => {
  const items = (plan && plan.about && plan.about.schoolDetails) || []
  for (const it of items) {
    const raw = (typeof it === "string" ? it : (it && (it.text || it.description)) || "").trim()
    if (raw.length >= 2) return raw.replace(/\s*[,(].*$/, "").trim() || raw
  }
  return "Charter Home School"
}
// Abbreviation lookup: per-plan custom override (edited from the legend) →
// canonical map → any school name → first-4 fallback. MUST match
// resource_calendar_controller#_abbrev / wizard rcAbbrev so the Review/PDF
// calendar shows the same abbreviations as the editor.
const getServiceAbbrev = (name, plan) => {
  if (!name) return ""
  const custom = (plan && plan.resourceCalendar && plan.resourceCalendar.abbrev) || {}
  if (custom[name]) return String(custom[name])
  if (SERVICE_ABBREV[name]) return SERVICE_ABBREV[name]
  if (plan && name === pcpSchoolName(plan)) return "SCH"
  return name.length > 4 ? name.substring(0, 4) : name
}

// 0→"12 AM", 9.5→"9:30 AM", 13→"1 PM", 24→"12 AM".
const fmtHour12 = (h) => {
  const slot   = Math.round(h * 2)
  const wholeH = (Math.floor(slot / 2) % 24 + 24) % 24
  const isHalf = slot % 2 === 1
  const hour12 = wholeH % 12 === 0 ? 12 : wholeH % 12
  return hour12 + (isHalf ? ":30" : "") + " " + (wholeH < 12 ? "AM" : "PM")
}

// Tailwind -200 tints — match the legacy palette[] in pcp_builder.html#7384.
// Light tints keep the cell text legible at the small chip size used in
// the grid; -300 is reserved for "In Parental Care" so it visually reads
// as "no active service" (gray) instead of competing with the colors.
// Vibrant service palette — MUST match resource_calendar_controller's
// PALETTE (and the wizard's RC_PALETTE) so the Review/PDF calendar colors
// are identical to the editor grid. (Was a pastel palette, which made the
// preview legend colors disagree with the editor.)
const RC_PALETTE = [
  "#3b82f6","#22c55e","#a855f7","#f59e0b","#ec4899","#14b8a6",
  "#6366f1","#f97316","#06b6d4","#84cc16","#f43f5e","#0ea5e9"
]
const RC_PARENTAL_COLOR = "#94a3b8"

// Chip ink — pick black-or-white text from the fill's perceived
// luminance so the abbreviation reads on BOTH the vibrant fills AND
// the light ones (slate Parental Care, the #E5E7EB default). Blanket
// white was unreadable on those light grays. Returns an inline
// color + text-shadow that overrides the stylesheet per-chip.
// Mirror of resource_calendar_controller#_chipInk.
const rcChipInk = (bg) => {
  const h = String(bg || "").replace("#", "")
  const x = h.length === 3 ? h.split("").map(c => c + c).join("") : h
  const r = parseInt(x.slice(0, 2), 16) || 0
  const g = parseInt(x.slice(2, 4), 16) || 0
  const b = parseInt(x.slice(4, 6), 16) || 0
  return (r * 299 + g * 587 + b * 114) / 1000 > 150
    ? "color:#1f2937;text-shadow:none"
    : "color:#ffffff;text-shadow:0 1px 1px rgba(0,0,0,0.5)"
}

// "Other" resources show their typed-in comment as the label — mirrors
// resource_calendar_controller#_serviceLabel so the color key matches the
// painted slot's resourceRef.
const rcServiceLabelFor = (r) => {
  const svc = ((r && (r.text || r.type)) || "").trim()
  if (svc === "Other") return (((r && r.comment) || "").trim()) || "Other"
  return svc
}

// Ordered service list EXACTLY as resource_calendar_controller#_services
// builds it: built-in (Parental Care + school) → currentServices → ALL
// goal resources → unmet barrier resources, deduped in that order. This
// ordering drives the index-based color assignment, so it must match the
// editor or the same service lands on a different palette slot.
const rcServiceList = (plan) => {
  const builtin = ["Parental Care", pcpSchoolName(plan)]
  const fromCurrent = (plan.currentServices || [])
    .map(s => (s.linkedType || s.text || "").trim()).filter(Boolean)
  const fromGoals = (plan.goals || [])
    .flatMap(g => (Array.isArray(g.resources) ? g.resources : []))
    .map(rcServiceLabelFor).filter(Boolean)
  const fromBarriersUnmet = (plan.barriers || [])
    .flatMap(b => (Array.isArray(b.resources) ? b.resources : []))
    .filter(r => r && r.tag === "unmet")
    .map(rcServiceLabelFor).filter(Boolean)
  const seen = new Set(), out = []
  ;[...builtin, ...fromCurrent, ...fromGoals, ...fromBarriersUnmet].forEach(t => {
    if (seen.has(t)) return
    seen.add(t); out.push(t)
  })
  return out
}

// Color map mirrors resource_calendar_controller#_colorFor: "Parental Care"
// is always slate; every other service takes the palette slot at its index
// in the service list with Parental Care removed. The school always holds
// index 0 (even when unpainted) — same as the editor — so downstream
// services line up on the same colors.
const buildRcColorMap = (plan) => {
  const nonParental = rcServiceList(plan).filter(t => t !== "Parental Care")
  const map = { "Parental Care": RC_PARENTAL_COLOR }
  nonParental.forEach((t, i) => { if (!map[t]) map[t] = RC_PALETTE[i % RC_PALETTE.length] })
  return map
}

// Port of pcp_builder.html#chipifyText (line 6460). Parses inline
// markers in list items and emits styled chips:
//   [time]        → time chip (clock prefix)
//   <activity>    → activity chip (purple gradient)
//   (0)/(Y) on prev activity → flip to "ok"/"yes" chip with green check
//   (1)/(N) on prev activity → flip to "help"/"no" chip with amber hand / red X
//   {home}/{community} → location chip
//   =>            → arrow
//   |             → separator
//   plain text    → neutral chip
// Returns null when no markers are present (caller falls back to plain text).
function chipifyText(text) {
  if (!text) return null
  // Strip the known PII placeholder tags before checking for chip
  // markers. [FirstName] / [Age] / etc. pass through to the
  // post-render _expandPlaceholders sweep — if those are the only
  // brackets in the text there's nothing real to chip, so we
  // return null and let the caller render the item as plain
  // escaped text. Without this guard "[FirstName] will participate
  // in..." chips into "Agastya" (raw) + "will participate..." in a
  // chip-neutral pill, which is what produced the boxed item text
  // in section 9.
  const probe = text.replace(
    /\[(?:First\s*Name|Last\s*Name|Age|Location|School|Grade|Name)\]/gi, ""
  )
  if (!/[\[<{]|\([01YN]\)|=>/.test(probe)) return null
  const parts = []
  let pos = 0
  const len = text.length
  while (pos < len) {
    const ch = text[pos]
    if (ch === "[") {
      const end = text.indexOf("]", pos)
      if (end === -1) { pos++; continue }
      const val = text.substring(pos + 1, end).trim()
      // Reserved PII placeholder names emit raw bracket text instead
      // of a chip-time pill so the post-render _expandPlaceholders
      // pass can still see the literal "[FirstName]" / "[Age]" and
      // substitute the real value. Without this branch the brackets
      // get stripped at chipify time (pcp_builder.html#7590 dodges
      // the same problem by bypassing chipify in the working
      // section). Anything else inside brackets — e.g. "[10am]" —
      // still becomes a time chip.
      if (/^(?:First\s*Name|Last\s*Name|Age|Location|School|Grade|Name)$/i.test(val)) {
        parts.push({ html: `[${esc(val)}]`, tag: "neutral" })
      } else if (val) {
        parts.push({ html: `<span class="chip-time">&#9201; ${esc(val)}</span>`, tag: "time" })
      }
      pos = end + 1
    } else if (ch === "<") {
      const end = text.indexOf(">", pos)
      if (end === -1) { pos++; continue }
      const val = text.substring(pos + 1, end).trim()
      if (val) parts.push({ html: `<span class="chip-activity">${esc(val)}</span>`, tag: "activity" })
      pos = end + 1
    } else if (ch === "=" && text[pos + 1] === ">") {
      parts.push({ html: `<span class="chip-arrow">&rarr;</span>`, tag: "arrow" })
      pos += 2
    } else if (ch === "{") {
      const end = text.indexOf("}", pos)
      if (end === -1) { pos++; continue }
      const loc = text.substring(pos + 1, end).trim().toLowerCase()
      if (loc === "home") {
        parts.push({
          html: `<span class="chip-loc chip-loc-home"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>Home</span>`,
          tag: "loc"
        })
      } else if (loc === "community") {
        parts.push({
          html: `<span class="chip-loc chip-loc-community"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>Community</span>`,
          tag: "loc"
        })
      }
      pos = end + 1
    } else if (ch === "|") {
      parts.push({ html: `<span class="chip-sep">│</span>`, tag: "sep" })
      pos++
    } else if (ch === "(" && (text[pos+1] === "0" || text[pos+1] === "1" || text[pos+1] === "Y" || text[pos+1] === "N") && text[pos+2] === ")") {
      const marker = text[pos + 1]
      const isPositive = (marker === "0" || marker === "Y")
      const isYesNo    = (marker === "Y" || marker === "N")
      // Find most recent activity OR neutral chip, skipping arrows/separators.
      let targetIdx = -1
      for (let i = parts.length - 1; i >= 0; i--) {
        if (parts[i].tag === "activity" || parts[i].tag === "neutral") { targetIdx = i; break }
        if (parts[i].tag === "arrow" || parts[i].tag === "sep") continue
        break
      }
      if (targetIdx >= 0) {
        const m = parts[targetIdx].html.match(/>([^<]+)</)
        const name = m ? m[1] : ""
        if (isYesNo) {
          parts[targetIdx].html = isPositive
            ? `<span class="chip-yes"><span class="chip-icon">&#10003;</span>${name}</span>`
            : `<span class="chip-no"><span class="chip-icon">&#10007;</span>${name}</span>`
          parts[targetIdx].tag = isPositive ? "yes" : "no"
        } else {
          parts[targetIdx].html = isPositive
            ? `<span class="chip-ok"><span class="chip-icon">&#10003;</span>${name}</span>`
            : `<span class="chip-help"><span class="chip-icon">&#9995;</span>${name}</span>`
          parts[targetIdx].tag = isPositive ? "ok" : "help"
        }
      }
      pos += 3
    } else {
      // Plain text up to next marker.
      let nextMarker = len
      for (let i = pos; i < len; i++) {
        const c = text[i]
        if (c === "[" || c === "<" || c === "|" || c === "{" ||
            (c === "=" && text[i+1] === ">") ||
            (c === "(" && (text[i+1] === "0" || text[i+1] === "1" || text[i+1] === "Y" || text[i+1] === "N") && text[i+2] === ")")) {
          nextMarker = i
          break
        }
      }
      const trimmed = text.substring(pos, nextMarker).replace(/^[,\s]+|[,\s]+$/g, "")
      if (trimmed) parts.push({ html: `<span class="chip-neutral">${esc(trimmed)}</span>`, tag: "neutral" })
      pos = nextMarker
    }
  }
  return parts.map(p => p.html).join("")
}

// Pre-process the colon-separated "activity: status" syntax that
// Day in Life items use into the chipify marker syntax the rest of
// the editor uses. Example:
//
//   "Bedtime snack: independently, Bath/shower: needs help"
//   →  "<Bedtime snack>(0) | <Bath/shower>(1)"
//
// "independently" / "independent" / "indep" → (0) chip-ok ✓
// "needs help"    / "needs"      / "help"   → (1) chip-help ✋
// Status text is case-insensitive and tolerant of trailing
// punctuation. Anything that doesn't match one of the known
// status tokens is left alone so the renderer falls through to
// the existing chipify or plain-text paths.
const INDEP_RX = /^(?:independently|independent|indep\.?|yes)$/i
const HELP_RX  = /^(?:needs?\s*help|needs\s*assistance|help|assistance|no)$/i
function colonStatusToMarkers(text) {
  if (!text || !text.includes(":")) return null
  const parts = text.split(/\s*,\s*/).filter(Boolean)
  if (parts.length === 0) return null
  const out = []
  let matched = 0
  parts.forEach(part => {
    const idx = part.lastIndexOf(":")
    if (idx < 0) { out.push(part); return }
    const label  = part.slice(0, idx).trim()
    const status = part.slice(idx + 1).trim().replace(/[.;]+$/, "")
    if (!label) { out.push(part); return }
    if (INDEP_RX.test(status)) {
      out.push(`<${label}>(0)`); matched++
    } else if (HELP_RX.test(status)) {
      out.push(`<${label}>(1)`); matched++
    } else {
      out.push(part)
    }
  })
  // Only return the transformed string if at least one segment
  // matched a known status — otherwise this isn't really a
  // colon-status list and the original text should pass through.
  return matched > 0 ? out.join(" | ") : null
}

// Render an item's body — apply chipifyText when markers are present,
// otherwise escape and pass through. Day-in-Life items arrive as
// colon-separated "activity: status" lists that don't have native
// chipify markers, so a quick preprocessor converts them before
// chipify takes over.
function renderItemBody(text) {
  const preprocessed = colonStatusToMarkers(text) || text
  const chipped = chipifyText(preprocessed)
  return chipped != null ? chipped : esc(text)
}

// Schedule helpers — shared by working / barriers / goals so the
// resource "Currently Approved" details box renders identically
// across the three sections (pcp_builder.html#1598 fmtSchedTime,
// #1623 fmtSchedUnit, #8129 schedChips markup).
// CANONICAL formatters (schedule_fields.js) — the preview/PDF reads identically
// to the editor rows, unmet needs, and current services, incl. the TBD tags.
const pcpFmtSchedTime = scheduleTimeText
const pcpFmtSchedUnit = scheduleUnitText

// Section number for a given section key — mirrors the filter/skip
// rules in render() so the numbers we put on ref chips align with
// the visible section H2s. Used by both currentServices and
// unmetNeeds to build "Goal 13.3" / "Barrier 12.1" labels.
const pcpSectionNumOf = (plan, key) => {
  const order = pcpSectionOrder()
    .filter(k => k !== "client" && k !== "facilitator")
    .filter(k => isSectionIncluded(plan, k))
  const idx = order.indexOf(key)
  return idx >= 0 ? idx + 1 : 0
}

// Port of pcp_builder.html#3885 resolveLinkedService — walk goals,
// barriers, works, and doesntWork to find every resource matching
// the given service type, then return aggregated details. Each
// detail carries its own sourceRef ("Goal 13.3" etc.) so callers
// can build cross-ref chips.
const pcpResolveLinkedService = (plan, linkedType, unmetOnly) => {
  const details = []
  const goalsNum    = pcpSectionNumOf(plan, "goals")    || 13
  const barriersNum = pcpSectionNumOf(plan, "barriers") || 12
  const worksNum    = pcpSectionNumOf(plan, "working")  || 11
  const pushFrom = (arr, refLabel, kindNum) => {
    ;(arr || []).forEach((row, ri) => {
      ;(row.resources || []).forEach(r => {
        if (unmetOnly && r.tag !== "unmet") return
        if (!unmetOnly && r.tag === "unmet") return
        if (!matchesServiceLink(r, linkedType)) return
        const linked = !!r.isLinked
        details.push({
          comment: linked ? "" : (r.comment || ""),
          tag: r.tag || "general",
          currentServices: linked ? "" : (r.currentServices || ""),
          ratio: linked ? "" : (r.ratio || ""),
          schedule: linked ? [] : (r.schedule || []),
          sourceRef: `${refLabel} ${kindNum}.${ri + 1}`
        })
      })
    })
  }
  pushFrom(plan.goals,                "Goal",         goalsNum)
  pushFrom(plan.barriers,             "Barrier",      barriersNum)
  pushFrom(plan.working?.works,       "Works",        worksNum)
  pushFrom(plan.working?.doesntWork,  "Not Working",  worksNum)
  const hasData = (d) =>
    (d.schedule && d.schedule.length > 0) ||
    (d.ratio && d.ratio !== "1:1") ||
    d.currentServices || d.comment
  details.sort((a, b) => (hasData(a) === hasData(b)) ? 0 : (hasData(a) ? -1 : 1))
  return { details }
}

// Render the flat "Currently Approved" row used by goals and
// barriers. Only emits markup when the resource carries actual
// data AND is shown in review (matches the legacy guard at
// pcp_builder.html#8499 / #8117 — only `rc` and `general` tags,
// r.showInReview must not be false). Returns "" otherwise so the
// caller can drop it inline without extra branching.
//
// Layout (per the screenshot reference):
//   [CURRENTLY APPROVED]  currentServices text   [Weekly → 40 min/wk]
// — small green badge on the left, optional currentServices text
// in the middle, frequency chips and (when present) a ratio pill
// on the right. No box / no table — everything flows on one row
// and wraps when the line runs out of space.
//
// Vendor / provider chip — a gradient icon avatar (storefront, inline SVG
// so it renders in the PDF) + the provider name on a soft two-tone pill.
// Shared by the goal cards' "Currently Requesting/Approved" row and the
// flat details box so the provider reads the same everywhere.
const ppVendorChip = (text) => {
  const t = (text || "").trim()
  if (!t) return ""
  return `<span class="pp-rc-vendor"><span class="pp-rc-vendor-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m2 7 4.4-4.4A2 2 0 0 1 7.8 2h8.4a2 2 0 0 1 1.4.6L22 7"/><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><path d="M9 22v-5h6v5"/><path d="M2 7h20"/></svg></span><span class="pp-rc-vendor-tx">${esc(t)}</span></span>`
}
const pcpRenderRcDetailsBox = (r) => {
  if (!r) return ""
  const tag = r.tag || "general"
  if (tag !== "rc" && tag !== "general") return ""
  if (r.showInReview === false) return ""
  const sched = (r.schedule || []).filter(scheduleHasContent)
  const hasRatio = r.ratio && r.ratio !== "1:1"
  if (!r.currentServices && !hasRatio && sched.length === 0) return ""
  const schedChips = sched.map(s => `
    <span class="pp-work-sched">
      <span class="pp-work-sched-d">${esc(s.dayType || "")}</span>
      <span class="pp-work-sched-arr">&rarr;</span>
      <span class="pp-work-sched-t">${esc(pcpFmtSchedTime(s))}${esc(pcpFmtSchedUnit(s))}</span>
    </span>`).join(" ")
  return `
    <div class="pp-rc-flat">
      <span class="pp-rc-flat-label">Currently Approved</span>
      ${ppVendorChip(r.currentServices)}
      ${hasRatio ? `<span class="pp-rc-flat-ratio">Ratio <span class="pp-work-ratio">${esc(r.ratio)}</span></span>` : ""}
      ${schedChips}
    </div>`
}

const list = (items) => {
  if (!items || !items.length) return `<p class="pp-empty">None listed.</p>`
  // Matches pcp_builder.html#6564 renderPrintList — every item list in
  // the print/PDF view uses .glow-list with a blue-gradient bullet.
  return `<ul class="glow-list">${items.map(it => `<li>${renderItemBody(itemText(it))}</li>`).join("")}</ul>`
}

// Special list renderer for "Who Lives With Me" — pulls the
// relationship out of a trailing "(Mother)" / "(youngest brother)"
// parens, maps it to an avatar icon + color token, and emits a card
// row with the name on one side and a colored relationship chip on
// the other. Falls back to a generic person icon when no
// relationship token is found.
const LWM_RELATIONSHIPS = [
  { match: /\bmother|\bmom\b|\bmama\b|\bmum\b/i,           label: "Mother",      icon: "heart",           color: "rose"    },
  { match: /\bfather|\bdad\b|\bpapa\b|\bdaddy\b/i,         label: "Father",      icon: "shield",          color: "blue"    },
  { match: /\bgrand\s*mother|\bgrandma\b|\bnana\b|\bgran\b/i, label: "Grandmother", icon: "heart-handshake", color: "rose" },
  { match: /\bgrand\s*father|\bgrandpa\b/i,                label: "Grandfather", icon: "heart-handshake", color: "sky"     },
  { match: /\bgrandparent/i,                               label: "Grandparent", icon: "heart-handshake", color: "amber"   },
  { match: /\bstep\s*mother|\bstepmom\b/i,                 label: "Stepmother",  icon: "heart",           color: "rose"    },
  { match: /\bstep\s*father|\bstepdad\b/i,                 label: "Stepfather",  icon: "shield",          color: "blue"    },
  { match: /\bsister\b/i,                                  label: "Sister",      icon: "user-round",      color: "pink"    },
  { match: /\bbrother\b/i,                                 label: "Brother",     icon: "user-round",      color: "indigo"  },
  { match: /\bson\b/i,                                     label: "Son",         icon: "baby",            color: "blue"    },
  { match: /\bdaughter\b/i,                                label: "Daughter",    icon: "baby",            color: "pink"    },
  { match: /\b(spouse|partner|husband|wife)\b/i,           label: "Partner",     icon: "heart-handshake", color: "red"     },
  { match: /\b(aunt|auntie)\b/i,                           label: "Aunt",        icon: "users-round",     color: "teal"    },
  { match: /\buncle\b/i,                                   label: "Uncle",       icon: "users-round",     color: "teal"    },
  { match: /\bcousin\b/i,                                  label: "Cousin",      icon: "users-round",     color: "amber"   },
  { match: /\b(roommate|housemate|flatmate)\b/i,           label: "Roommate",    icon: "home",            color: "violet"  },
  { match: /\b(caregiver|attendant|aide|caretaker|nurse|support staff|staff)\b/i, label: "Caregiver", icon: "stethoscope", color: "emerald" },
  { match: /\b(friend|pal|buddy)\b/i,                      label: "Friend",      icon: "smile",           color: "yellow"  },
  { match: /\b(pet|dog|cat|bird)\b/i,                      label: "Pet",         icon: "paw-print",       color: "amber"   }
]
// School-details list — picks up the school TYPE (Elementary /
// Middle / High / Preschool / College / etc.) and the GRADE
// ("4th", "10th grade", "Kindergarten", "Senior"…) out of the
// raw item text, emitting them as two separate chips beside the
// school name. The avatar icon + color follow the school type.
const SCHOOL_TYPES = [
  { match: /\b(?:pre[-\s]?school|nursery)\b/i,                 label: "Preschool",     icon: "baby",            color: "rose"    },
  { match: /\b(?:pre[-\s]?k|kindergarten)\b/i,                 label: "Kindergarten",  icon: "baby",            color: "amber"   },
  { match: /\b(?:elementary|primary)\s*(?:school)?\b/i,        label: "Elementary",    icon: "school",          color: "emerald" },
  { match: /\b(?:middle|junior\s*high)\s*(?:school)?\b/i,      label: "Middle",        icon: "school",          color: "blue"    },
  { match: /\b(?:high\s*school|senior\s*high)\b/i,             label: "High",          icon: "graduation-cap",  color: "indigo"  },
  { match: /\b(?:college|university)\b/i,                      label: "College",       icon: "graduation-cap",  color: "violet"  },
  { match: /\b(?:home[\s-]?school|homeschool|charter)\b/i,     label: "Charter",       icon: "home",            color: "teal"    },
  { match: /\b(?:vocational|trade\s*school|tech\b)\b/i,        label: "Vocational",    icon: "wrench",          color: "amber"   }
]
const GRADE_RX = /\b(?:pre[-\s]?k|kindergarten|grade\s*k\b|grade\s*\d{1,2}|\d{1,2}(?:st|nd|rd|th)(?:\s*grade)?|freshman|sophomore|junior|senior)\b/i
const schoolList = (items) => {
  if (!items || !items.length) return `<p class="pp-empty">No school details yet.</p>`
  return `<ul class="glow-list pp-parse-list">${items.map(it => {
    const raw = itemText(it).trim()
    const typeMatch  = SCHOOL_TYPES.find(t => t.match.test(raw)) || null
    const gradeMatch = raw.match(GRADE_RX)
    const grade      = gradeMatch ? gradeMatch[0].trim() : ""
    let main = raw
    if (typeMatch)  main = main.replace(typeMatch.match, " ")
    if (grade)      main = main.replace(grade, " ")
    main = main
      .replace(/\([^()]*\)/g, " ")
      .replace(/\[[^\]]*\]/g, " ")
      .replace(/[,;\-–—]+/g, " ")
      .replace(/\s{2,}/g, " ")
      .trim()
    const color = typeMatch?.color || "slate"
    return `
      <li class="pp-parse-li">
        <span class="pp-parse-text">
          ${esc(main || raw)}
          ${typeMatch ? `<span class="pp-parse-chip pp-parse-chip-inline" data-color="${color}">${esc(typeMatch.label)}</span>` : ""}
        </span>
        <span class="pp-parse-chips">
          ${grade ? `<span class="pp-parse-chip pp-parse-chip--grade">${esc(grade)}</span>` : ""}
        </span>
      </li>`
  }).join("")}</ul>`
}

const lwmList = (items) => {
  if (!items || !items.length) return `<p class="pp-empty">No one listed yet.</p>`
  return `<ul class="glow-list pp-parse-list">${items.map(it => {
    const raw = itemText(it)
    // Canonical format: "Name [Age][Relationship]". Falls back to
    // the older "(Relationship)[Age]" + "— 10 years old" forms.
    // Heuristic: the bracket that looks like an age (digit or
    // year/month/old) is age; the other is relationship.
    const brackets = [...raw.matchAll(/\[([^\]]+)\]/g)].map(x => x[1].trim())
    const parens   = [...raw.matchAll(/\(([^()]+)\)/g)].map(x => x[1].trim())
    const looksAge = (s) => /\d/.test(s) || /\byear|\bmonth|\bold\b/i.test(s)
    let rel = "", age = ""
    if (brackets.length >= 2) {
      const [a, b] = brackets
      if (looksAge(a) && !looksAge(b))      { age = a; rel = b }
      else if (!looksAge(a) && looksAge(b)) { rel = a; age = b }
      else                                  { age = a; rel = b }
    } else if (brackets.length === 1) {
      if (looksAge(brackets[0])) age = brackets[0]
      else                       rel = brackets[0]
    }
    if (!rel && parens.length) rel = parens[0]
    let name = raw
      .replace(/\[[^\]]+\]/g, "")
      .replace(/\([^()]+\)/g, "")
      .replace(/\s+/g, " ")
      .trim()
    if (!age) {
      const em = name.match(/^(.*?)\s*—\s*(.+)$/)
      if (em) { name = em[1].trim(); age = em[2].trim() }
    }
    const role      = rel ? (LWM_RELATIONSHIPS.find(r => r.match.test(rel)) || null) : null
    const roleColor = role?.color || "slate"
    let   roleLabel = role ? role.label : (rel || "")
    // For a Pet, show the specific animal when the source names one —
    // e.g. "(Cat-1) Roley" → "Pet: Cat" (rendered PET: CAT).
    if (role && role.label === "Pet") {
      const am = rel.match(/\b(dog|cat|bird)\b/i)
      if (am) roleLabel = `Pet: ${am[1][0].toUpperCase()}${am[1].slice(1).toLowerCase()}`
    }
    return `
      <li class="pp-parse-li">
        <span class="pp-parse-text">
          ${esc(name || raw)}
          ${age ? `<span class="pp-parse-chip pp-parse-chip-inline" data-color="blue">Age: ${esc(age)}</span>` : ""}
        </span>
        <span class="pp-parse-chips">
          ${roleLabel ? `<span class="pp-parse-chip" data-color="${roleColor}">${esc(roleLabel)}</span>` : ""}
        </span>
      </li>`
  }).join("")}</ul>`
}

// Section-header legend shown when any item in a section uses the
// (0)/(1) independence markers.
const renderChipLegend = () => `
  <div class="pp-chip-legend">
    <span class="chip-ok"><span class="chip-icon">&#10003;</span>Independent</span>
    <span class="chip-help"><span class="chip-icon">&#9995;</span>Needs Help</span>
  </div>`

// True if any item in any list carries an independence marker —
// either the explicit (0)/(1) chipify syntax used by Strengths
// etc., or the colon-status "X: independently / X: needs help"
// shape Day in Life items use. The latter is the same format the
// colonStatusToMarkers() preprocessor folds into chipify markers,
// so detecting either trigger keeps the Independent/Needs Help
// legend in sync with the chips the section actually renders.
const COLON_STATUS_RX = /:\s*(?:independently|independent|indep\.?|yes|needs?\s*help|needs\s*assistance|help|assistance|no)\b/i
const hasChipMarkers = (items) =>
  (items || []).some(it => {
    const text = typeof it === "string" ? it : (it && it.text) || ""
    return /\([01]\)/.test(text) || COLON_STATUS_RX.test(text)
  })
const anyHasChipMarkers = (lists) => (lists || []).some(l => hasChipMarkers(l))

const tile = (icon, color, label, items) => `
  <div class="pp-tile">
    <div class="pp-tile-icon pp-tile-icon-${color}"><i data-lucide="${icon}"></i></div>
    <div class="pp-tile-body">
      <div class="pp-tile-label">${esc(label)}</div>
      <div class="pp-tile-content pp-bg-${color}">${list(items)}</div>
    </div>
  </div>`

const subCard = (color, label, items, hint) => `
  <div class="pp-subcard pp-${color}">
    <h3>${esc(label)}</h3>
    ${hint ? `<p class="pp-subcard-hint">${esc(hint)}</p>` : ""}
    ${list(items)}
  </div>`

// Per-sub-section "Move to next page" chip. Mirrors production's
// dil-pb-chip pattern (pcp_builder.html#7609 mkChip etc.). pageBreaks
// is the plan.meta.pageBreaks map; pbKey is the unique key used as the
// chip's data-pb-key and as the entry in pageBreaks. Returns a button
// element wired to the print-preview Stimulus action.
const pbChip = (pageBreaks, pbKey) => {
  const on = !!(pageBreaks && pageBreaks[pbKey])
  return `<button type="button"
    class="dil-pb-chip dil-pb-chip-icon${on ? " active" : ""}"
    data-action="print-preview#togglePageBreak"
    data-pb-key="${esc(pbKey)}"
    title="${on ? "On next page — click to remove" : "Move this sub-section to the next page"}"
    aria-label="${on ? "Remove page break" : "Move to next page"}">
    <i data-lucide="${on ? "check" : "arrow-down-to-line"}"></i>
  </button>`
}

// Frequency-token matcher used by currentServices' linked rows to
// pull "4 hours /Month" / "30 mins /week" / "2 sessions /yr" out
// of the comment+currentServices description and render it as a
// chip on the right rather than leaving it inline as plain text.
const CS_FREQ_RX = /\b(\d+(?:\.\d+)?)\s*(hours?|hrs?|minutes?|mins?|sessions?|times?|days?|visits?|units?)\s*\/\s*(year|yr|month|mo|week|wk|day|d)\b/i

// "Edit" affordance — small pill anchored on every sub-section card
// that links to the matching native-editor section page so the user
// can jump from preview straight into the editor for that section.
// anchor is optional; when set it appends "#anchor" so the editor
// can scroll to a specific sub-unit (goal id, etc.).
const editBtn = (sectionKey, anchor) => {
  const hash = anchor ? "#" + anchor : ""
  return `<a class="pp-edit-btn"
              href="/editor/native/${encodeURIComponent(sectionKey)}${hash}"
              title="Edit in the native editor"
              aria-label="Edit in the native editor">
    <i data-lucide="pencil"></i>
  </a>`
}

// Annual travel = sum(sessions per year) × one-way mileage × 2.
// Mirrors goal_resources_controller._computeAnnualTravel so the PDF
// and the editor agree on the math. Returns null when we can't
// compute (no mileage, no schedule, in-home / telehealth). Top-level
// free function so it's safely callable from the RENDERERS object
// literal (those entries are invoked without `this` binding).
const PP_ANNUAL_PERIODS = {
  "Daily": 365,
  "Mon-Fri": 260, "Sat-Sun": 104, "Non-School": 0,
  "Weekly": 52, "Bi-Weekly": 26, "Monthly": 12, "Quarterly": 4,
  "Semi-Annually": 2, "Annually": 1, "As Needed": 0,
  "Mon-Wed-Fri": 156, "Tue-Thu": 104, "Mon-Wed": 104, "Tue-Thu-Sat": 156,
  "Mon": 52, "Tue": 52, "Wed": 52, "Thu": 52, "Fri": 52, "Sat": 52, "Sun": 52
}
function computeAnnualTravel(r) {
  if (!r) return null
  if (r.servicesLocation === "In Home" || r.servicesLocation === "Telehealth") return null
  const m = parseFloat(String(r.mileage || "").replace(/[^0-9.]/g, ""))
  if (!isFinite(m) || m <= 0) return null
  const sched = Array.isArray(r.schedule) ? r.schedule : []
  let annualTrips = 0
  sched.forEach(s => {
    const times = parseInt(String(s.times || "1").replace(/[^0-9]/g, ""), 10) || 1
    annualTrips += times * (PP_ANNUAL_PERIODS[s.dayType] || 0)
  })
  if (annualTrips <= 0) return null
  return { annualMiles: annualTrips * m * 2, annualTrips, mileagePerTrip: m }
}

// Mirrors goal_resources_controller._parseCost / _computeAnnualCost.
// Parses "$95/hr", "$120/session", "$3200/mo" etc. into an annual +
// per-period dollar figure based on the resource's schedule rows.
function ppParseCost(s) {
  const txt = String(s || "").trim()
  if (!txt) return null
  const m = txt.match(/-?\$?\s*([\d,]+(?:\.\d+)?)\s*\/?\s*([a-z]+)?/i)
  if (!m) return null
  const amount = parseFloat(m[1].replace(/,/g, ""))
  if (!isFinite(amount)) return null
  const u = (m[2] || "").toLowerCase()
  let unit = "session"
  if (/^hr|hour/.test(u))             unit = "hour"
  else if (/^min/.test(u))            unit = "minute"
  else if (/^session|sess|visit/.test(u)) unit = "session"
  else if (/^wk|week/.test(u))        unit = "week"
  else if (/^mo|month/.test(u))       unit = "month"
  else if (/^yr|year|ann/.test(u))    unit = "year"
  return { amount, unit }
}
function computeAnnualCost(r) {
  if (!r) return null
  const cost = ppParseCost(r.cost)
  if (!cost) return null
  const sched = Array.isArray(r.schedule) ? r.schedule : []
  if (!sched.length) {
    if (cost.unit === "month") return { annualCost: cost.amount * 12, periodCost: cost.amount, periodLabel: "month", rate: r.cost }
    if (cost.unit === "week")  return { annualCost: cost.amount * 52, periodCost: cost.amount, periodLabel: "week",  rate: r.cost }
    if (cost.unit === "year")  return { annualCost: cost.amount,      periodCost: cost.amount, periodLabel: "year",  rate: r.cost }
    return null
  }
  let annualCost = 0
  const dominantDayType = sched[0]?.dayType || "Weekly"
  sched.forEach(s => {
    const times   = parseInt(String(s.times || "1").replace(/[^0-9]/g, ""), 10) || 1
    const periods = PP_ANNUAL_PERIODS[s.dayType] || 0
    const hours   = parseFloat(s.hours || "0") || 0
    const mins    = parseFloat(s.mins  || "0") || 0
    const durationHrs = hours + (mins / 60)
    const sessionsPerYear = times * periods
    let perSession = 0
    switch (cost.unit) {
      case "hour":    perSession = cost.amount * durationHrs; break
      case "minute":  perSession = cost.amount * (hours * 60 + mins); break
      case "session": perSession = cost.amount; break
      case "week":    perSession = cost.amount / Math.max(times, 1); break
      case "month":   annualCost += cost.amount * 12; return
      case "year":    annualCost += cost.amount; return
      default:        perSession = cost.amount
    }
    annualCost += perSession * sessionsPerYear
  })
  const periodMap = { Weekly: 52, "Bi-Weekly": 26, Monthly: 12, Quarterly: 4, "Semi-Annually": 2, Annually: 1, Daily: 365, "Mon-Fri": 260, "Sat-Sun": 104 }
  const periodsPerYear = periodMap[dominantDayType] || 52
  const periodLabel = dominantDayType === "Monthly" ? "month"
                    : dominantDayType === "Annually" ? "year"
                    : dominantDayType === "Daily" ? "day"
                    : "week"
  return { annualCost, periodCost: annualCost / periodsPerYear, periodLabel, rate: r.cost }
}

// Section renderers --------------------------------------------------
//
// Each renderer returns a string of inner-card HTML. The wrapping
// .print-card with header is added by _renderSectionPage().

const RENDERERS = {
  about(plan, sectionNum) {
    const a = plan.about || {}
    const pb = plan.meta?.pageBreaks || {}
    let sn = 0
    const subN = () => `${sectionNum}.${++sn}`
    const tileN = (icon, color, label, items, pbKey, listFn = list) => {
      const num = subN()
      return `
        <div class="pp-tile has-pb-chip">
          ${pbChip(pb, pbKey)}
          ${editBtn("about")}
          <div class="pp-tile-icon pp-tile-icon-${color}"><i data-lucide="${icon}"></i></div>
          <div class="pp-tile-body">
            <div class="pp-tile-label">${num} ${esc(label)}</div>
            <div class="pp-tile-content pp-bg-${color}">${listFn(items)}</div>
          </div>
        </div>`
    }
    // Scalar "basic details" — rendered ONLY when filled in (hidden from the
    // preview + PDF otherwise). subN() is only spent on shown tiles.
    const infoTile = (icon, color, label, value) => {
      const v = (value == null ? "" : String(value)).trim()
      if (!v) return ""
      const num = subN()
      return `
        <div class="pp-tile">
          ${editBtn("about")}
          <div class="pp-tile-icon pp-tile-icon-${color}"><i data-lucide="${icon}"></i></div>
          <div class="pp-tile-body">
            <div class="pp-tile-label">${num} ${esc(label)}</div>
            <div class="pp-tile-content pp-bg-${color}">${esc(v)}</div>
          </div>
        </div>`
    }
    return `
      ${anyHasChipMarkers([a.communicationStyle, a.schoolDetails, a.workDetails]) ? renderChipLegend() : ""}
      <div class="pp-tiles">
        ${infoTile("languages", "sky",     "Primary Language(s) at Home",               a.primaryLanguages)}
        ${infoTile("sparkles",  "purple",  "Cultural Preferences and Details", a.culturalIdentity)}
        ${infoTile("home",      "emerald", "Living Situation",                          a.livingSituation)}
        ${tileN("message-circle", "sky",    "[FirstName]'s Communication Style",  a.communicationStyle, "about_comm")}
        ${tileN("school",         "emerald","[FirstName]'s School Details",       a.schoolDetails,      "about_school", schoolList)}
        ${tileN("briefcase",      "amber",  "[FirstName]'s Work Details / Business Details",  a.workDetails,      "about_work")}
      </div>`
  },

  important(plan, sectionNum) {
    // No sub-numbering: the two cards (Important TO Me, Important FOR
    // Me) are complete units, not hierarchical sub-sections, so labeling
    // them <sec>.1 / <sec>.2 reads as misleading hierarchy.
    const imp = plan.important || {}
    const pb = plan.meta?.pageBreaks || {}
    const subCardN = (color, label, items, hint, pbKey) => `
      <div class="pp-subcard pp-${color} has-pb-chip">
        ${pbChip(pb, pbKey)}
        ${editBtn("important")}
        <h3>${esc(label)}</h3>
        ${hint ? `<p class="pp-subcard-hint">${esc(hint)}</p>` : ""}
        ${list(items)}
      </div>`
    return `
      <div class="pp-twocol">
        ${subCardN("green", "Important TO [FirstName]",  imp.to,  "What makes me happy, satisfied, and content.",                "imp_to")}
        ${subCardN("blue",  "Important FOR [FirstName]", imp.for, "Health, safety, and being a valued member of the community.", "imp_for")}
      </div>`
  },

  wonderfulThings(plan, sectionNum) {
    // Matches pcp_builder.html#7663 — pink/purple/indigo/orange/teal/amber
    // sub-cards in a 2-col grid, sub-numbered <sec>.1 / .2 / ...
    const w = plan.wonderfulThings || {}
    const pb = plan.meta?.pageBreaks || {}
    let sn = 0
    const subN = () => `${sectionNum}.${++sn}`
    const subCardNum = (color, label, items, pbKey) => {
      if (!items || !items.length) return ""
      return `
        <div class="pp-subcard pp-${color} has-pb-chip">
          ${pbChip(pb, pbKey)}
          ${editBtn("wonderfulThings")}
          <h3>${subN()} ${esc(label)}</h3>
          ${list(items)}
        </div>`
    }
    return `
      ${anyHasChipMarkers([w.activities, w.places, w.events, w.foods, w.possessions, w.admiredQualities]) ? renderChipLegend() : ""}
      <div class="pp-grid2">
        ${subCardNum("pink",   "Favorite Activities",         w.activities,       "wt_activities")}
        ${subCardNum("purple", "Favorite Places",             w.places,           "wt_places")}
        ${subCardNum("indigo", "Special Events",              w.events,           "wt_events")}
        ${subCardNum("orange", "Favorite Foods",              w.foods,            "wt_foods")}
        ${subCardNum("teal",   "Strengths",                   w.possessions,      "wt_possessions")}
        ${subCardNum("amber",  "Qualities Admired by Others", w.admiredQualities, "wt_admired")}
      </div>`
  },

  notSoFavorites(plan, sectionNum) {
    const n = plan.notSoFavorites || {}
    const pb = plan.meta?.pageBreaks || {}
    let sn = 0
    const subN = () => `${sectionNum}.${++sn}`
    const subCardNum = (color, label, items, pbKey) => {
      if (!items || !items.length) return ""
      return `
        <div class="pp-subcard pp-${color} has-pb-chip">
          ${pbChip(pb, pbKey)}
          ${editBtn("notSoFavorites")}
          <h3>${subN()} ${esc(label)}</h3>
          ${list(items)}
        </div>`
    }
    // Switched from .pp-grid3 (3 cols) to .pp-grid2 (2 cols) to match
    // section 4 — same width-filling layout the user asked for the
    // Strengths cards. With only 5 subcards the 3-col layout left
    // odd gaps at the row boundary; 2 cols pack into 2×3 with the
    // last cell empty (3 full rows minus one slot).
    return `
      ${anyHasChipMarkers([n.triggers, n.sensory, n.situations, n.foods, n.environments]) ? renderChipLegend() : ""}
      <div class="pp-grid2">
        ${subCardNum("red",    "Things That Upset [FirstName]", n.triggers,   "nsf_triggers")}
        ${subCardNum("amber",  "Sensory Sensitivities",      n.sensory,      "nsf_sensory")}
        ${subCardNum("slate",  "Situations I'd Rather Skip", n.situations,   "nsf_situations")}
        ${subCardNum("yellow", "Foods I Dislike",            n.foods,        "nsf_foods")}
        ${subCardNum("stone",  "Places That Don't Work",     n.environments, "nsf_environments")}
      </div>`
  },

  bigDreams(plan, sectionNum) {
    // Port of pcp_builder.html#7788 — single-column stack of two
    // sub-cards (Hopes purple-50, Big Dreams indigo-50). Sub-numbers
    // are hardcoded (.1 / .2) to match the legacy: even if Hopes is
    // empty the Big Dreams card stays "9.2", not "9.1". Empty cards
    // are skipped entirely so the section shrinks to whatever the
    // facilitator has actually filled in. When BOTH are empty the
    // section falls through to a quiet "None listed" message
    // matching every other section's empty state.
    const d  = plan.bigDreams || {}
    const pb = plan.meta?.pageBreaks || {}
    const hasItems = (a) => Array.isArray(a) && a.length > 0
    const hasHopes  = hasItems(d.hopes)
    const hasDreams = hasItems(d.dreams)
    if (!hasHopes && !hasDreams) {
      return `<p class="pp-empty">No hopes or dreams listed yet.</p>`
    }
    const card = (color, num, label, items, pbKey) => `
      <div class="pp-subcard pp-${color} has-pb-chip">
        ${pbChip(pb, pbKey)}
        <h3>${sectionNum}.${num} ${esc(label)}</h3>
        ${list(items)}
      </div>`
    return `
      <div class="pp-twocol">
        ${hasHopes  ? card("purple", 1, "Hopes for the Next 12 Months", d.hopes,  "bd_hopes")  : ""}
        ${hasDreams ? card("indigo", 2, "Big Dreams for the Future",    d.dreams, "bd_dreams") : ""}
      </div>`
  },

  supportMatch(plan, sectionNum) {
    // Production colors (pcp_builder.html#7637-7656):
    //   .1 Qualities      → bg-fuchsia-50  text-fuchsia-800
    //   .2 Skills Needed  → bg-cyan-50     text-cyan-800
    //   .3 Personality    → bg-teal-50     text-teal-800
    //   .4 Deal Breakers  → bg-red-50      text-red-800
    const s = plan.supportMatch || {}
    const pb = plan.meta?.pageBreaks || {}
    let sn = 0
    const subN = () => `${sectionNum}.${++sn}`
    const card = (color, label, items, pbKey) => `
      <div class="pp-subcard pp-${color} has-pb-chip">
        ${pbChip(pb, pbKey)}
        ${editBtn("supportMatch")}
        <h3>${subN()} ${esc(label)}</h3>
        ${list(items)}
      </div>`
    return `
      <div class="pp-grid2">
        ${card("fuchsia", "Qualities & Characteristics",       s.qualities,    "sm_qualities")}
        ${card("cyan",    "Skills Needed",                     s.skills,       "sm_skills")}
        ${card("teal",    "Personality Match",                 s.personality,  "sm_personality")}
        ${card("red",     "Deal Breakers (What Doesn't Work)", s.dealBreakers, "sm_dealBreakers")}
      </div>`
  },

  working(plan, sectionNum) {
    // Port of pcp_builder.html#7542 renderWorkItemsPrint + #7595 working
    // section: each item is rendered with its resources stack — a tag
    // pill (Generic / RC / Unmet) + service type + optional comment,
    // and a "Currently Approved" / "Requested" details box with the
    // current services / ratio / frequency chips. Without this every
    // item was just plain text, missing all the resource context the
    // legacy review tab shows.
    const w  = plan.working || {}
    const pb = plan.meta?.pageBreaks || {}
    let sn = 0
    const has  = (arr) => Array.isArray(arr) && arr.length > 0
    const subN = () => `${sectionNum}.${++sn}`

    // Local aliases — the shared helpers live at module level so
    // goals/barriers/working all emit identical frequency chips.
    const fmtSchedTime = pcpFmtSchedTime
    const fmtSchedUnit = pcpFmtSchedUnit

    // Working uses the short tag labels ("RC", "Unmet"); goals /
    // barriers use the long forms ("RC Funded", "Unmet Need") —
    // matches pcp_builder.html#7546 tagLabelsMap vs #8497 tagLabels.
    const TAG_LABELS = { general: "Generic", rc: "RC", unmet: "Unmet" }

    // Port of pcp_builder.html#3885 resolveLinkedService — for a
    // r.isLinked resource the schedule/ratio/comment/currentServices
    // live on the upstream Goal / Barrier / Work entry, not on the
    // local resource. Walk every section that can host a resource
    // matching this type, collect their details, and aggregate.
    // Without this linked resources rendered as a bare tag pill
    // because hasDetails was false everywhere.
    const resolveLinkedService = (linkedType, unmetOnly) => {
      const details = []
      const pushFrom = (arr, sourceName, key) => {
        ;(arr || []).forEach((row, ri) => {
          ;(row.resources || []).forEach(r => {
            if (unmetOnly && r.tag !== "unmet") return
            if (!unmetOnly && r.tag === "unmet") return
            if (!matchesServiceLink(r, linkedType)) return
            // Linked-on-linked: skip schedule/ratio so we don't
            // double-count when the original entry is found.
            const linked = !!r.isLinked
            details.push({
              comment: linked ? "" : (r.comment || ""),
              tag: r.tag || "general",
              currentServices: linked ? "" : (r.currentServices || ""),
              ratio: linked ? "" : (r.ratio || ""),
              schedule: linked ? [] : (r.schedule || [])
            })
          })
        })
      }
      pushFrom(plan.goals,                "Goal",        "goals")
      pushFrom(plan.barriers,             "Barrier",     "barriers")
      pushFrom(plan.working?.works,       "Works",       "works")
      pushFrom(plan.working?.doesntWork,  "Not Working", "notWorking")
      // Sort: entries carrying real data first, pure references after.
      const hasData = (d) =>
        (d.schedule && d.schedule.length > 0) ||
        (d.ratio && d.ratio !== "1:1") ||
        d.currentServices || d.comment
      details.sort((a, b) => (hasData(a) === hasData(b)) ? 0 : (hasData(a) ? -1 : 1))
      return { details }
    }

    // Renderer for one resource (pcp_builder.html#7549-7588). Linked
    // resources resolve their data through resolveLinkedService so
    // the same Currently Approved / Frequency card appears
    // regardless of whether the schedule lives on the local row or
    // on an upstream Goal / Barrier / Work entry.
    const renderResource = (r) => {
      // Resolve linked resources up front so the rest of the
      // function can treat them like any other.
      if (r.isLinked) {
        const resolved = resolveLinkedService(r.type, r.tag === "unmet")
        const allSched = []
        let ratio = "", comment = "", currentServices = ""
        ;(resolved.details || []).forEach(d => {
          if (d.comment) comment = comment ? `${comment}; ${d.comment}` : d.comment
          if (d.ratio && !ratio) ratio = d.ratio
          if (d.currentServices && !currentServices) currentServices = d.currentServices
          ;(d.schedule || []).forEach(s => { if (scheduleHasContent(s)) allSched.push(s) })
        })
        r = { ...r, schedule: allSched, ratio, comment, currentServices }
      }
      const sched = (r.schedule || []).filter(scheduleHasContent)
      const schedChips = sched.map(s => `
        <span class="pp-work-sched">
          <span class="pp-work-sched-d">${esc(s.dayType || "")}</span>
          <span class="pp-work-sched-arr">&rarr;</span>
          <span class="pp-work-sched-t">${esc(fmtSchedTime(s))}${esc(fmtSchedUnit(s))}</span>
        </span>`).join(" ")
      const tag       = r.tag || "general"
      const tagLabel  = TAG_LABELS[tag] || "Generic"
      const isUnmet   = tag === "unmet"
      const hasRatio  = r.ratio && r.ratio !== "1:1"
      // Mileage + annual travel optionally surfaced on the PDF, per
      // the per-resource includeMileageInPdf toggle on goal-resources.
      const showMileage = !!r.includeMileageInPdf &&
                          r.servicesLocation !== "In Home" &&
                          r.servicesLocation !== "Telehealth" &&
                          (r.mileage || (r.schedule || []).length)
      const annualTravel = showMileage ? computeAnnualTravel(r) : null
      // Mileage reimbursement = annualMiles × plan.meta.mileageRate.
      // Only renders when the user has set a rate on the plan AND
      // we have an annual travel figure.
      const mileageRate = parseFloat(plan?.meta?.mileageRate)
      const mileageCost = (annualTravel && isFinite(mileageRate) && mileageRate > 0)
        ? annualTravel.annualMiles * mileageRate
        : null
      // Render every filled field of each vendor (name, contact, address,
      // location, cost, mileage). Reuses the pp-work-details-current row
      // style so no new CSS is needed. Falls back to the composed
      // currentServices string for linked / legacy resources with no vendors.
      // Uses resolvePrintVendor to resolve library-linked vendors live.
      const ppVendors = Array.isArray(r.vendors) ? r.vendors : []
      const vendorsHtml = ppVendors.map(v => {
        const resolved = resolvePrintVendor(v)
        const contact = [resolved.phone, resolved.email, resolved.website].filter(Boolean).join(" · ")
        const meta = [v.servicesLocation, v.cost, v.mileage ? `${v.mileage} mi` : ""].filter(Boolean).join(" · ")
        const parts = [resolved.name, contact, resolved.address, meta].filter(Boolean)
        return parts.length ? `<div class="pp-work-details-current">${parts.map(esc).join(" &mdash; ")}</div>` : ""
      }).filter(Boolean).join("")
      const hasDetail = !!(vendorsHtml || r.currentServices || hasRatio || schedChips || showMileage)
      const detailsTitle = isUnmet ? "Requested:" : "Currently Approved:"
      const detailsHtml = hasDetail ? `
        <div class="pp-work-details${isUnmet ? " pp-work-details-unmet" : ""}">
          <div class="pp-work-details-title">${detailsTitle}</div>
          ${vendorsHtml || (r.currentServices ? `<div class="pp-work-details-current">${esc(r.currentServices)}</div>` : "")}
          ${(hasRatio || schedChips || showMileage) ? `
            <table class="pp-work-details-tbl"><tbody>
              ${hasRatio ? `
                <tr>
                  <td class="pp-work-details-label">Ratio</td>
                  <td><span class="pp-work-ratio">${esc(r.ratio)}</span></td>
                </tr>` : ""}
              ${schedChips ? `
                <tr>
                  <td class="pp-work-details-label">Frequency</td>
                  <td>${schedChips}</td>
                </tr>` : ""}
              ${showMileage && r.mileage ? `
                <tr>
                  <td class="pp-work-details-label">Mileage</td>
                  <td>${esc(String(r.mileage).replace(/\s*\((est|osrm|ors)\)$/, ""))} <span class="pp-work-details-faint">one way</span></td>
                </tr>` : ""}
              ${annualTravel ? `
                <tr>
                  <td class="pp-work-details-label">Annual Travel</td>
                  <td><strong>${annualTravel.annualMiles.toLocaleString(undefined, { maximumFractionDigits: 0 })} mi/year</strong> <span class="pp-work-details-faint">(${annualTravel.annualTrips.toLocaleString()} sessions × ${annualTravel.mileagePerTrip} mi × 2)</span></td>
                </tr>` : ""}
              ${mileageCost !== null ? `
                <tr>
                  <td class="pp-work-details-label">Mileage Claim</td>
                  <td><strong>$${Math.round(mileageCost).toLocaleString()}/year</strong> <span class="pp-work-details-faint">@ $${mileageRate.toFixed(2)}/mi</span></td>
                </tr>` : ""}
            </tbody></table>` : ""}
        </div>` : ""
      // "Other" services print the specified name (ppServiceName) with no
      // trailing "— Other" (ppServiceComment suppresses it).
      const displayType = ppServiceName(r)
      const trailingComment = ppServiceComment(r)
      return `
        <div class="pp-work-res">
          <span class="pp-work-tag pp-work-tag-${tag}">${esc(tagLabel)}</span>
          <span class="pp-work-type">${esc(displayType)}</span>
          ${trailingComment ? `<span class="pp-work-comment">&mdash; ${esc(trailingComment)}</span>` : ""}
        </div>${detailsHtml}`
    }

    // Item list — replaces the generic list() helper so each <li>
    // can carry its resource stack. Item text bypasses chipify on
    // purpose (pcp_builder.html#7590) so [FirstName] / [Age] expand
    // cleanly into the bullet body.
    const workList = (items) => {
      if (!items || !items.length) return `<p class="pp-empty">None listed.</p>`
      return `<ul class="glow-list">${items.map(i => {
        const resources = i.resources || []
        const resHtml = resources.map(renderResource).join("")
        return `<li class="pp-work-li">${esc(i.text || "")}${resHtml}</li>`
      }).join("")}</ul>`
    }

    const card = (color, label, items, pbKey) => has(items) ? `
      <div class="pp-subcard pp-work-${color} has-pb-chip">
        ${pbChip(pb, pbKey)}
        <h3>${subN()} ${esc(label)}</h3>
        ${workList(items)}
      </div>` : ""
    return `
      <div class="pp-twocol">
        ${card("green", "What's Working",     w.works,      "work_works")}
        ${card("red",   "What's not working", w.doesntWork, "work_notWorking")}
      </div>`
  },

  dayInLife(plan, sectionNum) {
    // Matches pcp_builder.html#8324 — h3 sub-section headers
    // "<n>.1 Weekdays" / "<n>.2 Weekends" with .dil-h rows: a tag pill
    // (icon-circle + label) and a glow-list body. Weekend variant uses
    // .dil-h.purple.
    const d = plan.dayInLife || {}
    const sameMap = plan.meta?.weekendSameAsWeekday || {}
    const notSchool = isNotAttendingSchool(plan)
    const pb = plan.meta?.pageBreaks || {}
    const WEEKEND_MIRRORS = {
      weekendMorning:  "morning",
      weekendDay:      "afternoon",
      weekendNight:    "night",
      weekendAfterBed: "afterBed"
    }
    const weekendItems = (key) => sameMap[key] ? (d[WEEKEND_MIRRORS[key]] || []) : (d[key] || [])

    const row = (purpleVariant, icon, label, items, pbKey) => {
      if (!items || !items.length) return ""
      return `
        <div class="dil-h has-pb-chip${purpleVariant ? " purple" : ""}">
          <span class="dil-h-tag">
            <span class="icon-circle"><i data-lucide="${icon}"></i></span>
            ${esc(label)}
          </span>
          ${editBtn("dayInLife")}
          ${pbChip(pb, pbKey)}
          <span class="dil-h-body">${list(items)}</span>
        </div>`
    }

    const allItems = [d.morning, d.day, d.afternoon, d.night, d.afterBed,
                      d.weekendMorning, d.weekendDay, d.weekendNight, d.weekendAfterBed]

    // Icon keys mirror pcp_builder.html#8326-8336 dilIcons map:
    // morning → sunrise/lucide:sunrise, school → school, community/users,
    // brush → Before Bed, bed → After Bed, home → weekend day.
    // Day-in-the-Life items always render with status chips (the
    // ✓ Independent / 🤚 Needs Help pills inside each time block).
    // Render the legend INLINE with each sub-section h3 (6.1 Weekdays,
    // 6.2 Weekends) on the same row, so families never lose the
    // colour key — including when the section spans multiple pages.
    const subHead = (label, weekendVariant) => `
      <div class="pp-dil-subhead-row${weekendVariant ? " pp-dil-subhead-row-weekend" : ""}">
        <h3 class="pp-dil-sub-header${weekendVariant ? " pp-dil-sub-header-weekend" : ""}">${label}</h3>
        ${editBtn("dayInLife")}
        ${renderChipLegend()}
      </div>`
    // Emit the sub-section headers + time-block rows FLAT (no
    // .pp-dil-subsection wrapper) so each .dil-h block is a DIRECT child of
    // the print-card — which is exactly what the paginator treats as a
    // splittable unit. That makes every block's "→ next page" chip move THAT
    // block (and the ones after it) to the next sheet, each block kept whole.
    // A wrapper here made the whole Weekdays/Weekends GROUP the unit, so only
    // the first chip in a group ever fired (and it dragged the whole group).
    return `
      ${subHead(`${sectionNum}.1 Weekdays`, false)}
      ${row(false, "sunrise", "Morning Time",  d.morning,   "dil_morning")}
      ${notSchool ? "" : row(false, "school", "School Time", d.day, "dil_day")}
      ${row(false, notSchool ? "home" : "users",   notSchool ? "Home & Community" : "After School",  d.afternoon, "dil_afternoon")}
      ${row(false, "brush",   "Before Bed",    d.night,    "dil_night")}
      ${row(false, "bed",     "After Bed",     d.afterBed, "dil_afterBed")}
      ${subHead(`${sectionNum}.2 Weekends`, true)}
      ${row(true, "sunrise", "Morning Time" + (sameMap.weekendMorning ? " (same as weekday)" : ""),  weekendItems("weekendMorning"),  "dil_wkMorning")}
      ${row(true, "home",    "Home & Community" + (sameMap.weekendDay ? " (same as weekday)" : ""),  weekendItems("weekendDay"),      "dil_wkDay")}
      ${row(true, "brush",   "Before Bed" + (sameMap.weekendNight ? " (same as weekday)" : ""),      weekendItems("weekendNight"),    "dil_wkNight")}
      ${row(true, "bed",     "After Bed" + (sameMap.weekendAfterBed ? " (same as weekday)" : ""),    weekendItems("weekendAfterBed"), "dil_wkAfterBed")}`
  },

  circleOfSupport(plan) {
    // Grouped tiles: Who Lives With Me (household) first, then one tile per
    // COS_GROUPS category — Family (not in home) / Friends / Professionals /
    // Community. Derived from the same plan.about.circleOfSupport array (see
    // cos_groups.js, mirror of CircleOfSupportGroups) — no data migration.
    // Service Dog / TBD are not printed.
    const cosRaw = (plan.about && plan.about.circleOfSupport) || []
    const lwm = (plan.about && plan.about.whoLivesWithMe) || []
    const dismissed = new Set((plan.about && plan.about._lwmDismissed) || [])
    const pb = plan.meta?.pageBreaks || {}

    // Household — its own tile; its names also guard the Family (not in home)
    // tile so a legacy un-linked household member never prints in both.
    const householdNames = new Set()
    const householdPeople = []
    lwm.forEach(item => {
      const name = (typeof item === "string" ? item : (item && (item.text || item.name)) || "").trim()
      if (!name) return
      const id = item && item.id
      if (id != null && dismissed.has(id)) return
      householdNames.add(normCosName(name))
      householdPeople.push(name)
    })

    const tile = (pbKey, color, icon, title, names) => `
      <div class="pp-tile has-pb-chip">
        ${pbChip(pb, pbKey)}
        ${editBtn("circleOfSupport")}
        <div class="pp-tile-icon pp-tile-icon-${color}"><i data-lucide="${icon}"></i></div>
        <div class="pp-tile-body">
          <div class="pp-tile-label">${esc(title)}</div>
          <div class="pp-tile-content pp-bg-${color}">${lwmList(names)}</div>
        </div>
      </div>`

    const tiles = []
    if (householdPeople.length) {
      tiles.push(tile("cos_lives", "purple", "home", "Who Lives With [FirstName]", householdPeople))
    }
    COS_GROUPS.forEach(g => {
      const people = cosRaw.filter(p => {
        if (!p) return false
        if (g.excludeLinked && p.linkedFromWhoLives != null) return false
        if (g.filter === "Family" && householdNames.has(normCosName(p.name))) return false
        return p.relationship === g.filter
      }).map(p => p.name)
      if (people.length) tiles.push(tile(g.pbKey, g.pdfColor, g.pdfIcon, g.pdfTitle, people))
    })

    if (!tiles.length) return `<p class="pp-empty">No people listed yet.</p>`
    return `<div class="pp-tiles">${tiles.join("")}</div>`
  },

  communication(plan) {
    // Mirrors pcp_builder.html#6721 — bordered 4-column "When → I do
    // → It means → I want you to…" chart. Rendered as CSS-grid rows
    // (.pp-comm-row) rather than an HTML <table> so each row can carry
    // its own .dil-pb-chip "→ next page" arrow and pickSplittables can
    // treat rows as splittable units. A header row sits on top with
    // the same grid layout for visual continuity.
    //
    // Column widths come from plan.meta.commChartColPcts (array of 4
    // percentages summing to 100). Defaults match the legacy editor's
    // starting layout (18 / 30 / 18 / 34). Auto-fit button at the top
    // runs the iterative-probe optimizer in print-preview#autoFitCommChart.
    const rows = (plan.about && plan.about.communicationChart) || []
    if (!rows.length) return `<p class="pp-empty">No communication entries yet.</p>`
    const pb = plan.meta?.pageBreaks || {}
    const pcts = (Array.isArray(plan.meta?.commChartColPcts) && plan.meta.commChartColPcts.length === 4)
      ? plan.meta.commChartColPcts
      : [18, 30, 18, 34]
    const gridStyle = `style="grid-template-columns: ${pcts.map(p => p + '%').join(' ')}"`
    const toolbar = `
      <div class="pp-comm-toolbar">
        <button type="button"
                class="pp-comm-autofit"
                data-action="print-preview#autoFitCommChart"
                title="Auto-fit column widths so the chart takes the least vertical space">
          <i data-lucide="wand-2"></i> Auto-fit columns
        </button>
      </div>`
    // Resize handles sit on the right edge of cells 0/1/2 (not the
    // last cell). mousedown on a handle starts an inline drag that
    // shifts width between cell N and cell N+1; the drop saves the
    // final pcts to plan.meta.commChartColPcts.
    const rh = (i) => `<span class="pp-comm-resize"
                              data-col-idx="${i}"
                              data-action="mousedown->print-preview#startCommChartColResize touchstart->print-preview#startCommChartColResize"
                              title="Drag to resize column">
                         <span class="pp-comm-resize-bar"></span>
                       </span>`
    const header = `
      <div class="pp-comm-row pp-comm-head" ${gridStyle}>
        <div class="pp-comm-head-cell"><i data-lucide="clock"></i> When this happens…${rh(0)}</div>
        <div class="pp-comm-head-cell"><i data-lucide="activity"></i> I do this…${rh(1)}</div>
        <div class="pp-comm-head-cell"><i data-lucide="lightbulb"></i> It means…${rh(2)}</div>
        <div class="pp-comm-head-cell"><i data-lucide="hand"></i> I want you to…</div>
      </div>`
    const body = rows.map((r, i) => {
      const key = `comm_row_${r.id || i}`
      return `
        <div class="pp-comm-row pp-comm-body has-pb-chip" ${gridStyle}>
          ${pbChip(pb, key)}
          <div>${esc(r.happening || "")}</div>
          <div class="pp-comm-bold">${esc(r.behavior || "")}</div>
          <div class="pp-comm-meaning">${esc(r.meaning || "")}</div>
          <div class="pp-comm-support">${esc(r.support || "")}</div>
        </div>`
    }).join("")
    return `<div class="pp-comm-wrap">${toolbar}${header}${body}</div>`
  },

  goals(plan, sectionNum) {
    const arr = plan.goals || []
    if (!arr.length) return `<p class="pp-empty">No goals defined yet.</p>`
    const pb = plan.meta?.pageBreaks || {}
    const sn = sectionNum || 12
    // Mirrors pcp_builder.html#9709 (legacy goalCardHtml in /editor).
    // Structure per card:
    //   12.<n>                                                 [12 mo]
    //   Heading (primary-color bold, optional)
    //   Description (regular text; bullet sub-items if multi-line)
    //   ● Service Type   TAG_PILL  — comment
    //       [Currently Approved | Requesting]  [Weekly → 2x 60min/wk] [ratio]
    //   ● ...
    const tagInfo = {
      general: { cls: "pp-rtag-generic", label: "Generic",   dotCls: "pp-goal-dot-general", subCls: "pp-goal-sub-rc",    subLabel: "Currently Approved",   chipCls: "pp-goal-sched-green" },
      rc:      { cls: "pp-rtag-rc",      label: "RC Funded", dotCls: "pp-goal-dot-rc",      subCls: "pp-goal-sub-rc",    subLabel: "Currently Approved",   chipCls: "pp-goal-sched-green" },
      unmet:   { cls: "pp-rtag-unmet",   label: "Unmet Need",dotCls: "pp-goal-dot-unmet",   subCls: "pp-goal-sub-unmet", subLabel: "Currently Requesting", chipCls: "pp-goal-sched-amber" }
    }
    const fmtChipBody = (s) => {
      if (s.mode === "cost" && s.cost) {
        return "$" + esc(String(s.cost)) + esc(pcpFmtSchedUnit(s))
      }
      return esc(pcpFmtSchedTime(s)) + esc(pcpFmtSchedUnit(s))
    }
    return arr.map((g, i) => {
      const desc = String(g.description || "")
      const descLines = desc.split("\n")
      const descHead = descLines[0] || ""
      const descRest = descLines.slice(1).filter(l => l.trim())
      const heading = (g.heading || "").trim()

      const headingHtml = heading
        ? `<span class="pp-goal-heading">${esc(heading)}</span>` : ""
      const descHtml = descRest.length > 0
        ? `<div class="pp-goal-desc">${esc(descHead)}<ul>${descRest.map(l => `<li>${esc(l.trim())}</li>`).join("")}</ul></div>`
        : `<div class="pp-goal-desc">${esc(descHead)}</div>`

      const resHtml = (g.resources || []).map(r => {
        const t = tagInfo[r.tag] || tagInfo.general
        const displayName = ppServiceName(r)
        const sched = (r.schedule || []).filter(s => scheduleHasContent(s) || s.cost)
        const hasRatio = r.ratio && r.ratio !== "1:1"
        const schedChips = sched.map(s => `
          <span class="pp-goal-sched-chip ${t.chipCls}">
            <span class="pp-goal-sched-day">${esc(s.dayType || "")}</span>
            <span class="pp-goal-sched-arr">&rarr;</span>
            <span class="pp-goal-sched-val">${fmtChipBody(s)}</span>
          </span>`).join("")
        const ratioChip = hasRatio
          ? `<span class="pp-goal-sched-ratio ${t.chipCls}">${esc(r.ratio)} ratio</span>` : ""
        const secondaryDesc = r.currentServices || ""
        // Optional mileage row — only when the per-resource toggle
        // (Include in PDF) is on AND the service actually involves
        // travel (i.e. not in-home / telehealth) AND we have miles.
        // Vendor + its cost/mileage live on r.vendors now (the editor's source
        // of truth). Use the first vendor when the array is present — null
        // means the vendor was REMOVED, so the vendor/cost/mileage cards below
        // render nothing. The legacy flat fields (r.vendorName/cost/mileage…)
        // aren't cleared on removal, so only fall back to them for old
        // resources that were never migrated to a vendors array.
        // Uses resolvePrintVendor to resolve library-linked vendors live.
        const rawV = Array.isArray(r.vendors)
          ? (r.vendors[0] || null)
          : { name: r.vendorName, phone: r.vendorPhone, email: r.vendorEmail,
              website: r.vendorWebsite, address: r.vendorAddress,
              cost: r.cost, mileage: r.mileage, servicesLocation: r.servicesLocation }
        // Resolve display fields from library when linked
        const resolvedV = rawV ? resolvePrintVendor(rawV) : null
        // V merges resolved display fields with resource-specific fields (cost, mileage, location)
        const V = rawV ? {
          ...rawV,
          name: resolvedV.name,
          phone: resolvedV.phone,
          email: resolvedV.email,
          website: resolvedV.website,
          address: resolvedV.address
        } : null
        const vCost     = V ? (V.cost || "") : ""
        const vMileage  = V ? (V.mileage || "") : ""
        const vLocation = V ? (V.servicesLocation || "") : ""
        const showMileage = !!r.includeMileageInPdf &&
                            vLocation !== "In Home" &&
                            vLocation !== "Telehealth" &&
                            vMileage
        const annualTravel = showMileage ? computeAnnualTravel({ servicesLocation: vLocation, mileage: vMileage, schedule: r.schedule }) : null
        const mileageClean = showMileage
          ? String(vMileage).replace(/\s*\((est|osrm|ors)\)$/, "")
          : ""
        // Mileage reimbursement cost — only when the rate is set on
        // the plan AND we have an annual travel total.
        const mileageRate = parseFloat(plan?.meta?.mileageRate)
        const mileageCost = (annualTravel && isFinite(mileageRate) && mileageRate > 0)
          ? annualTravel.annualMiles * mileageRate
          : null
        // Mileage card — only renders when Claim Mileage is on.
        // Shows the one-way miles, annual mileage breakdown, and (when
        // a plan-level reimbursement rate is set) the dollar claim.
        const mileageHtml = showMileage
          ? `<div class="pp-goal-card-block pp-mileage-card">
               <div class="pp-goal-card-head">
                 <span class="pp-goal-card-icon">🚗</span>
                 <span class="pp-goal-card-title">Mileage &amp; Reimbursement</span>
               </div>
               <div class="pp-goal-card-grid pp-mileage-grid">
                 <div class="pp-goal-card-cell">
                   <div class="pp-goal-card-cell-label">One-way</div>
                   <div class="pp-goal-card-cell-value">${esc(mileageClean)} mi</div>
                 </div>
                 ${annualTravel ? `
                   <div class="pp-goal-card-cell">
                     <div class="pp-goal-card-cell-label">Annual miles</div>
                     <div class="pp-goal-card-cell-value">${annualTravel.annualMiles.toLocaleString(undefined, { maximumFractionDigits: 0 })} mi</div>
                     <div class="pp-goal-card-cell-foot">${annualTravel.annualTrips.toLocaleString()} sessions × ${annualTravel.mileagePerTrip} mi × 2</div>
                   </div>` : ""}
                 ${mileageCost !== null ? `
                   <div class="pp-goal-card-cell pp-goal-card-cell-emphasis">
                     <div class="pp-goal-card-cell-label">Annual claim</div>
                     <div class="pp-goal-card-cell-value">$${Math.round(mileageCost).toLocaleString()}</div>
                     <div class="pp-goal-card-cell-foot">@ $${mileageRate.toFixed(2)}/mi</div>
                   </div>` : ""}
               </div>
             </div>`
          : ""
        // Service Cost card — per-period and annual dollar rollup for the
        // resource's own cost rate (independent of mileage). ONLY when the vendor's
        // "Include Cost" toggle is on (OFF by default) — otherwise cost is neither
        // shown nor counted on the plan / PDF.
        const costRollup = (V && V.includeCost)
          ? computeAnnualCost({ cost: vCost, schedule: r.schedule })
          : null
        const usd = (n) => "$" + Math.round(n).toLocaleString()
        const costHtml = costRollup
          ? `<div class="pp-goal-card-block pp-cost-card">
               <div class="pp-goal-card-head">
                 <span class="pp-goal-card-icon">💰</span>
                 <span class="pp-goal-card-title">Service Cost</span>
               </div>
               <div class="pp-goal-card-grid pp-cost-grid">
                 <div class="pp-goal-card-cell">
                   <div class="pp-goal-card-cell-label">Rate</div>
                   <div class="pp-goal-card-cell-value">${esc(costRollup.rate || "")}</div>
                 </div>
                 <div class="pp-goal-card-cell">
                   <div class="pp-goal-card-cell-label">Per ${costRollup.periodLabel}</div>
                   <div class="pp-goal-card-cell-value">${usd(costRollup.periodCost)}</div>
                 </div>
                 <div class="pp-goal-card-cell pp-goal-card-cell-emphasis">
                   <div class="pp-goal-card-cell-label">Annual</div>
                   <div class="pp-goal-card-cell-value">${usd(costRollup.annualCost)}</div>
                 </div>
               </div>
             </div>`
          : ""
        // Vendor card — Name / Phone / Email / Website / Address shown
        // as labeled cells in a tinted bordered box (matches the tag
        // theme so unmet vs. approved goals stay visually distinct).
        const vendorRows = []
        if (V) {
          if (V.name)    vendorRows.push(["Vendor",  esc(V.name)])
          if (V.phone)   vendorRows.push(["Phone",   `<a href="tel:${esc(String(V.phone).replace(/[^\d+]/g, ""))}">${esc(V.phone)}</a>`])
          if (V.email)   vendorRows.push(["Email",   `<a href="mailto:${esc(V.email)}">${esc(V.email)}</a>`])
          if (V.website) vendorRows.push(["Website", `<a href="${esc(V.website)}" target="_blank" rel="noopener">${esc(V.website)}</a>`])
          if (V.address) vendorRows.push(["Address", `<a href="https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(V.address)}" target="_blank" rel="noopener">${esc(V.address)}</a>`])
          // Brochure chip — internal anchor pointing to the brochure
          // appendix page at the back of the PCP. Same href works in
          // the on-screen preview (smooth scroll) and the printed PDF
          // (Chrome's PDF renderer turns same-doc anchors into page
          // jumps), so the reader can flip to the rate sheet without
          // chasing a separate file.
          const pamphlets = Array.isArray(r.vendorPamphlets) ? r.vendorPamphlets : []
          if (pamphlets.length) {
            const anchor = `brochure-vendor-${esc(String(r.vendorId || V.name || ""))}`
            const single = pamphlets.length === 1
            const label = single
              ? `<span class="pp-pamphlet-chip-name">${esc(pamphlets[0].filename || "Brochure")}</span>`
              : `<span class="pp-pamphlet-chip-name">${pamphlets.length} brochures</span>`
            const chip = `<a class="pp-pamphlet-chip" href="#${anchor}">
                            <span class="pp-pamphlet-chip-icon">${pamphlets[0].is_image ? "🖼️" : "📄"}</span>
                            ${label}
                            <span class="pp-pamphlet-chip-hint">→ last page</span>
                          </a>`
            vendorRows.push(["Brochure", chip])
          }
        }
        const vendorHtml = vendorRows.length
          ? `<div class="pp-goal-card-block pp-vendor-card pp-vendor-${r.tag || "general"}">
               <div class="pp-goal-card-head">
                 <span class="pp-goal-card-icon">🏢</span>
                 <span class="pp-goal-card-title">Vendor Details</span>
               </div>
               <div class="pp-goal-card-grid pp-vendor-grid">
                 ${vendorRows.map(([k, v]) => `
                   <div class="pp-goal-card-cell">
                     <div class="pp-goal-card-cell-label">${k}</div>
                     <div class="pp-goal-card-cell-value">${v}</div>
                   </div>`).join("")}
               </div>
             </div>`
          : ""
        const subHtml = (secondaryDesc || schedChips || ratioChip)
          ? `<div class="pp-goal-item-sub">
               <span class="pp-goal-sub-label ${t.subCls}">${t.subLabel}</span>
               ${ppVendorChip(secondaryDesc)}
               ${schedChips}
               ${ratioChip}
             </div>${vendorHtml}${costHtml}${mileageHtml}`
          : `${vendorHtml}${costHtml}${mileageHtml}`
        const commentHtml = ppServiceComment(r)
          ? ` <span class="pp-goal-item-comment">&mdash; ${esc(ppServiceComment(r))}</span>`
          : ""
        return `
          <div class="pp-goal-item">
            <span class="pp-goal-dot ${t.dotCls}"></span>
            <div class="pp-goal-item-body">
              <div class="pp-goal-item-title">
                <span class="pp-goal-item-name">${esc(displayName)}</span>
                <span class="${t.cls}">${t.label}</span>
                ${commentHtml}
              </div>
              ${subHtml}
            </div>
          </div>`
      }).join("")

      // Reference documents (DDS directives / statutes) cited on the goal.
      const refDocIcon = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M9 13h6M9 17h4"/></svg>`
      const refsHtml = (g.references || [])
        .filter(r => r && (r.title || r.url))
        .map(r => {
          const label = esc(r.title || r.url || "")
          const hasUrl = r.url && /^https?:\/\//i.test(r.url)
          const inner = `<span class="pp-ref-ic">${refDocIcon}</span><span class="pp-ref-tx">${label}</span>${hasUrl ? `<span class="pp-ref-ar">↗</span>` : ""}`
          return hasUrl
            ? `<a class="pp-ref-chip" href="${esc(r.url)}" target="_blank" rel="noopener">${inner}</a>`
            : `<span class="pp-ref-chip pp-ref-chip-plain">${inner}</span>`
        }).join("")
      return `
        <div class="pp-goal-card has-pb-chip" id="ref-goal-${i}">
          ${pbChip(pb, `goal_${g.id || i}`)}
          ${g.timeline ? `<div class="pp-goal-timeline">${esc(g.timeline)}</div>` : ""}
          ${editBtn("goals", `ref-goal-${i}`)}
          <div class="pp-goal-titleline">
            <span class="pp-goal-num">${sn}.${i + 1}</span>
            ${headingHtml}
          </div>
          ${descHtml}
          ${resHtml ? `<div class="pp-goal-resources">${resHtml}</div>` : ""}
          ${refsHtml ? `<div class="pp-goal-refs"><span class="pp-goal-refs-label"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>References &amp; Authority</span><div class="pp-goal-refs-list">${refsHtml}</div></div>` : ""}
        </div>`
    }).join("")
  },

  barriers(plan, sectionNum) {
    const arr = (plan.barriers || []).filter(b => b.description || b.strategy)
    if (!arr.length) return `<p class="pp-empty">No barriers identified yet.</p>`
    const pb = plan.meta?.pageBreaks || {}
    // Mirrors pcp_builder.html#8042 — yellow→orange→red gradient
    // header card with barrier count badge, then per-barrier colored
    // sub-cards with Description + Strategy + Services-Needed boxes.
    const palette = [
      { card: "pp-bar-yellow", num: "pp-bar-num-yellow", desc: "pp-bar-desc-yellow", strat: "pp-bar-strat-green" },
      { card: "pp-bar-orange", num: "pp-bar-num-orange", desc: "pp-bar-desc-orange", strat: "pp-bar-strat-emerald" },
      { card: "pp-bar-red",    num: "pp-bar-num-red",    desc: "pp-bar-desc-red",    strat: "pp-bar-strat-teal" },
      { card: "pp-bar-amber",  num: "pp-bar-num-amber",  desc: "pp-bar-desc-amber",  strat: "pp-bar-strat-lime" }
    ]
    const rows = arr.map((b, i) => {
      const c = palette[i % palette.length]
      // Each resource: tag pill + "{type} — {comment}" + (for rc /
      // general with data) the green Currently Approved details box.
      // Mirrors pcp_builder.html#8117-8142 — barriers use the same
      // box shape that goals do, just emitted inside the Services
      // Needed list.
      const resHtml = (b.resources && b.resources.length)
        ? `<div class="pp-bar-resbox">
             <span class="pp-bar-reslabel">Services Needed</span>
             <ul class="pp-bar-reslist">${b.resources.map(r => {
               const tagCls = r.tag === "rc" ? "pp-rtag-rc" : r.tag === "unmet" ? "pp-rtag-unmet" : "pp-rtag-generic"
               const tagLbl = r.tag === "rc" ? "RC Funded" : r.tag === "unmet" ? "Unmet Need" : "Generic"
               const text   = ppServiceName(r) + (ppServiceComment(r) ? " — " + ppServiceComment(r) : "")
               return `<li>
                 <span class="${tagCls}">${tagLbl}</span> ${esc(text)}
                 ${pcpRenderRcDetailsBox(r)}
               </li>`
             }).join("")}</ul>
           </div>`
        : ""
      return `
        <div class="pp-bar-card ${c.card} has-pb-chip" id="ref-barrier-${i}">
          ${pbChip(pb, `barrier_${b.id || i}`)}
          <div class="pp-bar-row-head">
            <div class="pp-bar-num ${c.num}">${i + 1}</div>
            <h3 class="pp-bar-row-title">${sectionNum}.${i + 1} Barrier</h3>
          </div>
          <div class="pp-bar-content">
            ${b.description ? `<div class="pp-bar-subbox ${c.desc}"><span class="pp-bar-sublabel">Description</span><p>${esc(b.description)}</p></div>` : ""}
            ${(b.strategy && b.strategy.trim()) ? `<div class="pp-bar-subbox ${c.strat}"><span class="pp-bar-sublabel">Strategy to Address</span><p>${esc(b.strategy)}</p></div>` : ""}
            ${resHtml}
          </div>
        </div>`
    }).join("")
    // Production's pdfjs render of barriers ends up as a plain h2 + the
    // colored per-barrier cards (the gradient banner from
    // pcp_builder.html#8042-8074 doesn't survive its PDF pipeline). The
    // section page wrapper provides the h2 — we just emit the card list.
    return `<div class="pp-bar-list">${rows}</div>`
  },

  currentServices(plan) {
    // Port of pcp_builder.html#7822. Section 14 renders two streams:
    //   1. Plain (non-linked) items as a blue glow-list bullet list.
    //   2. Linked items grouped by service type — one green-bordered
    //      card per type with the service pill, cross-ref chips
    //      (Goal X.Y / Barrier X.Y / Works X.Y), a right-aligned
    //      "Live from Goals, Barriers & Working" label, and the
    //      aggregated resource detail rows beneath.
    // Each card (plain + every linked type) carries its own
    // page-break chip so the user can push any single service to
    // the next page from the review tab.
    const pb       = plan.meta?.pageBreaks || {}
    const csItems  = plan.currentServices || []
    const csPlain  = csItems.filter(s => !s.linkedType)
    const csLinked = csItems.filter(s =>  s.linkedType)
    if (csPlain.length === 0 && csLinked.length === 0) {
      return `<p class="pp-empty">No current services listed.</p>`
    }

    // Section numbers for the ref chips ("Goal 13.6", etc.).
    // Mirrors the filter/skip rules in render() so the numbers stay
    // aligned with the visible section H2s.
    const sectionNumOf = (key) => {
      const order = pcpSectionOrder()
        .filter(k => k !== "client" && k !== "facilitator")
        .filter(k => isSectionIncluded(plan, k))
      const idx = order.indexOf(key)
      return idx >= 0 ? idx + 1 : 0
    }
    const goalsNum    = sectionNumOf("goals")    || 13
    const barriersNum = sectionNumOf("barriers") || 12
    const worksNum    = sectionNumOf("working")  || 11

    // Tag colors / labels for the resource row pills.
    const tagCls = (tag) =>
      tag === "general" ? "pp-rtag-generic" : "pp-rtag-rc"
    const tagLabel = (tag) =>
      tag === "general" ? "Generic" : "RC Funded"

    // Plain items — blue card with glow-list bullets. Each item gets
    // its own tag pill (RC default) and, when the item carries its
    // own resources[] with schedule/ratio data, an inline frequency
    // chip row underneath. The card carries the "cs_plain"
    // page-break chip — same key the legacy file uses
    // (pcp_builder.html#7856 pbAttr('cs_plain')).
    // Plain (manually-added) items — each rendered with the SAME
    // green-chip card shape as the linked services: the service name in a
    // green header chip, with the RC Funded / Generic tag on the row below.
    // No ref chips or "Live from…" label, since these aren't linked.
    const plainHtml = csPlain.filter(s => (s.text || "").trim()).map(it => {
      const t    = it.tag === "general" ? "general" : "rc"
      const name = (it.text || "").trim()
      const desc = (it.currentServices || "").trim()
      // Manual services carry their schedule directly on it.schedule
      // (set by the CS-list Frequency editor) — render it as the same
      // dayType → time/wk chip the goals/linked rows use.
      const chips = (it.schedule || [])
        .filter(scheduleHasContent)
        .map(s => `
          <span class="pp-work-sched">
            <span class="pp-work-sched-d">${esc(s.dayType || "")}</span>
            <span class="pp-work-sched-arr">&rarr;</span>
            <span class="pp-work-sched-t">${esc(pcpFmtSchedTime(s))}${esc(pcpFmtSchedUnit(s))}</span>
          </span>`).join(" ")
      const pbKey = "cs_plain_" + (name.replace(/[^A-Za-z0-9]+/g, "_").toLowerCase() || "item")
      return `
        <div class="pp-cs-linked-card has-pb-chip">
          ${pbChip(pb, pbKey)}
          <div class="pp-cs-linked-head">
            <span class="pp-cs-linked-type">${esc(name)}</span>
          </div>
          <div class="pp-cs-linked-body">
            <div class="pp-cs-row">
              <span class="${tagCls(t)}">${tagLabel(t) === "RC Funded" ? "RC FUNDED" : "GENERIC"}</span>
              <span class="pp-cs-row-desc">${desc ? esc(desc) : ""}</span>
              <span class="pp-cs-row-chips">${chips}</span>
            </div>
          </div>
        </div>`
    }).join("")

    // Linked items — group by linkedType, aggregate resources from
    // goals/barriers/working into one card per type. Mirrors the
    // legacy loop at pcp_builder.html#7872-7951.
    const linkedByType = new Map()
    csLinked.forEach(item => {
      if (!linkedByType.has(item.linkedType)) linkedByType.set(item.linkedType, [])
    })
    linkedByType.forEach((_, type) => {
      const out = []
      const collect = (arr, kind, refLabel) => {
        ;(arr || []).forEach((row, ri) => {
          ;(row.resources || []).forEach(r => {
            if (r.tag === "unmet")            return
            if (r.showInReview === false)     return
            if (!matchesServiceLink(r, type)) return
            const linked = !!r.isLinked
            out.push({
              ...r,
              sourceRef: `${refLabel} ${kind}.${ri + 1}`,
              source: refLabel.toLowerCase(),
              schedule: linked ? [] : (r.schedule || []),
              ratio:    linked ? "" : (r.ratio || ""),
              currentServices: linked ? "" : (r.currentServices || ""),
              comment:  linked ? "" : (r.comment || "")
            })
          })
        })
      }
      collect(plan.goals,                goalsNum,    "Goal")
      collect(plan.barriers,             barriersNum, "Barrier")
      collect(plan.working?.works,       worksNum,    "Works")
      // Note: skipping doesn't-work — the legacy includes it but the
      // screenshot reference only shows Goal/Works/Barrier chips so
      // we don't surface notWorking refs.
      const hasData = (d) =>
        (d.schedule && d.schedule.length > 0) ||
        (d.ratio && d.ratio !== "1:1") ||
        d.currentServices || d.comment
      out.sort((a, b) => (hasData(a) === hasData(b)) ? 0 : (hasData(a) ? -1 : 1))
      linkedByType.set(type, out)
    })

    // Ref chip — clickable <a> that scrolls to the target.
    //   Goal X.Y    → #ref-goal-<Y-1>     (0-based goal index)
    //   Barrier X.Y → #ref-barrier-<Y-1>  (0-based barrier index)
    //   Works X.Y   → #section-working    (whole section)
    // Matches pcp_builder.html#7954 makeRefChip. Anchors are
    // emitted on each goal / barrier card by their respective
    // renderers. Click handling goes through #jumpToRef so the
    // smooth-scroll lands inside the .en-print-scroll container
    // (default browser hash navigation scrolls the document, not
    // the nested scroll frame, so the click felt like a no-op).
    const refChip = (ref) => {
      const isWorks   = /^Works/.test(ref)
      const isBarrier = /^Barrier/.test(ref)
      const cls = isWorks    ? "pp-cs-ref pp-cs-ref-works"
                : isBarrier  ? "pp-cs-ref pp-cs-ref-barrier"
                :              "pp-cs-ref pp-cs-ref-goal"
      let targetId = "section-working"
      if (!isWorks) {
        const m = ref.match(/\.(\d+)$/)
        const idx = m ? parseInt(m[1], 10) - 1 : 0
        targetId = `ref-${isBarrier ? "barrier" : "goal"}-${idx}`
      }
      return `<a class="${cls}"
                 href="#${esc(targetId)}"
                 data-action="click->print-preview#jumpToRef"
                 data-target-id="${esc(targetId)}">${esc(ref)}</a>`
    }

    const linkedHtml = [...linkedByType.entries()]
      .filter(([, items]) => items.length > 0)
      .map(([type, items]) => {
        // "Other" services print their typed-in name (the resource's
        // comment) instead of the literal "Other" — mirrors ppServiceName
        // so the header reads what the user actually entered.
        const typeLabel = type === "Other"
          ? (items.map(ppServiceName).find(n => n && n !== "Other") || "Other")
          : type
        // Collect every unique ref so the header chips read
        // "Goal 13.6  Works 11.2  Barrier 12.1" the same way as
        // the screenshot reference.
        const refSet = new Set()
        items.forEach(r => { if (r.sourceRef) refSet.add(r.sourceRef) })
        const refs = [...refSet].sort((a, b) => {
          const order = (s) => /^Goal/.test(s) ? 0 : /^Barrier/.test(s) ? 1 : 2
          return order(a) - order(b)
        })
        const headerRefs = refs.map(refChip).join(" ")

        // One row per detail: tag pill + (comment — currentServices)
        // + frequency chips + (ratio chip when not 1:1). Matches
        // pcp_builder.html#8013-8019 — flat row, flex-wrap.
        const rowsHtml = items.map(r => {
          const tag = r.tag === "general" ? "general" : "rc"
          const sched = (r.schedule || []).filter(scheduleHasContent)
          const schedChips = sched.map(s => `
            <span class="pp-work-sched">
              <span class="pp-work-sched-d">${esc(s.dayType || "")}</span>
              <span class="pp-work-sched-arr">&rarr;</span>
              <span class="pp-work-sched-t">${esc(pcpFmtSchedTime(s))}${esc(pcpFmtSchedUnit(s))}</span>
            </span>`).join(" ")
          const ratioInline = (r.ratio && r.ratio !== "1:1")
            ? `<span class="pp-work-ratio">${esc(r.ratio)} ratio</span>` : ""
          // Lift any "N hours /Month" / "30 mins /week" / "2 sessions /yr"
          // pattern out of the description text and re-render it as a
          // standalone frequency chip on the right — matches the
          // legacy editor's frequency-chip treatment.
          // Use ppServiceComment so an "Other" service's name (its comment)
          // isn't repeated here — it's already the card header. Non-"Other"
          // rows keep their comment as before.
          const descRaw = [ppServiceComment(r), r.currentServices].filter(Boolean).join(" — ")
          const freqM   = descRaw.match(CS_FREQ_RX)
          const freqChip = freqM
            ? `<span class="pp-cs-freq">${esc(freqM[0].replace(/\s+/g, " ").trim())}</span>`
            : ""
          const descClean = freqM
            ? descRaw.replace(CS_FREQ_RX, "").replace(/[:;\s—-]+$/, "").trim()
            : descRaw
          // No "No details" placeholder — the linked service name +
          // Goal/Barrier badge already identify the row, so an empty
          // description just stays blank.
          const descText = descClean ? esc(descClean) : ""
          return `
            <div class="pp-cs-row">
              <span class="${tagCls(tag)}">${tagLabel(tag) === "RC Funded" ? "RC FUNDED" : "GENERIC"}</span>
              <span class="pp-cs-row-desc">${descText}</span>
              <span class="pp-cs-row-chips">${freqChip}${schedChips}${ratioInline}</span>
            </div>`
        }).join("")

        // Per-linked-service page-break chip — key derived from the
        // service type so each card can push to the next page on
        // its own. Matches pcp_builder.html#8029 _csPbKey.
        const pbKey = "cs_" + type.replace(/[^A-Za-z0-9]+/g, "_").toLowerCase()
        return `
          <div class="pp-cs-linked-card has-pb-chip">
            ${pbChip(pb, pbKey)}
            <div class="pp-cs-linked-head">
              <i data-lucide="link" class="pp-cs-linked-icon"></i>
              <span class="pp-cs-linked-type">${esc(typeLabel)}</span>
              ${headerRefs}
              <span class="pp-cs-linked-live">Live from Goals, Barriers &amp; Working</span>
            </div>
            <div class="pp-cs-linked-body">${rowsHtml}</div>
          </div>`
      }).join("")

    return plainHtml + linkedHtml
  },

  unmetNeeds(plan, sectionNum) {
    // Derived directly from Goal / Barrier resources tagged "unmet" — a
    // table matching the editor's Unmet Needs list (service + source +
    // frequency chips + vendor). See _unmetNeedsTable.
    return RENDERERS._unmetNeedsTable(plan)
  },

  _unmetNeedsTable(plan) {
    const rows = []
    ;(plan.goals || []).forEach((g, gi) => {
      ;(g.resources || []).forEach(r => {
        if (r && r.tag === "unmet") rows.push({ r, ref: `Goal ${gi + 1}` })
      })
    })
    ;(plan.barriers || []).forEach((b, bi) => {
      ;(b.resources || []).forEach(r => {
        if (r && r.tag === "unmet") rows.push({ r, ref: `Barrier ${bi + 1}` })
      })
    })
    if (!rows.length) return `<p class="pp-empty">No unmet needs listed.</p>`
    // Same frequency chip the goal / barrier cards use ("Weekly → 2× 1 hrs/wk",
    // or "Weekly → 2 sessions/wk" when the row's unit is sessions) via the
    // shared pcpFmtSchedTime + pcpFmtSchedUnit helpers.
    const freqChips = (schedule) => (Array.isArray(schedule) ? schedule : [])
      .filter(scheduleHasContent)
      .map(s => `
        <span class="pp-work-sched">
          <span class="pp-work-sched-d">${esc(s.dayType || "")}</span>
          <span class="pp-work-sched-arr">&rarr;</span>
          <span class="pp-work-sched-t">${esc(pcpFmtSchedTime(s))}${esc(pcpFmtSchedUnit(s))}</span>
        </span>`).join(" ")
    const freqStore = plan.unmetFreq || {}
    // "Goal N" / "Barrier N" → the anchor id emitted on that card by the
    // goals / barriers renderers, so the ref pill jumps there through
    // #jumpToRef (same mechanism the other section renderers use). Goals
    // aren't filtered, so the card index is N-1. Barrier cards are only
    // emitted for barriers with a description/strategy, so map the plan
    // index to the rendered card index; empty string when no card exists.
    const refToTargetId = (ref) => {
      const gm = /^Goal\s+(\d+)$/.exec(ref)
      if (gm) return `ref-goal-${parseInt(gm[1], 10) - 1}`
      const bm = /^Barrier\s+(\d+)$/.exec(ref)
      if (bm) {
        const barr = (plan.barriers || [])[parseInt(bm[1], 10) - 1]
        const rendered = (plan.barriers || []).filter(b => b && (b.description || b.strategy))
        const idx = rendered.indexOf(barr)
        return idx >= 0 ? `ref-barrier-${idx}` : ""
      }
      return ""
    }
    const body = rows.map(({ r, ref }) => {
      const svc = (r.text || r.type || "").trim()
      const service = svc === "Other" ? (r.comment || "Other") : (svc || "Untitled service")
      const isBarrier = /^Barrier/.test(ref)
      const refCls = isBarrier ? "pp-un-ref-barrier" : "pp-un-ref-goal"
      const targetId = refToTargetId(ref)
      const refHtml = targetId
        ? `<a class="pp-un-ref ${refCls}" href="#${esc(targetId)}"
              data-action="click->print-preview#jumpToRef"
              data-target-id="${esc(targetId)}">${esc(ref)}</a>`
        : `<span class="pp-un-ref ${refCls}">${esc(ref)}</span>`
      const chips = freqChips(r.schedule)
      const manual = freqStore[String(r.id)]
      const freqHtml = chips || (manual ? esc(manual) : `<span class="pp-un-dash">&mdash;</span>`)
      // Resolve vendor names from library when linked
      const vendors = (Array.isArray(r.vendors) ? r.vendors : [])
        .map(v => resolvePrintVendor(v).name.trim()).filter(Boolean).join(", ")
      return `
        <tr>
          <td class="pp-un-td-need">${esc(service)}</td>
          <td class="pp-un-td-src">${refHtml}</td>
          <td class="pp-un-td-freq">${freqHtml}</td>
          <td class="pp-un-td-vendor">${vendors ? esc(vendors) : `<span class="pp-un-dash">&mdash;</span>`}</td>
        </tr>`
    }).join("")
    return `
      <table class="pp-un-table">
        <thead>
          <tr>
            <th>Unmet Need</th>
            <th>Goal / Barrier</th>
            <th>Requested Frequency</th>
            <th>Vendor Name</th>
          </tr>
        </thead>
        <tbody>${body}</tbody>
      </table>`
  },

  _unmetNeedsLegacy(plan, sectionNum) {
    // Port of pcp_builder.html#8761 — each unmet need renders as a
    // colored card whose theme is picked from a service-type lookup
    // (Behavior Services → red shield, Respite → rose lamp, …),
    // not a cycle. Header has a checkbox + index + service pill +
    // cross-ref chips. Body is one row per detail: bullet + comment
    // on the left, frequency + ratio chips on the right of the
    // same line. Footer ("N items / Action Required") only shows
    // when there are 2+ details.
    const arr = (plan.unmetNeeds || []).filter(n =>
      (n.text && n.text.trim()) || n.serviceType || n.linkedType
    )
    if (!arr.length) return `<p class="pp-empty">No unmet needs listed.</p>`
    const pb = plan.meta?.pageBreaks || {}

    // Service type → { icon, color } lookup. Port of
    // pcp_builder.html#8800-8863 serviceIconMap (current + legacy
    // labels). Anything not in the map falls back to "alert-
    // triangle" + "rose" so unrecognized service types still get a
    // usable card shell.
    const SVC_ICON = {
      "Adaptive Skills Training":                                  { icon: "sparkles",        color: "green" },
      "Adaptive Skill Training":                                   { icon: "sparkles",        color: "green" },
      "Adult Day Activities Program":                              { icon: "sun",             color: "amber" },
      "Adult Day Centers/Programs":                                { icon: "sun",             color: "amber" },
      "Assessment and Consultation":                               { icon: "clipboard-check", color: "blue" },
      "Behavior Services":                                         { icon: "shield",          color: "red" },
      "Behavior Management Services":                              { icon: "shield",          color: "red" },
      "Camp & Associated Travel Expenses":                         { icon: "tent",            color: "green" },
      "Case Management":                                           { icon: "users",           color: "green" },
      "Childcare Services":                                        { icon: "baby",            color: "rose" },
      "Client/Parent Support Behavior Intervention Training":      { icon: "heart-handshake", color: "rose" },
      "Community Integration Training":                            { icon: "map-pin",         color: "green" },
      "Coordinated Family Support Services (CFS)":                 { icon: "home",            color: "amber" },
      "Crisis Intervention Services":                              { icon: "siren",           color: "red" },
      "Crisis Team – Evaluation & Behavioral Intervention":        { icon: "siren",           color: "red" },
      "Durable Medical Equipment":                                 { icon: "wrench",          color: "gray" },
      "Early Start Therapeutic Services":                          { icon: "baby",            color: "purple" },
      "Educational Services":                                      { icon: "graduation-cap",  color: "blue" },
      "Employment Programs":                                       { icon: "briefcase",       color: "teal" },
      "Enhanced Service Coordination":                             { icon: "network",         color: "blue" },
      "Family Home Agency":                                        { icon: "home",            color: "amber" },
      "Family Home Agency (FHA) / Family Foster Agency (FFA)":     { icon: "home",            color: "amber" },
      "Family Support":                                            { icon: "heart-handshake", color: "rose" },
      "Health Care Facilities":                                    { icon: "hospital",        color: "rose" },
      "Health Care Facilities (HCF)":                              { icon: "hospital",        color: "rose" },
      "Home Health Supports":                                      { icon: "house",           color: "blue" },
      "Housing Support Services":                                  { icon: "building",        color: "teal" },
      "IHSS – In-Home Supportive Services":                        { icon: "house",           color: "blue" },
      "Increase Community Access":                                 { icon: "map-pin",         color: "green" },
      "Independent Living Services":                               { icon: "key",             color: "amber" },
      "Independent Living Services (ILS)":                         { icon: "key",             color: "amber" },
      "Infant Development Services":                               { icon: "heart-pulse",     color: "purple" },
      "Insurance, co-payments, coinsurance, and deductibles":      { icon: "credit-card",     color: "orange" },
      "Medical Specialists and Professionals":                     { icon: "stethoscope",     color: "purple" },
      "Mobility Training":                                         { icon: "footprints",      color: "green" },
      "Non-Medical Therapies":                                     { icon: "sparkles",        color: "purple" },
      "Occupational Therapy (OT)":                                 { icon: "brain",           color: "purple" },
      "Out-of-Home Respite Services":                              { icon: "moon",            color: "blue" },
      "Personal Assistance / Personal Care Services":              { icon: "hand",            color: "red" },
      "Personal Assistance Services":                              { icon: "hand",            color: "red" },
      "Pharmaceutical Services":                                   { icon: "pill",            color: "orange" },
      "Physical Therapy (PT)":                                     { icon: "brain",           color: "purple" },
      "Residential Care Homes":                                    { icon: "bed",             color: "teal" },
      "Respite Services (In-Home)":                                { icon: "lamp",            color: "rose" },
      "Respite Services – In-Home":                                { icon: "lamp",            color: "rose" },
      "Self-Determination":                                        { icon: "compass",         color: "amber" },
      "Service Coordination":                                      { icon: "network",         color: "blue" },
      "Social Rec":                                                { icon: "users",           color: "blue" },
      "Non Medical":                                               { icon: "music",           color: "purple" },
      "Education":                                                 { icon: "graduation-cap",  color: "amber" },
      "Camp":                                                      { icon: "tent",            color: "green" },
      "Social Recreation":                                         { icon: "users",           color: "blue" },
      "Social Skills Training":                                    { icon: "smile",           color: "green" },
      "Social Skills Training / Classes":                          { icon: "smile",           color: "green" },
      "Specialized Transportation":                                { icon: "bus",             color: "orange" },
      "Speech Therapy":                                            { icon: "brain",           color: "purple" },
      "Supplemental Program Supports":                             { icon: "puzzle",          color: "green" },
      "Supported Living Services":                                 { icon: "hand-helping",    color: "teal" },
      "Therapy Services":                                          { icon: "brain",           color: "purple" },
      "Transportation":                                            { icon: "bus",             color: "orange" },
      "Translation / Interpreter Services":                        { icon: "languages",       color: "blue" },
      "Translator/Interpreter Services":                           { icon: "languages",       color: "blue" }
    }
    const getSvcCat = (type) =>
      (type && SVC_ICON[type]) || { icon: "alert-triangle", color: "rose" }

    // Cross-ref chip — same scroll-to-target behavior as the
    // currentServices chips (#section-working for Works /
    // Not Working, #ref-goal-N / #ref-barrier-N otherwise).
    const refChip = (ref) => {
      const isWorks      = /^Works/.test(ref)
      const isNotWorking = /^Not Working/.test(ref)
      const isBarrier    = /^Barrier/.test(ref)
      const cls = isWorks      ? "pp-un-ref pp-un-ref-works"
                : isNotWorking ? "pp-un-ref pp-un-ref-notworks"
                : isBarrier    ? "pp-un-ref pp-un-ref-barrier"
                :                 "pp-un-ref pp-un-ref-goal"
      let targetId = "section-working"
      if (!isWorks && !isNotWorking) {
        const m = ref.match(/\.(\d+)$/)
        const i = m ? parseInt(m[1], 10) - 1 : 0
        targetId = `ref-${isBarrier ? "barrier" : "goal"}-${i}`
      }
      return `<a class="${cls}"
                 href="#${esc(targetId)}"
                 data-action="click->print-preview#jumpToRef"
                 data-target-id="${esc(targetId)}">${esc(ref)}</a>`
    }

    const cards = arr.map((needObj, idx) => {
      // Resolve linkedType → details on the fly (matches the legacy
      // inline resolution at pcp_builder.html#8203).
      let svcType = needObj.serviceType
      let details = needObj.details || []
      if (!svcType && needObj.linkedType) {
        const resolved = pcpResolveLinkedService(plan, needObj.linkedType, true)
        if (resolved.details.length > 0) {
          svcType = needObj.linkedType
          details = resolved.details
        }
      }

      // Dedupe details that share the same body (comment +
      // currentServices + ratio + schedule). When duplicates
      // share a comment but differ in source-refs, keep one copy
      // and union the refs into extraRefs so the header chip still
      // shows every origin. Mirrors pcp_builder.html#8920-8946.
      if (Array.isArray(details) && details.length > 1) {
        const seen = new Map()
        details.forEach(d => {
          const key = JSON.stringify({
            c:  (d.comment || "").trim(),
            cs: (d.currentServices || "").trim(),
            r:  d.ratio || "",
            s:  d.schedule || []
          })
          if (!seen.has(key)) {
            seen.set(key, { ...d, extraRefs: Array.isArray(d.extraRefs) ? [...d.extraRefs] : [] })
          } else {
            const merged = seen.get(key)
            const refs = new Set(merged.extraRefs.map(x => x.sourceRef).filter(Boolean))
            if (merged.sourceRef) refs.add(merged.sourceRef)
            if (d.sourceRef)      refs.add(d.sourceRef)
            ;(d.extraRefs || []).forEach(x => { if (x.sourceRef) refs.add(x.sourceRef) })
            merged.extraRefs = [...refs]
              .filter(s => s && s !== merged.sourceRef)
              .map(sourceRef => ({ sourceRef }))
          }
        })
        details = [...seen.values()]
      }

      // Cross-ref chips header — every unique sourceRef across the
      // resolved details (including the extraRefs union pulled in
      // by the dedupe step), sorted Goal → Barrier → Works →
      // Not Working so the chips read in the same order the
      // screenshot reference shows them.
      const refSet = new Set()
      details.forEach(d => {
        if (d.sourceRef) refSet.add(d.sourceRef)
        ;(d.extraRefs || []).forEach(x => { if (x.sourceRef) refSet.add(x.sourceRef) })
      })
      const refs = [...refSet].sort((a, b) => {
        const order = r => /^Goal/.test(r) ? 0
                         : /^Barrier/.test(r) ? 1
                         : /^Works/.test(r) ? 2 : 3
        return order(a) - order(b)
      })
      const refsHtml = refs.map(refChip).join(" ")

      // Body — one row per detail (matches pcp_builder.html#8985):
      //   [dot] [comment — currentServices]     [Weekly → 40 hrs/mo] [2:1 ratio]
      // Description sits left-justified, frequency + ratio chips
      // float to the right of the same row. No separate "Requested"
      // line — the chips themselves are the request. Plain-text
      // needs (no resolved details) drop the bullets and render
      // their text underneath the header.
      let bodyHtml
      if (svcType && details.length > 0) {
        bodyHtml = `<div class="pp-un-details">
          ${details.map(d => {
            const sched = (d.schedule || []).filter(scheduleHasContent)
            const hasRatio = d.ratio && d.ratio !== "1:1"
            const schedChips = sched.map(s => `
              <span class="pp-work-sched">
                <span class="pp-work-sched-d">${esc(s.dayType || "")}</span>
                <span class="pp-work-sched-arr">&rarr;</span>
                <span class="pp-work-sched-t">${esc(pcpFmtSchedTime(s))}${esc(pcpFmtSchedUnit(s))}</span>
              </span>`).join(" ")
            const ratioInline = hasRatio
              ? `<span class="pp-work-ratio">${esc(d.ratio)} ratio</span>` : ""
            const descParts = [d.comment, d.currentServices].filter(Boolean)
            const descInline = descParts.length
              ? esc(descParts.join(" — "))
              : `<span class="pp-un-nodetails">No details</span>`
            return `
              <div class="pp-un-detail-row">
                <span class="pp-un-bullet"></span>
                <span class="pp-un-detail-desc">${descInline}</span>
                <span class="pp-un-detail-chips">${schedChips}${ratioInline}</span>
              </div>`
          }).join("")}
        </div>`
      } else {
        bodyHtml = `<p class="pp-un-text">${esc(needObj.text || "")}</p>`
      }

      // Footer only renders when there are 2+ details — matches
      // pcp_builder.html#8995 which gates the "<N> items /
      // Action Required" strip on details.length >= 2. A single
      // detail's chips already speak for themselves; the footer
      // would just be noise.
      const cat = getSvcCat(svcType)
      const showFooter = details.length >= 2
      const footerHtml = showFooter ? `
        <div class="pp-un-card-foot">
          <span class="pp-un-count">${details.length} items</span>
          <span class="pp-un-status">Action Required</span>
        </div>` : ""

      // "Other" services show the specified name (a detail's comment) in
      // the header pill instead of the literal "Other".
      const svcDisplay = svcType === "Other"
        ? (details.find(d => d.comment)?.comment || "Other")
        : svcType

      return `
        <div class="pp-un-card pp-un-card-${cat.color} has-pb-chip">
          ${pbChip(pb, `unmet_${needObj.id || idx}`)}
          <div class="pp-un-card-head">
            <span class="pp-un-check"></span>
            <span class="pp-un-idx">${idx + 1}.</span>
            ${svcType
              ? `<span class="pp-un-pill"><i data-lucide="${cat.icon}"></i>${esc(svcDisplay)}</span>`
              : ""}
            ${refsHtml}
          </div>
          <div class="pp-un-card-body">${bodyHtml}</div>
          ${footerHtml}
        </div>`
    }).join("")

    return `<div class="pp-un-list">${cards}</div>`
  },

  // Legacy alias — older plans whose saved sectionOrder still has
  // "pricingDocs" should fall through to the renamed renderer.
  pricingDocs(plan) { return RENDERERS.brochures(plan) },
  // Brochure appendix — every PDF / screenshot the admin uploaded for
  // vendors referenced anywhere in this plan, surfaced after the last
  // PCP page as proof of pricing / paper trail.
  // Uses resolvePrintVendor to resolve library-linked vendors live.
  brochures(plan) {
    const goals = Array.isArray(plan.goals) ? plan.goals : []
    const seen = new Set()
    const entries = []
    goals.forEach(g => {
      (g.resources || []).forEach(r => {
        const pamphlets = Array.isArray(r.vendorPamphlets) ? r.vendorPamphlets : []
        if (!pamphlets.length) return
        const key = r.vendorId || r.vendorName || ""
        if (seen.has(key)) return
        seen.add(key)
        // Resolve vendor display from library when linked
        const resolved = resolvePrintVendor({
          vendorId: r.vendorId,
          name: r.vendorName,
          phone: r.vendorPhone,
          email: r.vendorEmail,
          website: r.vendorWebsite,
          address: r.vendorAddress
        })
        entries.push({
          vendorKey: key,
          vendorName: resolved.name || resolved.address || "Unnamed vendor",
          vendorPhone: resolved.phone,
          vendorEmail: resolved.email,
          vendorWebsite: resolved.website,
          pamphlets
        })
      })
    })
    if (!entries.length) {
      return `<p class="pp-empty">No vendor brochures attached to this plan.</p>`
    }
    return entries.map(v => `
      <div class="pp-pricing-doc" id="brochure-vendor-${esc(String(v.vendorKey))}">
        <div class="pp-pricing-doc-head">
          <span class="pp-pricing-doc-icon">📎</span>
          <span class="pp-pricing-doc-title">${esc(v.vendorName)}</span>
          <span class="pp-pricing-doc-meta">
            ${v.vendorPhone ? `${esc(v.vendorPhone)} · ` : ""}
            ${v.vendorEmail ? `${esc(v.vendorEmail)} · ` : ""}
            ${v.vendorWebsite ? esc(v.vendorWebsite) : ""}
          </span>
        </div>
        <div class="pp-pricing-doc-body">
          ${v.pamphlets.map(p => p.is_image
            ? `<figure class="pp-pricing-doc-img">
                 <img src="${esc(p.url)}" alt="${esc(p.filename)}">
                 <figcaption>${esc(p.filename)}</figcaption>
               </figure>`
            : `<a class="pp-pricing-doc-pdf" href="${esc(p.url)}" target="_blank" rel="noopener">
                 <span class="pp-pricing-doc-pdf-icon">📄</span>
                 <span class="pp-pricing-doc-pdf-name">${esc(p.filename)}</span>
                 <span class="pp-pricing-doc-pdf-hint">PDF — open</span>
               </a>`
          ).join("")}
        </div>
      </div>`).join("")
  },

  review(plan) {
    const r = plan.review || {}
    const check = (b) => b
      ? `<span class="pp-check pp-check-on">✓</span>`
      : `<span class="pp-check"></span>`
    return `
      <div class="pp-review">
        <p>${check(r.goalsCheck)} <strong>Goals:</strong> I confirm the goals and outcomes match our current priorities.</p>
        <p>${check(r.needsCheck)} <strong>Unmet Needs:</strong> The support needs listed are accurate.</p>
        <div class="pp-review-sign">
          <div><div class="pp-review-label">Printed Name</div><div class="pp-review-line">${esc(r.signerName || "")}</div></div>
          <div><div class="pp-review-label">Relationship</div><div class="pp-review-line">${esc(r.relationship || "")}</div></div>
          <div><div class="pp-review-label">Signature</div><div class="pp-review-line pp-review-line-tall"></div></div>
          <div><div class="pp-review-label">Date</div><div class="pp-review-line">${esc(r.date || "")}</div></div>
        </div>
      </div>`
  },

  // Port of pcp_builder.html#7379 _renderRcCalendarHalf — splits the
  // weekly grid into two letter-sized pages (12 AM–12 PM and 12 PM–12 AM)
  // so the table fits without overflow and the user gets the same
  // page-pair the production PDF produces. `half`:
  //   "am"  → slot indices 0..24   ( 12 AM – 12 PM )
  //   "pm"  → slot indices 24..48  ( 12 PM – 12 AM )
  //   null  → full day 0..48 (used as a fallback when only one half
  //           has slots and render() collapses to a single page)
  resourceCalendar(plan, _sectionNum, half) {
    const rc    = plan.resourceCalendar || {}
    const slots = rc.slots || []
    if (!slots.length) {
      return `<p class="pp-empty">No services scheduled yet — assign slots in the Service Calendar section.</p>${editBtn("resourceCalendar")}`
    }

    const CM = buildRcColorMap(plan)
    const colorFor = (name) => CM[name] || "#E5E7EB"

    // ---- Summary page ---------------------------------------------------
    // Single "Hours per service" table — every row carries an inline
    // category chip (RC / Generic / Unmet / Parental) next to the
    // service name. Rows are sorted by category order then by hours
    // descending so the visual grouping still reads top-to-bottom
    // without explicit group headers.
    if (half === "summary") {
      const WEEK_HOURS  = 168
      const MONTH_RATIO = 31 / 7
      const PARENTAL    = new Set(["Parental Care", "In Parental Care", "Charter Home School", pcpSchoolName(plan)])
      const fmt1 = (n) => (Math.round(n * 10) / 10).toFixed(1).replace(/\.0$/, "")
      const pct  = (n) => `${(Math.round((n / WEEK_HOURS) * 1000) / 10).toFixed(1)}%`

      const perService = {}
      slots.forEach(s => {
        const h = Math.max(0, (s.endHour || 0) - (s.startHour || 0))
        perService[s.resourceRef] = (perService[s.resourceRef] || 0) + h
      })

      // Build lookup tables for tag (rc / general) and unmet membership
      // so we can label each service with the right category.
      const tagLookup   = {}
      const unmetLookup = new Set()
      ;(plan.currentServices || []).forEach(s => {
        const key = s.linkedType || s.text || ""
        if (key && s.tag) tagLookup[key] = s.tag
      })
      ;(plan.unmetNeeds || []).forEach(n => {
        const key = n.linkedType || n.text || ""
        if (key) unmetLookup.add(key)
      })
      const categoryOf = (name) => {
        if (PARENTAL.has(name))         return "parental"
        if (unmetLookup.has(name))      return "unmet"
        if (tagLookup[name] === "rc")   return "rc"
        return "generic"
      }
      const categoryLabel = {
        rc:       "RC",
        generic:  "Generic",
        unmet:    "Unmet",
        parental: "Parental"
      }
      const categoryOrder = { rc: 0, generic: 1, unmet: 2, parental: 3 }

      const rows = Object.entries(perService)
        .map(([name, h]) => ({ name, h, cat: categoryOf(name) }))
        .sort((a, b) => (categoryOrder[a.cat] - categoryOrder[b.cat]) || (b.h - a.h))
        .map(({ name, h, cat }) => `
          <tr>
            <td class="pp-rc-summary-svc">
              <span class="pp-rc-summary-dot" style="background:${colorFor(name)}"></span>
              <span class="pp-rc-summary-name">${esc(name)}</span>
              <span class="pp-rc-summary-cat pp-rc-summary-cat--${cat}">${categoryLabel[cat]}</span>
            </td>
            <td><strong>${esc(getServiceAbbrev(name, plan))}</strong></td>
            <td>${fmt1(h)}</td>
            <td>${fmt1(h * MONTH_RATIO)}</td>
            <td>${pct(h)}</td>
          </tr>`).join("")

      const svcTable = `
        <h3 class="pp-rc-summary-h3">Hours per service</h3>
        <table class="pp-rc-summary-table pp-rc-summary-services">
          <thead>
            <tr>
              <th>Service</th>
              <th>Abbr</th>
              <th>Hours / wk</th>
              <th>Hours / mo</th>
              <th>% of week</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>`

      return `
        <div class="pp-rc-summary">
          <p class="pp-rc-summary-intro">
            There are <strong>168 hours</strong> in a week. This is how
            those hours are allocated across the services on this plan.
          </p>
          ${svcTable}
        </div>
        ${editBtn("resourceCalendar")}`
    }

    // Slot → day+half-hour lookup. Multiple slots can land in the same
    // cell (e.g. PA + IH on Wednesday 5 PM) so we keep an array per cell.
    const SL = {}
    slots.forEach(s => {
      const si0 = Math.round((s.startHour || 0) * 2)
      const si1 = Math.round((s.endHour   || 0) * 2)
      for (let si = si0; si < si1; si++) {
        const k = s.day + "-" + si
        if (!SL[k]) SL[k] = []
        SL[k].push(s)
      }
    })

    // Range to render
    let loSi, hiSi
    if (half === "am")      { loSi =  0; hiSi = 24 }
    else if (half === "pm") { loSi = 24; hiSi = 48 }
    else                    { loSi =  0; hiSi = 48 }

    // Weekly totals (used in legend) — built across ALL slots, not just
    // this half, so the AM and PM pages show identical legends.
    const hrs = {}
    slots.forEach(s => { hrs[s.resourceRef] = (hrs[s.resourceRef] || 0) + ((s.endHour || 0) - (s.startHour || 0)) })
    const usedRefs = [...new Set(slots.map(s => s.resourceRef))]

    const days = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"]
    // The editor stores day 0=Mon … 6=Sun (resource_calendar_controller's
    // DAYS order) and the display columns run in that same Mon→Sun order,
    // so the column index maps straight to the stored day index. (Was
    // [1,2,3,4,5,6,0], which shifted every column by one day — the editor's
    // Friday landed in the preview's Sunday column.)
    const DM = [0, 1, 2, 3, 4, 5, 6]

    const legend = `
      <div class="pp-rc-legend-chips">
        ${usedRefs.map(ref => {
          const bg = colorFor(ref)
          return `
          <span class="pp-rc-chip" style="background:${bg};${rcChipInk(bg)}">
            <strong>${esc(getServiceAbbrev(ref, plan))}</strong> ${esc(ref)} <em>${(hrs[ref] || 0).toFixed(1)} h/wk</em>
          </span>`
        }).join("")}
      </div>`

    let rows = ""
    for (let si = loSi; si < hiSi; si++) {
      const isHalfRow = si % 2 === 1
      const timeLabel = isHalfRow ? `<span class="pp-rc-half">:30</span>` : esc(fmtHour12(si / 2))
      rows += `<tr class="${isHalfRow ? "is-half" : ""}">`
      rows += `<td class="pp-rc-time">${timeLabel}</td>`
      for (let col = 0; col < 7; col++) {
        const d = DM[col]
        const cellSlots = SL[d + "-" + si] || []
        if (cellSlots.length) {
          const chips = cellSlots.map(s => {
            const bg = colorFor(s.resourceRef)
            return `<span class="pp-rc-cell-chip" style="background:${bg};${rcChipInk(bg)}">${esc(getServiceAbbrev(s.resourceRef, plan))}</span>`
          }).join(" ")
          rows += `<td class="pp-rc-cell">${chips}</td>`
        } else {
          rows += `<td class="pp-rc-cell"></td>`
        }
      }
      rows += `</tr>`
    }

    const grid = `
      <table class="pp-rc-grid">
        <colgroup>
          <col class="pp-rc-time-col">
          ${days.map(() => "<col>").join("")}
        </colgroup>
        <thead>
          <tr>
            <th class="pp-rc-time-th">Time</th>
            ${days.map(d => `<th>${d}</th>`).join("")}
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>`

    // Notes belong to the section as a whole — render them on the
    // last page (PM half, or the only half if just one is shown).
    const showNotes = (half === "pm" || half === null) && rc.notes && rc.notes.trim()
    const notesItems = showNotes
      ? rc.notes.split("\n").map(l => l.trim()).filter(Boolean)
      : []
    const notes = showNotes
      ? `<div class="pp-rc-notes">
          <strong class="pp-rc-notes-label">Notes:</strong>
          <ul>${notesItems.map(l => `<li>${esc(l)}</li>`).join("")}</ul>
        </div>`
      : ""

    return legend + grid + notes + editBtn("resourceCalendar")
  }
}

// Controller ---------------------------------------------------------

export default class extends Controller {
  static targets = ["pages", "themeBadge", "pbMenuBtn", "pbMenu", "pbMenuList", "sideRail"]

  connect() {
    this.plan = this._load()
    this.theme = THEMES[this.plan?.meta?.theme] || THEMES.celebration
    this.org = null
    this._wireSideRail()
    if (this.hasThemeBadgeTarget) this.themeBadgeTarget.textContent = this.theme.label
    // Apply the picked theme to the preview chrome so the cover
    // banner, section-header gradients, and accent color all
    // follow the user's choice — matches pcp_builder.html which
    // applies the picked theme to the rendered output, not the
    // editor-default indigo.
    this._applyTheme(this.theme)
    this.render()
    // If we arrived with a "#ref-goal-N" hash (e.g. the per-goal "preview"
    // icon in the editor), scroll to that card — the browser's own initial
    // anchor jump fires before this controller has built the DOM.
    this._scrollToInitialHash()
    // Backfill vendorPamphlets on any goal resource that was picked
    // BEFORE the wizard started snapshotting pamphlets — fetches
    // /api/vendors/:id for each unique missing vendorId and re-renders.
    this._hydratePamphlets()
    // PDF-render path injects the org payload as window.PCPM_PRELOAD_ORG
    // (the headless browser can't fetch /api/branding — no auth cookie).
    // Skip the fetch entirely when we already have it.
    if (window.PCPM_PRELOAD_ORG) {
      this.org = window.PCPM_PRELOAD_ORG
      // Render is queued on next tick; the synchronous render() above
      // already used `this.org = null` so we re-render with the org now.
      queueMicrotask(() => this.render())
    } else {
      // Pull workspace branding for the side strip + confidential notice
      // and re-render once it lands. /api/branding is auth-only / cheap.
      fetch("/api/branding", { credentials: "same-origin", headers: { Accept: "application/json" } })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data) { this.org = data; this.render() } })
        .catch(() => {})
    }
    this._onStorage = (e) => {
      if (e.key === STORAGE_KEY) {
        this.plan = this._load()
        this.theme = THEMES[this.plan?.meta?.theme] || THEMES.celebration
        if (this.hasThemeBadgeTarget) this.themeBadgeTarget.textContent = this.theme.label
        this._applyTheme(this.theme)
        this.render()
      }
    }
    window.addEventListener("storage", this._onStorage)
    // In-tab plan changes fire `plan:change` (dispatched by every
    // editor controller after a save) — the storage event only
    // fires across tabs, so without this listener the theme
    // picker on the preview header would save the new theme to
    // localStorage but the preview wouldn't redraw until the next
    // page load. Same pipeline as _onStorage above: refresh the
    // plan + theme, update the badge, re-render.
    this._onPlanChange = (e) => {
      const path = e?.detail?.path
      if (!path) return
      this.plan = e.detail.plan || this._load()
      const newTheme = THEMES[this.plan?.meta?.theme] || THEMES.celebration
      if (path === "meta.theme" || newTheme !== this.theme) {
        this.theme = newTheme
        if (this.hasThemeBadgeTarget) this.themeBadgeTarget.textContent = this.theme.label
        this._applyTheme(this.theme)
      }
      this.render()
    }
    document.addEventListener("plan:change", this._onPlanChange)
  }

  // Push the picked theme onto the preview frame as CSS custom
  // properties so every CSS rule that reads var(--color-primary)
  // or var(--header-gradient) picks up the new color without
  // having to know about themes. Mirrors pcp_builder.html's
  // applyTheme() which writes the same two CSS vars onto :root.
  _applyTheme(theme) {
    if (!theme) return
    this.element.style.setProperty("--color-primary", theme.primary)
    if (theme.gradient) {
      this.element.style.setProperty("--header-gradient", theme.gradient)
    }
  }

  disconnect() {
    window.removeEventListener("storage", this._onStorage)
    document.removeEventListener("plan:change", this._onPlanChange)
    this._headerObserver?.disconnect()
  }

  // Lazy-fetch vendor pamphlets for any resource that's pointing at a
  // vendor but doesn't carry the pamphlet snapshot yet (older plans
  // captured before the wizard started snapshotting). Single GET per
  // unique vendorId; mutates this.plan in place and re-renders.
  async _hydratePamphlets() {
    const goals = Array.isArray(this.plan?.goals) ? this.plan.goals : []
    const needed = new Set()
    goals.forEach(g => (g.resources || []).forEach(r => {
      if (r.vendorId && !(Array.isArray(r.vendorPamphlets) && r.vendorPamphlets.length)) {
        needed.add(r.vendorId)
      }
    }))
    if (!needed.size) return
    const ids = [...needed]
    const pamphletsByVendor = {}
    await Promise.all(ids.map(async (id) => {
      try {
        const res = await fetch(`/api/vendors?ids=${encodeURIComponent(id)}`, { credentials: "same-origin", headers: { Accept: "application/json" } })
        if (!res.ok) return
        const j = await res.json()
        const v = Array.isArray(j?.results) && j.results.find(r => String(r.id) === String(id))
        if (v && Array.isArray(v.pamphlets) && v.pamphlets.length) {
          pamphletsByVendor[id] = v.pamphlets
        }
      } catch (_) { /* swallow — chips just won't show */ }
    }))
    if (!Object.keys(pamphletsByVendor).length) return
    goals.forEach(g => (g.resources || []).forEach(r => {
      if (r.vendorId && pamphletsByVendor[r.vendorId] && !(r.vendorPamphlets || []).length) {
        r.vendorPamphlets = pamphletsByVendor[r.vendorId]
      }
    }))
    this.render()
  }

  // ---- Floating side rail (visible when toolbar scrolls off) ----
  // Watches the .en-preview-toolbar with IntersectionObserver; the
  // sideRail element is `hidden` while the toolbar intersects the
  // viewport and revealed otherwise. The rail's buttons relay clicks
  // to the original toolbar buttons (sideRailRelay below) so the
  // existing controllers don't need to know about the rail at all.
  _wireSideRail() {
    if (!this.hasSideRailTarget) return
    const header = this.element.querySelector(".en-preview-toolbar")
    if (!header) return
    this._headerObserver = new IntersectionObserver(entries => {
      const intersecting = entries[0]?.isIntersecting ?? true
      this.sideRailTarget.hidden = intersecting
    }, { rootMargin: "-1px 0px 0px 0px", threshold: 0 })
    this._headerObserver.observe(header)
    // Mirror the active theme color onto the rail's swatch.
    queueMicrotask(() => {
      const swatch = this.sideRailTarget.querySelector(".en-side-rail-swatch")
      if (swatch && this.theme?.primary) swatch.style.background = this.theme.primary
    })
  }

  // Side-rail button → simulate a click on its toolbar twin. For
  // dropdown buttons (theme + pagination) the menu is anchored to
  // the toolbar wrapper, so we scroll the user back to the top first
  // so the dropdown lands in view.
  sideRailRelay(event) {
    const kind = event.currentTarget?.dataset?.relay
    const selectors = {
      theme:      '.en-preview-toolbar [data-theme-picker-target="button"]',
      pagination: '.en-preview-toolbar [data-print-preview-target="pbMenuBtn"]',
      pdf:        '.en-preview-toolbar [data-editor-toolbar-target="pdfBtn"]'
    }
    const sel = selectors[kind]
    if (!sel) return
    const target = this.element.querySelector(sel)
    if (!target) return
    if (kind === "theme" || kind === "pagination") {
      window.scrollTo({ top: 0, behavior: "smooth" })
      setTimeout(() => target.click(), 320)
    } else {
      target.click()
    }
  }

  // The Service Calendar renders like every other section — always
  // shown in the preview (an empty one prints a short "no services
  // scheduled yet" note, exactly as other empty sections still appear).
  // Per-plan hiding goes through the section-enable toggle
  // (meta.sectionEnabled), which is checked BEFORE this at every call
  // site. The template's stale resourceCalendar.enabled:false flag —
  // which the native editor has no UI to flip — is intentionally
  // ignored so a painted calendar is never silently dropped.
  _rcVisible() {
    return true
  }

  render() {
    if (!this.hasPagesTarget) return
    if (!this.plan || Object.keys(this.plan).length === 0) {
      this.pagesTarget.innerHTML = `
        <div class="en-print-empty is-error">
          <i data-lucide="file-x"></i>
          <p>No plan data in this browser. Open the editor, fill in some fields, then come back.</p>
        </div>`
      renderIcons(this.element)
      return
    }
    // First pass: build the list of pages with their keys so we can
    // compute "PAGE n / N" headers correctly.
    const order = pcpSectionOrder()
    const pageQueue = [{ key: "cover" }, { key: "toc" }]
    // Optional consolidated "Open Questions" page (toolbar toggle) — a summary
    // of every unresolved item, linked to its section, near the front.
    if (this.plan.meta?.includeOpenQuestions) pageQueue.push({ key: "openQuestions" })
    let sectionNum = 0
    const sectionEnabled = (k) => isSectionIncluded(this.plan, k)
    order.forEach(key => {
      if (key === "client" || key === "facilitator") return
      // The review / sign page is removed in the legacy review tab
      // and replaced by an optional life-photo page at the very
      // end (see pcp_builder.html#10145-10167, "// Review/Sign
      // section removed"). We mirror that here so neither the
      // page queue nor the TOC carry a Review entry.
      if (key === "review") return
      if (key === "brochures") return   // Brochure appendix disabled for now
      if (!sectionEnabled(key)) return
      if (key === "resourceCalendar" && !this._rcVisible()) return
      sectionNum++
      // Service Calendar: split into AM/PM half-pages exactly like
      // pcp_builder.html#9890. Each half always fits on one letter
      // page; halves with no slots are skipped. When only one half has
      // slots we render that single half but pass `half: null` so the
      // notes block lands on it (and the title shows no suffix).
      if (key === "resourceCalendar") {
        const slots = this.plan.resourceCalendar?.slots || []
        const hasAm = slots.some(s => (s.startHour || 0) < 12 && (s.endHour || 0) > 0)
        const hasPm = slots.some(s => (s.endHour   || 0) > 12)
        if (hasAm && hasPm) {
          pageQueue.push({ key, sectionNum, half: "am", isFirstHalf: true  })
          pageQueue.push({ key, sectionNum, half: "pm", isFirstHalf: false })
        } else if (hasAm || hasPm) {
          pageQueue.push({ key, sectionNum, half: null, isFirstHalf: true })
        } else {
          // No slots — still push one empty-state page so the section
          // remains visible / numbered.
          pageQueue.push({ key, sectionNum, half: null, isFirstHalf: true })
        }
        // Append a tabular summary page whenever slots exist so the
        // reader can see total allocation, per-service hours, and the
        // per-day breakdown at a glance.
        if (slots.length) {
          pageQueue.push({ key, sectionNum, half: "summary", isFirstHalf: false })
        }
        return
      }
      pageQueue.push({ key, sectionNum })
    })
    // Optional life-photo page at the end — only when the plan
    // carries a lifePhoto or a caption. Matches pcp_builder.html
    // #10145-10165; replaces what used to be the Review/Sign
    // closing page.
    const c = this.plan.client || {}
    // Skip the life-photo page entirely when redacting — it's a photo of the
    // participant plus a free-text caption, both PHI.
    if (!this.plan.meta?.redacted &&
        (c.lifePhoto || (c.lifePhotoCaption && c.lifePhotoCaption.trim()))) {
      pageQueue.push({ key: "lifePhoto" })
    }
    // Optional Review / Acknowledgment / Release + signature page — the true
    // last page, appended only when the "Review & Sign Page" toolbar toggle is
    // on (plan.meta.releaseForm). Off by default.
    if (this.plan.meta?.releaseForm) {
      pageQueue.push({ key: "releaseForm" })
    }
    this.totalPages = pageQueue.length
    const pages = pageQueue.map((info, idx) => {
      const pageNum = idx + 1
      if (info.key === "cover")         return this._renderCover(pageNum)
      if (info.key === "toc")           return this._renderToc(pageNum)
      if (info.key === "openQuestions") return this._renderOpenQuestions(pageNum)
      if (info.key === "lifePhoto")   return this._renderLifePhoto(pageNum)
      if (info.key === "releaseForm") return this._renderReleaseForm(pageNum)
      return this._renderSectionPage(info.key, info.sectionNum, pageNum, info.half, info.isFirstHalf) || ""
    })
    this.pagesTarget.innerHTML = this._expandPlaceholders(pages.join(""))
    // Redact PHI on the rendered preview (names → tags, photo/age blanked)
    // when the Redact toggle is on. Runs on the DOM, after expansion is
    // suppressed, so nothing PII-bearing is left visible.
    if (this.plan?.meta?.redacted) this._redactDom()
    // Highlight unresolved placeholders (TBD … ?? / lone TBD / lone ??) so
    // reviewers can spot them in both the on-screen Review and the PDF.
    this._highlightPlaceholders(this.pagesTarget)
    renderIcons(this.element)
    // Auto-paginate after first render — split any .print-page whose
    // .print-card content overflows the page into multiple pages by
    // chunking the card's children. Two passes is enough because each
    // chunk is bounded by page height. Once splits settle we re-number
    // every page header so the "Page n / N" badge counts the post-split
    // page set, not the original list.
    requestAnimationFrame(() => {
      this._autoPaginate()
      this._renumberPageHeaders()
      this._inlineEditButtons()
      this._floatPbChips()
      renderIcons(this.element)
      this._sizeSideStrips()
      // If the user landed on /editor/native/preview#section-XXX (via the
      // section header's Preview chip in the editor pane), the browser's
      // native anchor-scroll fired BEFORE we rendered any pages so it
      // resolved to nothing and the viewport stayed at the top. Re-resolve
      // the hash now that the destination element exists.
      this._scrollToHash()
    })
  }

  // Honour a `#section-<key>` URL fragment after first render. Called
  // once from inside the RAF in render(); also wired to hashchange so a
  // user editing the URL directly still works.
  _scrollToHash() {
    const hash = window.location.hash
    if (!hash || hash.length < 2) return
    const target = document.getElementById(hash.slice(1))
    if (!target) return
    target.scrollIntoView({ behavior: "auto", block: "start" })
  }

  // Forward-expand the typeable placeholder tags the facilitator may
  // have sprinkled into list items / notes so the rendered preview
  // shows the real values. Mirrors pcp_builder.html#10181 (FirstName
  // pass) + #1805 expandHTMLTags ([Age] pass):
  //
  //   [FirstName] / [First Name] → plan.client.firstName
  //                               (case-insensitive, allows a space)
  //   [Age]                      → computed from plan.client.dob
  //
  // The legacy file gates the FirstName pass on !appState.redacted —
  // the native preview has no redact toggle yet, so we always expand.
  // We work on the joined HTML string before injecting into the DOM
  // so a single sweep covers every section + cover/toc page.
  // Wrap unresolved-placeholder text in a highlight span so it stands out in
  // the Review and the PDF. Matches a "TBD … ??" span first, then a lone TBD or
  // lone ?? (same markers the Open Questions panel keys off). Operates on text
  // nodes only, so tags/attributes are never touched and no HTML is injected.
  _highlightPlaceholders(root) {
    if (!root) return
    const HL_RX = /\bTBD\b[\s\S]*?\?\?|\bTBD\b|\?\?/gi
    const MARK_RX = /\bTBD\b|\?\?/i
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: (n) => {
        if (!n.nodeValue || !MARK_RX.test(n.nodeValue)) return NodeFilter.FILTER_REJECT
        const p = n.parentElement
        if (!p || p.closest("script, style, .pp-tbd-hl")) return NodeFilter.FILTER_REJECT
        return NodeFilter.FILTER_ACCEPT
      },
    })
    const targets = []
    let node
    while ((node = walker.nextNode())) targets.push(node)
    for (const textNode of targets) {
      const text = textNode.nodeValue
      HL_RX.lastIndex = 0
      let last = 0
      let m
      const frag = document.createDocumentFragment()
      while ((m = HL_RX.exec(text))) {
        if (!m[0]) { HL_RX.lastIndex++; continue }
        if (m.index > last) frag.appendChild(document.createTextNode(text.slice(last, m.index)))
        const span = document.createElement("span")
        span.className = "pp-tbd-hl"
        span.textContent = m[0]
        frag.appendChild(span)
        last = m.index + m[0].length
      }
      if (last === 0) continue
      if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)))
      textNode.parentNode.replaceChild(frag, textNode)
    }
  }

  _expandPlaceholders(html) {
    if (!html) return html
    // Redact mode: keep [FirstName]/[Age] tokens literal — the redaction
    // pass (_redactDom) strips PII, and expanding here would re-insert the
    // real name/age we just removed.
    if (this.plan?.meta?.redacted) return html
    const c = this.plan?.client || {}
    const firstName = (c.firstName && c.firstName.trim()) ||
                      (c.name && c.name.trim() && c.name.trim().split(/\s+/)[0]) ||
                      ""
    const lastName = (c.lastName && c.lastName.trim()) ||
                     (c.name && c.name.trim() && c.name.trim().split(/\s+/).slice(1).join(" ")) ||
                     ""
    const age = getAge(c.dob)
    let out = html
    if (firstName) {
      // esc() to keep accidental HTML in the first-name field from
      // breaking the surrounding markup.
      out = out.replace(/\[First\s*Name\]/gi, esc(firstName))
    }
    // [LastName] → surname. When there's no surname, drop the token (and any
    // leading space) so a "[FirstName] [LastName]" pair collapses cleanly to
    // just the first name — never a stray bracket or trailing space on a
    // formal/signable page.
    out = lastName
      ? out.replace(/\[Last\s*Name\]/gi, esc(lastName))
      : out.replace(/\s*\[Last\s*Name\]/gi, "")
    if (age !== null && age !== undefined) {
      out = out.replace(/\[Age\]/g, String(age))
    }
    return out
  }

  // ---- Redaction (plan.meta.redacted) ------------------------------
  // Privacy pass over the RENDERED preview: swaps every visible name for a
  // relationship tag, blanks the photo + age, and (via _expandPlaceholders)
  // leaves [FirstName]/[Age] tokens literal. Runs on live DOM text nodes so
  // it can't corrupt HTML attributes or the saved plan, and so it catches a
  // name wherever it renders — not only in the fields we anticipated.
  _redactDom() {
    const root = this.pagesTarget
    if (!root) return

    // 1. Photos — swap the participant photo for the empty avatar, and drop
    //    any life-photo image entirely.
    root.querySelectorAll("img.print-cover-participant-photo").forEach(img => {
      const ph = document.createElement("div")
      ph.className = "print-cover-participant-empty"
      ph.innerHTML = '<i data-lucide="user"></i>'
      img.replaceWith(ph)
    })
    root.querySelectorAll("[data-page='lifePhoto'] img, .print-life-photo img, .pp-life-photo img").forEach(img => img.remove())

    // 2. Cover age + name are class-targeted, so the cover never depends on
    //    string matching for its own PII.
    root.querySelectorAll(".print-cover-age-pill").forEach(el => {
      el.innerHTML = '<i data-lucide="cake"></i> AGE: [Age]'
    })
    const rc = this.plan?.client || {}
    const rcFirst = (rc.firstName || "").trim()
    const rcLast  = (rc.lastName  || "").trim()
    const maskedName = [
      rcFirst ? rcFirst[0].toUpperCase() + "XXXX" : "",
      rcLast  ? rcLast[0].toUpperCase()  + "XXXX" : "",
    ].filter(Boolean).join(" ") || "[FirstName]"
    root.querySelectorAll(".print-cover-participant-name").forEach(el => {
      el.textContent = maskedName
    })
    // UCI # → fully masked.
    root.querySelectorAll(".print-cover-uci-value").forEach(el => {
      el.textContent = "XXXX"
    })
    // City/state/zip narrows identity — the toggle is literally "Redact PHI".
    root.querySelectorAll(".print-cover-loc-pill").forEach(el => {
      el.innerHTML = '<i data-lucide="map-pin"></i> [Location]'
    })

    // Strip contact / address links (tel:/mailto:/maps) so vendor + contact
    // details don't survive inside href attributes the text walk can't reach.
    root.querySelectorAll('a[href^="tel:"], a[href^="mailto:"], a[href*="google.com/maps"], a[href*="maps.google"]').forEach(a => {
      a.removeAttribute("href")
      a.removeAttribute("target")
    })

    // 3. Free-text names → tags, everywhere they render.
    const map = this._buildRedactionMap()
    if (map.length) this._redactTextNodes(root, map)

    renderIcons(this.element)
  }

  // Ordered [{ re, tag }] replacement list. Client names first
  // ([FirstName]/[LastName]); each supporter keyed by the relationship in
  // their "(Brother)" parens and numbered so the same person reads the same
  // tag throughout (e.g. [Brother-01]). Longest names first so a full name
  // is consumed before its bare first-name substring.
  _buildRedactionMap() {
    const c = this.plan?.client || {}
    const about = this.plan?.about || {}
    const raw = []

    const first = (c.firstName || "").trim()
    const last  = (c.lastName  || "").trim()
    const full  = (c.name || "").trim() || [first, last].filter(Boolean).join(" ")
    // Mask a name to its first initial + XXXX ("John" → "JXXXX") — keeps the
    // plan identifiable at a glance without exposing the full name.
    const maskFirst = first ? first[0].toUpperCase() + "XXXX" : ""
    const maskLast  = last  ? last[0].toUpperCase()  + "XXXX" : ""
    const maskFull  = [maskFirst, maskLast].filter(Boolean).join(" ")
    if (full)  raw.push({ name: full,  tag: maskFull || "[FirstName]" })
    if (first) raw.push({ name: first, tag: maskFirst })
    if (last)  raw.push({ name: last,  tag: maskLast })
    // Also convert any literal [FirstName]/[LastName] tokens (section headings,
    // content kept literal in redact mode) to the same masked form.
    if (maskFirst) raw.push({ name: "[FirstName]", tag: maskFirst })
    if (maskLast)  raw.push({ name: "[LastName]",  tag: maskLast })
    const school = (c.school || "").trim()
    if (school) raw.push({ name: school, tag: "[School]" })
    // School actually lives in about.schoolDetails free text ("Lincoln
    // Elementary, 5th grade") — isolate the NAME exactly the way schoolList()
    // renders it (strip the school-type + grade + bracketed bits).
    ;(about.schoolDetails || []).forEach(it => {
      let s = itemText(it).trim()
      if (!s) return
      const tm = SCHOOL_TYPES.find(t => t.match.test(s))
      if (tm) s = s.replace(tm.match, " ")
      s = s.replace(GRADE_RX, " ")
           .replace(/\([^()]*\)/g, " ").replace(/\[[^\]]*\]/g, " ")
           .replace(/[,;\-–—]+/g, " ").replace(/\s{2,}/g, " ").trim()
      if (s.length >= 2) raw.push({ name: s, tag: "[School]" })
    })

    const counts = {}
    const tagFor = (rel) => {
      let key = String(rel || "").replace(/[^a-zA-Z]/g, "")
      key = key ? key.charAt(0).toUpperCase() + key.slice(1).toLowerCase() : "Person"
      counts[key] = (counts[key] || 0) + 1
      return `[${key}-${String(counts[key]).padStart(2, "0")}]`
    }
    const seenPeople = new Set()
    const addPerson = (name, rel) => {
      name = String(name || "").trim()
      if (name.length < 2) return
      const k = name.toLowerCase()
      if (seenPeople.has(k)) return
      seenPeople.add(k)
      raw.push({ name, tag: tagFor(rel) })
    }
    ;(about.whoLivesWithMe || []).forEach(it => {
      const t = (typeof it === "string" ? it : (it && (it.text || it.name)) || "").trim()
      if (!t) return
      addPerson(t.split(/[([]/)[0].trim(), (t.match(/\(([^)]+)\)/) || [])[1] || "Family")
    })
    ;(about.circleOfSupport || []).forEach(p => {
      if (p) addPerson(p.name || "", p.relationship || "Person")
    })

    // Facilitator (plan preparer) + workspace fallback — name / agency /
    // contact. It's the professional, not the participant, but redacted on
    // request for a fully de-identified share.
    const fac    = this.plan?.facilitator || {}
    const orgFac = (this.org && this.org.facilitator) || {}
    const facName    = (fac.name    || orgFac.name    || "").trim()
    const facAgency  = (fac.agency  || orgFac.agency  || "").trim()
    const facContact = (fac.contact || orgFac.contact || "").trim()
    if (facName)    raw.push({ name: facName,    tag: "[Facilitator]" })
    if (facAgency)  raw.push({ name: facAgency,  tag: "[Agency]" })
    if (facContact) raw.push({ name: facContact, tag: "[Contact]" })

    // Vendors / service providers on goal + barrier resources — the name
    // gets a stable [Vendor-NN]; phone/email/website/address → [Vendor].
    // Resolves from vendor library when linked.
    const vendorSeq = {}
    let vendorN = 0
    const vendorTagFor = (nm) => {
      const k = nm.toLowerCase()
      if (!vendorSeq[k]) vendorSeq[k] = ++vendorN
      return `[Vendor-${String(vendorSeq[k]).padStart(2, "0")}]`
    }
    ;[...(this.plan?.goals || []), ...(this.plan?.barriers || [])].forEach(g =>
      ((g && g.resources) || []).forEach(r => {
        if (!r) return
        // Resolve vendor from library for redaction purposes
        const resolved = resolvePrintVendor({
          vendorId: r.vendorId,
          name: r.vendorName,
          phone: r.vendorPhone,
          email: r.vendorEmail,
          website: r.vendorWebsite,
          address: r.vendorAddress
        })
        const vn = (resolved.name || "").trim()
        if (vn.length >= 2 && vn !== "(vendor removed)") raw.push({ name: vn, tag: vendorTagFor(vn) })
        ;[resolved.address, resolved.phone, resolved.email, resolved.website].forEach(v => {
          const s = (v == null ? "" : String(v)).trim()
          if (s.length >= 2) raw.push({ name: s, tag: "[Vendor]" })
        })
      })
    )

    const seen = new Set()
    return raw
      .filter(e => e.name && e.name.length >= 2)
      .filter(e => { const k = e.name.toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true })
      .sort((a, b) => b.name.length - a.name.length)
      .map(e => ({
        // Plain global matcher; word boundaries are enforced in JS
        // (_redactTextNodes) — a \p{L} lookbehind/lookahead over-matched in
        // V8 (matched "Sam" inside "Samuel"), which would leak/garble text.
        re: new RegExp(escapeRegExp(e.name), "giu"),
        tag: e.tag
      }))
  }

  _redactTextNodes(root, map) {
    // A name matches only when BOTH neighbouring characters are non-word
    // (not a letter/number) — unicode-aware, so accented names still bound
    // correctly and a name is never replaced inside a longer word.
    const isWord = (ch) => ch != null && /[\p{L}\p{N}]/u.test(ch)
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null)
    const nodes = []
    let node
    while ((node = walker.nextNode())) nodes.push(node)
    nodes.forEach(n => {
      let text = n.nodeValue
      if (!text || !text.trim()) return
      let changed = false
      for (const { re, tag } of map) {
        re.lastIndex = 0
        const next = text.replace(re, (m, off, whole) => {
          const before = whole[off - 1], after = whole[off + m.length]
          return (!isWord(before) && !isWord(after)) ? tag : m
        })
        if (next !== text) { text = next; changed = true }
      }
      if (changed) n.nodeValue = text
    })
  }

  // The side strip (page-margin-left-content) rotates -90deg around its
  // bottom-right corner; with a long URL + email + phone the unrotated
  // row gets wider than the fixed --strip-w (2in), so after rotation it
  // extends above the page and bleeds into the previous page. Measure
  // each strip's actual pre-rotation width and set --strip-w on its
  // page so the bottom calc lands inside the page bounds.
  _sizeSideStrips() {
    const strips = this.element.querySelectorAll(".page-margin-left-content")
    strips.forEach(strip => {
      strip.style.transform = "none"
      const w = strip.scrollWidth
      strip.style.transform = ""
      // Use the strip's actual pre-rotation width as --strip-w.
      // The CSS centers via bottom: calc(50% + var(--strip-w)/2),
      // which only lands the rotated strip with its midpoint at
      // 50% when --strip-w matches the real content width — the
      // previous rounding-up to 0.1in left a few px of top/bottom
      // asymmetry that read as visibly off-center on every page.
      strip.style.setProperty("--strip-w", w + "px")
    })
  }

  // Splits overflowing section pages so no .print-card exceeds the
  // page's content area. Simple chunker: walks each section page's
  // .print-card, measures cumulative child height, and when it would
  // cross the budget starts a new .print-page with a "(cont.)" badge.
  _autoPaginate() {
    const pageHeight = parseFloat(getComputedStyle(this.element).getPropertyValue("--pp-page-h") || "0")
    const padding    = parseFloat(getComputedStyle(this.element).getPropertyValue("--pp-pad")    || "0")
    // CSS values are in inches; convert to px at 96 dpi.
    const inToPx = 96
    const PAGE_PX  = (pageHeight || 11) * inToPx
    const PAD_PX   = (padding    || 0.75) * inToPx
    const HEADER_PX = 28   // page-header bar
    const FOOTER_PX = 40   // confidential footer reserve
    const BUDGET    = PAGE_PX - 2 * PAD_PX - HEADER_PX - FOOTER_PX

    const pageBreaks = this.plan?.meta?.pageBreaks || {}

    // True when this card-region child has an active "move to next page"
    // chip on it (looked up via the chip button's data-pb-key). The chip
    // lives inside the child wrapper (.pp-tile, .pp-subcard, .pp-goal-card,
    // .dil-h, …) so querySelector finds it from the child root.
    const hasExplicitBreak = (child) => {
      const btn = child.querySelector?.("[data-pb-key]")
      if (!btn) return false
      const k = btn.dataset.pbKey
      return !!(k && pageBreaks[k])
    }

    // Some sections render their chip-bearing sub-units inside a single
    // wrapper (about → .pp-tiles, wonderfulThings → .pp-grid2, etc.) so
    // the print-card has only ONE non-H2 direct child. To split those we
    // walk one level down and use the wrapper's children as the chunk
    // candidates, then reconstruct the wrapper around each chunk so CSS
    // grid / flex layout survives. Anything else (Goals, Day-in-Life,
    // Barriers list, Unmet list) already exposes its sub-units as direct
    // children of the card — those keep using the flat path.
    const pickSplittables = (card, h2) => {
      // Skip everything inside the card that isn't actual splittable
      // content. Three flavours of chrome show up here:
      //   - the section H2 _renderSectionPage emits next to the body
      //   - the section-level "→ next page" chip button next to the h2
      //   - the .pp-chip-legend pill the renderers emit when any item
      //     in the section uses the (0)/(1) Independent/Needs-Help
      //     markers (anyHasChipMarkers branch in about / wonderfulThings
      //     / notSoFavorites / dayInLife). It floats right with a
      //     -32px top margin, but it's still a sibling of the body
      //     wrapper, so without filtering it counts as a second direct
      //     child and the wrapper drill never happens.
      const isChrome = (c) =>
        c === h2 ||
        c.matches?.("button.dil-pb-chip") ||
        c.matches?.(".pp-chip-legend")
      const directChildren = [...card.children].filter(c => !isChrome(c))
      // Flat layout: the card has its sub-units inline.
      if (directChildren.length >= 2) return { children: directChildren, wrapper: null }
      // Nested layout: the lone child is a wrapper; walk inside it.
      const wrapper = directChildren[0]
      if (!wrapper) return { children: [], wrapper: null }
      const inner = [...wrapper.children]
      if (inner.length < 2) return { children: [], wrapper: null }
      // Clone the wrapper without its children so each chunk can rebuild
      // it (preserves classes for CSS grid / flex layout).
      const shell = wrapper.cloneNode(false)
      return { children: inner, wrapper: shell }
    }

    const sectionPages = [...this.pagesTarget.querySelectorAll('.print-page:not([data-page="cover"]):not([data-page="toc"])')]
    sectionPages.forEach(page => {
      const card = page.querySelector(".print-card")
      if (!card) return

      const h2 = card.querySelector("h2")
      const h2Height = h2 ? h2.getBoundingClientRect().height + 14 /* margin */ : 0
      const { children, wrapper } = pickSplittables(card, h2)
      if (children.length < 2) return  // nothing to split

      // Pagination is MANUAL now — only the chip-driven "→ next page"
      // forces a split. Overflow is intentionally left visible
      // (.print-page is height-locked + overflow: visible in CSS) so
      // the user can SEE which sub-section spilled and click its chip
      // to push it onto the next sheet. No more automatic chunking by
      // page-height budget.
      const anyExplicit = children.some(hasExplicitBreak)
      if (!anyExplicit) return

      const sideStrip    = page.querySelector(".page-margin-left")?.outerHTML || ""
      const confidential = page.querySelector(".page-confidential")?.outerHTML || ""
      const navRail      = page.querySelector(".page-nav-right")?.outerHTML || ""
      const bgIcons      = page.querySelector(".page-bg-icons")?.outerHTML || ""
      const headerEl     = page.querySelector(".page-header")
      const pageKey      = page.dataset.page
      // Top-level Independent / Needs Help legend (Strengths / Not-So-
      // Favorites / etc. use .pp-chip-legend at the section body level).
      // Captured up front so every continuation page carries a clone
      // right after its h2. dayInLife uses per-sub-section legends
      // baked into .pp-dil-subhead-row, so it doesn't need this.
      const legendEl = card.querySelector(":scope > .pp-chip-legend")

      const pages = [[]]
      let used = h2Height
      const budget = BUDGET - h2Height
      // Put c at the START of a fresh page chunk. If the current last
      // chunk is still empty (first element + immediate overflow /
      // oversize / forceBreak), re-use that slot instead of pushing
      // a new one — otherwise the rebuild loop later renders that
      // empty chunk as a page with just an H2 and nothing else, which
      // is where the "blank pages" came from.
      const startNewPage = (c) => {
        const last = pages[pages.length - 1]
        if (last.length === 0) last.push(c)
        else                   pages.push([c])
      }
      // Manual chunker: only the chip-driven force break opens a new
      // page. Everything else stays on the current chunk regardless of
      // measured height — the page CSS lets it overflow visibly, and
      // the user decides what to push next via the .dil-pb-chip arrow.
      children.forEach((c, idx) => {
        if (idx > 0 && hasExplicitBreak(c)) {
          // If the break is on the FIRST block of a sub-section — its previous
          // sibling is the sub-section header (Day-in-Life's "5.2 Weekends"
          // row) — carry that header down too so the WHOLE sub-section starts
          // fresh on the new page (header + this block + the ones after it).
          // Any other block breaks at itself: per-block "move this + the rest".
          const prev = children[idx - 1]
          const last = pages[pages.length - 1]
          if (prev && prev.matches?.(".pp-dil-subhead-row") && last[last.length - 1] === prev) {
            last.pop()
            startNewPage(prev)
            pages[pages.length - 1].push(c)
          } else {
            startNewPage(c)
          }
        } else {
          pages[pages.length - 1].push(c)
        }
      })
      // `used` / `budget` are no longer consulted, but kept declared
      // above so future tweaks (e.g. an opt-in "warn if overflowing"
      // toggle) can re-introduce the budget without restructuring.
      void used; void budget

      // Safety net: drop any chunk that still ended up empty so the
      // rebuild loop below doesn't emit an H2-only blank page.
      for (let i = pages.length - 1; i >= 0; i--) {
        if (pages[i].length === 0) pages.splice(i, 1)
      }

      if (pages.length <= 1) return  // overflow was within tolerance

      // Preserve the original page's id="section-<key>" attribute on the
      // FIRST chunk so anchor jumps (TOC + nav rail) still resolve after
      // a section gets paginated. Without this the original page is
      // removed and the new pages have no id, breaking every cross-link.
      const originalId = page.id || ""

      // Build new .print-page nodes; the first inherits the original
      // page header + id, subsequent ones get a derived id so they
      // can still be addressed individually (#section-<key>-cont1 etc.).
      // We don't try to fix the page number here — _renumberPageHeaders
      // does a clean pass over the post-split list once every page is
      // in the DOM, so the "Page n / N" counts the final page set.
      const newPages = pages.map((chunk, i) => {
        const wrap = document.createElement("section")
        wrap.className = "print-page"
        wrap.dataset.page = pageKey + (i === 0 ? "" : `-cont${i}`)
        if (originalId) {
          wrap.id = i === 0 ? originalId : `${originalId}-cont${i}`
        }
        const hdr = headerEl ? headerEl.cloneNode(true) : null
        if (hdr) wrap.appendChild(hdr)
        const newCard = document.createElement("div")
        newCard.className = "print-card"
        if (h2) {
          const h2Clone = h2.cloneNode(true)
          if (i > 0) {
            h2Clone.insertAdjacentHTML("beforeend", ' <span style="font-size:0.5em;font-weight:600;opacity:0.6;letter-spacing:0;">(cont.)</span>')
          }
          newCard.appendChild(h2Clone)
        }
        // Re-emit the section's legend on every continuation page so
        // families don't lose the ✓ Independent / 🤚 Needs Help key
        // when content spills onto a new sheet.
        if (legendEl) newCard.appendChild(legendEl.cloneNode(true))
        // When the section's chunks came from inside a wrapper (.pp-tiles,
        // .pp-grid2, …) we have to recreate that wrapper around the chunk
        // so the wrapper's CSS grid / flex layout still kicks in. Sections
        // that exposed sub-units as direct children of the card don't
        // need this (wrapper is null) and the chunk is dropped straight in.
        if (wrapper) {
          const wrapShell = wrapper.cloneNode(false)
          chunk.forEach(c => wrapShell.appendChild(c.cloneNode(true)))
          newCard.appendChild(wrapShell)
        } else {
          chunk.forEach(c => newCard.appendChild(c.cloneNode(true)))
        }
        // Continuation pages also need the bg-icons (above content),
        // side strip, confidential, AND the right-rail nav so every
        // page in the printed plan exposes the same chrome the editor
        // emits on the first page.
        if (bgIcons) wrap.insertAdjacentHTML("afterbegin", bgIcons)
        wrap.appendChild(newCard)
        wrap.insertAdjacentHTML("beforeend", sideStrip)
        wrap.insertAdjacentHTML("beforeend", confidential)
        if (navRail) wrap.insertAdjacentHTML("beforeend", navRail)
        return wrap
      })
      // Replace the original page in DOM with the split set.
      const parent = page.parentNode
      newPages.forEach(np => parent.insertBefore(np, page))
      parent.removeChild(page)
    })
  }

  // Walks every .print-page that landed in the DOM after auto-pagination
  // and rewrites its "Page n / N" header + the TOC's page numbers.
  // Without this, continuation pages keep the cloned page-num from
  // their source page (so two pages in a row both say "Page 5"), the
  // total stays at the pre-split count, and the TOC's "Goals → 7"
  // points at whichever page goals USED to start on before pagination
  // pushed it down.
  _renumberPageHeaders() {
    const pages = [...this.pagesTarget.querySelectorAll(".print-page")]
    const total = pages.length
    this.totalPages = total

    // Map section-key → first page index (1-based) so we can rewrite
    // the TOC. A section split across N pages claims the first slot;
    // continuation pages don't get their own TOC entries.
    const sectionPageNum = {}
    pages.forEach((page, i) => {
      const cur = page.querySelector(".page-header-pagenum .pp-pn-cur")
      const tot = page.querySelector(".page-header-pagenum .pp-pn-total")
      if (cur) cur.textContent = String(i + 1)
      if (tot) tot.textContent = String(total)

      // Invert sibling stacking so an OVERFLOWING earlier page paints
      // on top of its later siblings (default flow has later pages
      // covering anything that bleeds past the 1056px page box). With
      // this, the user SEES the bleed and can move it to the next
      // page via the .dil-pb-chip "→ next page" pagination arrow.
      page.style.zIndex = String(total - i)

      // First-occurrence wins; continuation pages have data-page like
      // "goals-cont1" so we trim everything from the first hyphen on.
      const raw = page.dataset.page || ""
      const root = raw.split("-cont")[0]
      if (root && !(root in sectionPageNum)) sectionPageNum[root] = i + 1
    })

    // Patch TOC entries. The TOC's row layout is
    //   <a data-section-key="goals" …> … <span class="page">7</span></a>
    // (see _renderToc) — just find each row and update its .page span.
    const tocRows = this.pagesTarget.querySelectorAll('[data-page="toc"] .print-toc-row')
    tocRows.forEach(row => {
      const key = row.dataset.sectionKey
      const span = row.querySelector(".page")
      if (key && span && sectionPageNum[key]) span.textContent = String(sectionPageNum[key])
    })
  }

  // Every pb-chip card has `overflow: hidden` (rounded inner
  // backgrounds depend on it), which clips the chip pinned above the
  // top edge. Wrap each card in a `.pp-pb-wrap` (overflow: visible)
  // and lift the chip into the wrapper so it floats freely above the
  // card border. Idempotent — skips cards already wrapped.
  _floatPbChips() {
    this.pagesTarget.querySelectorAll(".dil-pb-chip").forEach(chip => {
      const card = chip.parentElement
      if (!card || !card.classList.contains("has-pb-chip")) return
      const parent = card.parentElement
      if (!parent || parent.classList.contains("pp-pb-wrap")) return
      const wrap = document.createElement("div")
      wrap.className = "pp-pb-wrap"
      parent.insertBefore(wrap, card)
      wrap.appendChild(card)
      wrap.appendChild(chip)
    })
  }

  // Re-parent every .pp-edit-btn into its card's title element so the
  // pencil sits right after the title text (e.g. "1.1 Who Lives With
  // Me ✎") instead of being absolutely pinned at the card's
  // top-right corner. Card structures vary across renderers, so we
  // probe a list of known title selectors and pick the first hit.
  _inlineEditButtons() {
    // Priority list: walked in order; the first selector that matches
    // INSIDE the card wins. Order matters — e.g., `.pp-goal-heading`
    // (the subtitle "xxx") must come BEFORE `.pp-goal-num` (the "13.1"
    // number) so the pencil lands at the end of the heading text and
    // only falls back to the number when no heading is set.
    const TITLE_PRIORITY = [
      ".pp-tile-label",         // about tiles ("1.1 Who Lives With Me")
      ".pp-cos-head",           // circle of support
      ".pp-bar-head",           // barriers
      ".pp-goal-heading",       // goal subtitle (preferred)
      ".pp-goal-num",           // goal number (fallback when no heading)
      ".pp-cs-head",            // current services
      ".pp-dil-head",           // day in life subsection header
      ".pp-work-head",          // working
      ".pp-comm-card-head",     // communication
      ".pp-subcard-head",       // generic subcard head
      ".pp-subcard > h3",       // fallback: first h3 inside a subcard
      ".print-section-h2"       // last-resort: the section h2 itself
                                // (Service Calendar pages have no sub-header)
    ]
    this.pagesTarget.querySelectorAll(".pp-edit-btn").forEach(btn => {
      const card = btn.parentElement
      if (!card || card.classList.contains("pp-edit-host")) return
      let title = null
      for (const sel of TITLE_PRIORITY) {
        title = card.querySelector(sel)
        if (title) break
      }
      if (!title) return
      title.classList.add("pp-edit-host")
      title.appendChild(btn)
    })
  }

  // ---- Page chrome (side strip + confidential footer) ----
  // Mirrors the page-margin-left + page-confidential blocks the iframe
  // editor's generatePageFooter (pcp_builder.html#6869) emits on every
  // page. Cover deliberately skips the side strip (the iframe does too).
  _renderPageChrome() {
    const o = this.org || {}
    const email   = o.email   || "info@aaviond.com"
    const phone   = o.phone   || ""
    const website = o.website || ""
    const safePhoneHref = phone ? `tel:${String(phone).replace(/[^+0-9]/g, "")}` : ""
    const webHref       = website ? (/^https?:\/\//i.test(website) ? website : `https://${website}`) : ""
    const ico = {
      phone: `<svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z"/></svg>`,
      email: `<svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-10 5L2 7"/></svg>`,
      web:   `<svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>`
    }
    const linkStyle = `style="color:inherit;text-decoration:none;"`
    const span = (icon, content) =>
      `<span style="display:inline-flex;align-items:center;gap:3px;">${icon}${content}</span>`
    const logoUrl = (o.logoUrl) || "/logo.png"
    const stripInner = [
      phone   ? span(ico.phone, `<a href="${esc(safePhoneHref)}" ${linkStyle}>${esc(phone)}</a>`)   : "",
      span(ico.email, `<a href="mailto:${esc(email)}" ${linkStyle}>${esc(email)}</a>`),
      website ? span(ico.web,   `<a href="${esc(webHref)}" ${linkStyle}>${esc(website)}</a>`)        : ""
    ].filter(Boolean).join("\n        ")
    const sideStrip = `
      <div class="page-margin-left">
        <div class="page-margin-left-content">
          <img src="${esc(logoUrl)}" alt="">
          ${stripInner}
        </div>
      </div>`
    // Mirror pcp_builder.html#6880 — confidential notice is two
    // paragraphs: privacy + proprietary-software boilerplate.
    const defaultNotice = "Confidential: This Person-Centered Plan contains sensitive information and is intended solely for service planning and coordination. Disclosure is limited to authorized parties and as required by law.\nThis document is generated by AavionD's proprietary Person-Centered Plan Builder software; its layout, format, and structure are the proprietary property of AavionD LLC. Unauthorized reproduction, redistribution, or reverse engineering of this software or its output format is prohibited."
    const notice = o.confidentialNotice || defaultNotice
    const confidential = `<div class="page-confidential">${esc(notice)}</div>`
    return { sideStrip, confidential }
  }

  // ---- Cover ----
  // Mirrors pcp_builder.html#6800 (the iframe editor's print Cover):
  // gradient hero with title + two pills, a white participant card
  // below with photo, AGE: NN badge, address pill, FACILITATOR block,
  // and a "Last Updated" timestamp. Background music-note motif is
  // theme-specific (this implementation uses the celebration motif's
  // music + party icons).
  // ---- Shared page-header bar ----
  // Mirrors generatePageHeader from pcp_builder.html — every page gets
  // "Person Centered Plan - <name>", a REV badge, and PAGE n/N.
  _renderPageHeader(pageNum) {
    const rev = (this.plan?.meta?.revision || "1.0").toString().split(".")[0]
    const total = this.totalPages || pageNum
    return `
      <div class="page-header">
        <span class="page-header-title">Person Centered Plan - ${esc(this._participantName())}<span class="page-rev-pill"><i data-lucide="git-branch"></i>Rev ${esc(rev)}</span></span>
        <span class="page-header-pagenum"><i data-lucide="file"></i>Page <span class="pp-pn-cur">${pageNum}</span><span class="pp-pn-slash">/</span><span class="pp-pn-total">${total}</span></span>
      </div>`
  }

  // ---- Theme-driven background icons (decorative .smart-icon dots
  //      sprinkled around each page). Mirrors pcp_builder.html#6807. ----
  _renderBgIcons(pageIdx) {
    const icons = this.theme.bgIcons || []
    if (!icons.length) return ""
    // Deterministic pseudo-random per page so the layout is stable.
    let seed = (pageIdx + 1) * 1234
    const rnd = () => {
      const x = Math.sin(seed++) * 10000
      return x - Math.floor(x)
    }
    let html = ""
    for (let i = 0; i < 8; i++) {
      const icon = icons[i % icons.length]
      const top  = rnd() * 90
      // Cap `left` at 75% so decorative dots never land in the right
      // ~25% of the page where the .page-nav-right rail lives. At
      // opacity 0.08 the dots already read as faint, but if one
      // landed right next to a rail item it visually masquerades as
      // a stranded rail icon — that's the "gap + stranded icons"
      // pattern the user reported in the downloaded PDF.
      const left = rnd() * 75
      const size = 20 + rnd() * 40
      html += `<i data-lucide="${icon}" class="smart-icon" style="top:${top.toFixed(1)}%;left:${left.toFixed(1)}%;width:${size.toFixed(0)}px;height:${size.toFixed(0)}px;"></i>`
    }
    return `<div class="page-bg-icons" aria-hidden="true">${html}</div>`
  }

  // True when a section actually has a rendered page in the preview.
  // The render() pass uses these same rules (line ~822) so the nav rail
  // mirrors the actual pages — no rail icon points at a missing anchor.
  _isSectionInPreview(key) {
    // "brochures" (the vendor-brochure appendix) is disabled for now per
    // request — kept in SECTIONS but never rendered/listed.
    if (key === "client" || key === "facilitator" || key === "review" || key === "brochures") return false
    if (!SECTIONS[key]) return false
    if (!isSectionIncluded(this.plan, key)) return false
    if (key === "resourceCalendar" && !this._rcVisible()) return false
    return true
  }

  // ---- Right-side nav rail (vertical icon column) ----
  // Mirrors generatePageNav from pcp_builder.html#6886. Every page gets
  // a vertical column on the right edge with icons for Cover, TOC, and
  // every enabled section. Active section is highlighted. Sections that
  // were filtered out of the preview (toggled off, empty calendar) are
  // excluded so every icon resolves to a real #section-<key> anchor.
  _renderNavRail(activeKey) {
    // Fall back to the full section list when the plan has no custom order,
    // matching the page-queue + TOC. _isSectionInPreview then drops only
    // client/facilitator/review/brochures + editor-disabled sections —
    // empty sections still show in the rail.
    const order = pcpSectionOrder()
    const items = [
      { key: "cover", icon: "book-marked", label: "Front Page" },
      { key: "toc",   icon: "list",         label: "Table of Contents" }
    ]
    order.forEach(k => {
      if (!this._isSectionInPreview(k)) return
      const cfg = SECTIONS[k]
      items.push({ key: k, icon: cfg.icon, label: cfg.label })
    })
    const html = items.map(s => {
      const active = s.key === activeKey ? " data-nav-active" : ""
      // data-action routes the click through navJump() below so:
      //   - we use scrollIntoView (smooth + scroll-margin aware) instead
      //     of the browser's hard jump that hides the destination behind
      //     the sticky preview toolbar, and
      //   - a click on a section that wasn't rendered (excluded /
      //     filtered) gracefully bails instead of leaving the user on
      //     a blank stretch of page.
      return `<a href="#section-${esc(s.key)}"
                 title="${esc(s.label)}"
                 data-nav-key="${esc(s.key)}"
                 data-action="print-preview#navJump"
                 ${active}>${sectionIconHtml(s.icon)}</a>`
    }).join("")
    return `<div class="page-nav-right">${html}</div>`
  }

  // Nav rail click handler. Smooth-scrolls to the section page; the
  // .print-page CSS rule sets scroll-margin-top so the destination
  // isn't hidden behind the sticky preview toolbar. The rail itself
  // only emits icons for sections that have a rendered page (see
  // _renderNavRail) so there's no "anchor missing" branch to handle.
  navJump(event) {
    const key = event.currentTarget?.dataset?.navKey
    if (!key) return
    const target = document.getElementById(`section-${key}`)
    if (!target) return
    event.preventDefault()
    target.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  // Branches on the workspace's brand_display preference (Settings →
  // Editor brand display). All three modes still show the facilitator
  // name + agency + contact (this is a required field on every plan);
  // only the surrounding chrome differs:
  //   card → full business card (logo + name + agency + contact + tagline)
  //   logo → compact card (logo + name + agency + contact, no tagline)
  //   text → plain text block (no logo, no card chrome, no tagline)
  _renderCoverFacilitator(org, fnm, fag, fct) {
    const style = (org && org.brandDisplay) || "logo"
    const name    = esc(fnm || "N/A")
    const agency  = fag ? `<div class="print-cover-fac-agency">${esc(fag)}</div>` : ""
    const contact = fct ? `<div class="print-cover-fac-contact">${esc(fct)}</div>` : ""

    if (style === "text") {
      return `
        <div class="print-cover-facilitator is-text">
          <div class="print-cover-facilitator-label">Facilitator</div>
          <div class="print-cover-facilitator-body">
            <div class="print-cover-fac-name">${name}</div>
            ${agency}
            ${contact}
          </div>
        </div>`
    }

    const logo = org && org.logoUrl
      ? `<img src="${esc(org.logoUrl)}" alt="${esc(org.companyName || "Logo")}" onerror="this.style.display='none'">`
      : ""
    // Tagline is workspace-configured (Settings → Tagline). Only the
    // "card" variant prints it — "logo" and "text" stay clean. When
    // the workspace leaves it blank the line is omitted entirely.
    const taglineText = (org && org.tagline && org.tagline.trim()) || ""
    const tagline = (style === "card" && taglineText)
      ? `<div class="print-cover-facilitator-tagline">“${esc(taglineText)}”</div>`
      : ""
    const modClass = style === "card" ? "" : " is-logo"

    return `
      <div class="print-cover-facilitator${modClass}">
        <div class="print-cover-facilitator-head">
          <span class="print-cover-facilitator-label">Facilitator</span>
          <span class="print-cover-facilitator-brand">${logo}</span>
        </div>
        <div class="print-cover-facilitator-body">
          <div class="print-cover-fac-name">${name}</div>
          ${agency}
          ${contact}
        </div>
        ${tagline}
      </div>`
  }

  _renderCover(pageNum) {
    const c = this.plan.client || {}
    const fac = this.plan.facilitator || {}
    const org = this.org || {}
    const fullName = (c.name && c.name.trim()) ||
                     [c.firstName, c.lastName].filter(Boolean).join(" ") ||
                     "Participant"
    const age = getAge(c.dob)
    const photoBlock = c.photo
      ? `<img class="print-cover-participant-photo" src="${esc(c.photo)}" alt="">`
      : `<div class="print-cover-participant-empty"><i data-lucide="user"></i></div>`
    // Facilitator falls back to the workspace's branded facilitator
    // (Settings) if the plan didn't override.
    const fnm = (fac.name    && fac.name.trim())    || (org.facilitator && org.facilitator.name)    || ""
    const fag = (fac.agency  && fac.agency.trim())  || (org.facilitator && org.facilitator.agency)  || ""
    const fct = (fac.contact && fac.contact.trim()) || (org.facilitator && org.facilitator.contact) || ""
    const lastMod = this.plan?.meta?.lastModified
    const lastDate = lastMod ? new Date(lastMod).toLocaleDateString() : new Date().toLocaleDateString()
    const chrome = this._renderPageChrome()
    return `
      <section class="print-page is-cover" data-page="cover" id="section-cover">
        ${this._renderPageHeader(pageNum)}
        <div class="print-cover-bg-icons" aria-hidden="true">
          ${["music","sparkles","gift","heart","star","cake","party-popper","smile"].flatMap(ic => Array(2).fill(ic)).map((ic, i) => {
            // Cap `left` at 70% so cover decorations never bleed into
            // the right-edge column where the .page-nav-right rail
            // lives. At opacity 0.18 a stray icon landing there reads
            // as a stranded rail item — exactly the visual the user
            // reported in the downloaded PDF.
            const left = ((i * 73) % 68) + 4, top = 22 + ((i * 41) % 64), size = 14 + (i % 3) * 4
            return `<i data-lucide="${ic}" style="position:absolute;left:${left}%;top:${top}%;width:${size}px;height:${size}px;opacity:0.18;"></i>`
          }).join("")}
        </div>
        <div class="print-cover-inner">
          <div class="print-cover-hero">
            <h1 class="print-cover-title">Person Centered Plan</h1>
            <div class="print-cover-pills">
              <span class="print-cover-pill print-cover-pill-self"><i data-lucide="sparkles"></i> Self-Determination Program</span>
              <div class="print-cover-pill-row">
                <span class="print-cover-pill print-cover-pill-doc"><i data-lucide="mouse-pointer-click"></i> Interactive Document</span>
                <span class="print-cover-pill print-cover-pill-updated"><i data-lucide="calendar"></i> Last Updated: ${esc(lastDate)}</span>
              </div>
            </div>
          </div>
          <div class="print-cover-participant">
            ${photoBlock}
            <div class="print-cover-card-body">
              <div class="print-cover-meta-label"><i data-lucide="user"></i> Participant</div>
              <div class="print-cover-participant-name">${esc(fullName)}</div>
              <div class="print-cover-meta-pills">
                ${age != null ? `<span class="print-cover-age-pill"><i data-lucide="cake"></i> AGE: ${age}</span>` : ""}
                ${(c.uci || "").trim() ? `<span class="print-cover-uci-pill"><i data-lucide="hash"></i> UCI #: <span class="print-cover-uci-value">${esc((c.uci || "").trim())}</span></span>` : ""}
                ${(() => {
                  // Privacy: NEVER render the street address on the PDF.
                  // The client.address field is wizard-only — used for
                  // goal-resource Mileage Lookup (Google Maps origin).
                  // The cover-page location chip is composed strictly
                  // from city, state, and zip — never the street.
                  const city  = (c.city  || "").trim();
                  const state = (c.state || "").trim();
                  const zip   = (c.zip   || "").trim();
                  if (!city && !zip) return "";
                  let label;
                  if (city && zip) {
                    label = (state ? city + ", " + state : city) + " - " + zip;
                  } else if (city) {
                    label = state ? city + ", " + state : city;
                  } else {
                    label = zip;
                  }
                  return `<span class="print-cover-loc-pill"><i data-lucide="map-pin"></i> ${esc(label)}</span>`;
                })()}
              </div>
              ${this._renderCoverFacilitator(org, fnm, fag, fct)}
            </div>
          </div>
        </div>
        ${chrome.sideStrip}
        ${chrome.confidential}
        ${this._renderNavRail("cover")}
      </section>`
  }

  // ---- Life Photo (closing page) ----
  // Port of pcp_builder.html#10145 — closes the document with a
  // single tilted photo + caption instead of the old Review/Sign
  // page. Only emitted when the plan carries a lifePhoto or a
  // lifePhotoCaption; otherwise the document just ends after the
  // last content section.
  _renderLifePhoto(pageNum) {
    const c = this.plan.client || {}
    const chrome = this._renderPageChrome()
    // Fallback graphic — pcp_builder.html#10147 ships a tiny inline
    // SVG placeholder when the user hasn't uploaded a photo yet.
    const placeholderSvg =
      "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiB2aWV3Qm94PSIwIDAgMjAwIDIwMCI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNmM2Y0ZjYiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1mYW1pbHk9ImFyaWFsIiBmb250LXNpemU9IjIwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjOWNhM2I4Ij5MaWZlIFBob3RvPC90ZXh0Pjwvc3ZnPg=="
    const photoSrc = c.lifePhoto || placeholderSvg
    const caption  = (c.lifePhotoCaption && c.lifePhotoCaption.trim())
      || "A photo that represents my life"
    return `
      <section class="print-page is-lifephoto" data-page="lifePhoto" id="section-lifePhoto">
        ${this._renderBgIcons(pageNum)}
        ${this._renderPageHeader(pageNum)}
        <div class="print-lifephoto-inner">
          <div class="print-lifephoto-frame">
            <img class="print-lifephoto-img" src="${esc(photoSrc)}" alt="">
          </div>
          <h3 class="print-lifephoto-caption">${esc(caption)}</h3>
        </div>
        ${chrome.sideStrip}
        ${chrome.confidential}
        ${this._renderNavRail("lifePhoto")}
      </section>`
  }

  // Optional closing page — a review checklist, an acknowledgment that the
  // PCP was reviewed, a Regional Center consent/release statement (covering
  // this plan and any revisions), and parent / guardian signature lines.
  // Consolidated "Open Questions" page — every unresolved (TBD / ??) item,
  // grouped by the section it belongs to and linked there, so reviewers get one
  // list and know exactly where each belongs. Added when the "Open Questions"
  // toolbar toggle (plan.meta.includeOpenQuestions) is on. The links resolve to
  // section-<key> anchors (on-screen via jumpToRef; internal links in the PDF).
  _renderOpenQuestions(pageNum) {
    const chrome = this._renderPageChrome()
    const items = findOpenQuestions(this.plan)
    const byId = {}
    items.forEach(it => { (byId[it.sectionId || ""] ||= []).push(it) })
    const labelFor = (sid) => (SECTIONS[sid] && SECTIONS[sid].label) || (sid || "General")
    let body
    if (!items.length) {
      body = `<p class="pp-oq-empty">No open questions — every field has a value.</p>`
    } else {
      body = Object.keys(byId).map(sid => {
        const label = labelFor(sid)
        const jump = sid
          ? `<a class="pp-oq-jump" href="#section-${esc(sid)}" data-action="click->print-preview#jumpToRef" data-target-id="section-${esc(sid)}">Go to ${esc(label)} &rarr;</a>`
          : ""
        const rows = byId[sid].map(it => `<li class="pp-oq-item">${esc(it.question || "Open item")}</li>`).join("")
        return `
          <div class="pp-oq-group">
            <div class="pp-oq-group-head"><span class="pp-oq-section">${esc(label)}</span>${jump}</div>
            <ul class="pp-oq-list">${rows}</ul>
          </div>`
      }).join("")
    }
    const card = `
      <div class="pp-oq-card">
        <h2 class="pp-oq-title">Open Questions</h2>
        <p class="pp-oq-intro">Items still to confirm on this plan${items.length ? ", grouped by the section they belong to — follow a link to jump straight there" : ""}.</p>
        ${body}
      </div>`
    return `
      <section class="print-page" data-page="openQuestions" id="section-openQuestions">
        ${this._renderBgIcons(pageNum)}
        ${this._renderPageHeader(pageNum)}
        ${card}
        ${chrome.sideStrip}
        ${chrome.confidential}
        ${this._renderNavRail("openQuestions")}
      </section>`
  }

  // Appended only when plan.meta.releaseForm is on (the "Review & Sign Page"
  // toolbar toggle). Uses [FirstName]/[LastName] placeholders so it expands
  // (and redacts) like every other page.
  _renderReleaseForm(pageNum) {
    const chrome = this._renderPageChrome()
    const box = `<span class="pp-check"></span>`
    const checklist = [
      "I have reviewed this entire Person-Centered Plan.",
      "The goals and desired outcomes reflect the individual's own priorities.",
      "The strengths, preferences, and support needs are described accurately.",
      "The services, providers, and supports listed are acceptable.",
      "I understand how to request a change or a review of this plan.",
      "I received a copy of this plan."
    ].map(t => `<li class="pp-release-check">${box}<span>${t}</span></li>`).join("")

    const signer = (role) => `
      <div class="pp-release-signer">
        <div class="pp-release-role">${esc(role)}</div>
        <div class="pp-review-sign">
          <div><div class="pp-review-label">Parent / Guardian Name</div><div class="pp-review-line"></div></div>
          <div><div class="pp-review-label">Relationship</div><div class="pp-review-line"></div></div>
          <div><div class="pp-review-label">Signature</div><div class="pp-review-line pp-review-line-tall"></div></div>
          <div><div class="pp-review-label">Date</div><div class="pp-review-line"></div></div>
        </div>
      </div>`

    const card = `
      <div class="print-card">
        <h2 class="print-section-h2"><span style="color:#6366f1;display:inline-flex;align-items:center;"><i data-lucide="clipboard-check"></i></span> Plan Review, Acknowledgment &amp; Release</h2>
        <p class="pp-release-intro">This page confirms that the Person-Centered Plan for <strong>[FirstName] [LastName]</strong> has been reviewed with the parents / guardians, and authorizes its use by the Regional Center and the providers coordinating that support.</p>

        <h3 class="pp-release-h3">Review Checklist</h3>
        <ul class="pp-release-list">${checklist}</ul>

        <h3 class="pp-release-h3">Acknowledgment of Review</h3>
        <p class="pp-release-statement">By signing below, I acknowledge that I participated in developing and/or reviewing this Person-Centered Plan, that it accurately reflects the individual's goals and support needs, and that I had the opportunity to ask questions and request changes.</p>

        <h3 class="pp-release-h3">Consent &amp; Release of Information</h3>
        <p class="pp-release-statement">I authorize the sharing of this Person-Centered Plan, and the information it contains, with the <strong>Regional Center</strong> and the service providers it coordinates in supporting [FirstName]. I understand this information is confidential. This authorization applies to this plan <strong>and any subsequent revisions</strong>, and remains in effect for <span class="pp-release-fill"></span> months from the date signed unless revoked earlier in writing.</p>

        <h3 class="pp-release-h3">Parent / Guardian Signature</h3>
        ${signer("Parent / Guardian")}
      </div>`

    return `
      <section class="print-page" data-page="releaseForm" id="section-releaseForm">
        ${this._renderBgIcons(pageNum)}
        ${this._renderPageHeader(pageNum)}
        ${card}
        ${chrome.sideStrip}
        ${chrome.confidential}
        ${this._renderNavRail("releaseForm")}
      </section>`
  }

  // ---- TOC ----
  _renderToc(pageNum) {
    // Fall back to the full section list (same as the page-queue builder)
    // when the plan has no custom order — otherwise the TOC renders EMPTY
    // on a default plan even though every section page is present.
    const order = pcpSectionOrder()
    let n = 0, refPage = 2  // TOC anchor pages start at 3 (cover=1, TOC=2)
    // Drop client/facilitator/review and any section the plan has
    // explicitly disabled. Empty sections DO still render — every section
    // shows by default so the plan structure is always visible.
    const isSectionRenderable = (k) => {
      if (k === "client" || k === "facilitator" || k === "review" || k === "brochures") return false
      if (!isSectionIncluded(this.plan, k)) return false
      if (k === "resourceCalendar") return this._rcVisible()
      return true
    }
    const items = order
      .filter(isSectionRenderable)
      .map(k => {
        const cfg = SECTIONS[k]
        if (!cfg) return ""
        n++
        const p = ++refPage
        // TOC rows link two ways:
        //   - href="#section-<key>"          → in-page scroll within the preview
        //   - data-action="print-preview#jumpToEditor" data-section-key=…
        //     → on click, navigates to /editor/native/section/<key>
        // so users can open the matching editor tab straight from the TOC.
        return `
          <li>
            <a class="print-toc-row"
               href="#section-${esc(k)}"
               data-action="print-preview#jumpToEditor"
               data-section-key="${esc(k)}">
              <span class="icon">${sectionIconHtml(cfg.icon)}</span>
              <span class="label"><strong>${n}. ${esc(cfg.label)}</strong><span class="leader"></span></span>
              <span class="page">${p}</span>
            </a>
          </li>`
      }).join("")
    const chrome = this._renderPageChrome()
    return `
      <section class="print-page" data-page="toc" id="section-toc">
        ${this._renderPageHeader(pageNum)}
        <h2 class="print-toc-title"><i data-lucide="list"></i> Table of Contents</h2>
        <ul class="print-toc-list">${items}</ul>
        ${chrome.sideStrip}
        ${chrome.confidential}
        ${this._renderNavRail("toc")}
      </section>`
  }

  // ---- Section page ----
  _renderSectionPage(key, sectionNum, pageNum, half, isFirstHalf) {
    const cfg = SECTIONS[key]
    if (!cfg) return null
    const renderer = RENDERERS[key]
    const body = renderer ? renderer(this.plan, sectionNum, half) : `<p class="pp-empty">Section content not yet ported.</p>`
    const chrome = this._renderPageChrome()
    // Sections whose body renderer provides its own full-bleed header.
    // Production's PDF pipeline strips the gradient banners off both
    // barriers and unmetNeeds (the bg-gradient utilities don't survive
    // pdfjs rendering), so we render them as standard h2 + content for
    // pixel parity with the reference PDF.
    const SELF_HEADER = new Set()
    // Section-level "Move to next page" chip removed — sub-section
    // chips on individual tiles / rows / sub-cards already give
    // the user fine-grained control, and a section-wide chip is
    // redundant since the first sub-unit's break drags the whole
    // section to the next page. Keep showPbChip / pbChip as ""
    // so downstream code that still references them is a no-op.
    const showPbChip = false
    const pbChip = ""
    // H2 title suffix for the Service Calendar half-pages
    // (pcp_builder.html#7428 titleSuffix). The native section H2 already
    // builds "{n}. {label}" — we append the time range as a small muted
    // span so it reads "7. Service Calendar — 12 AM to 12 PM".
    const titleSuffix = half === "am"
      ? `<span class="pp-rc-title-range">— 12 AM to 12 PM</span>`
      : half === "pm"
        ? `<span class="pp-rc-title-range">— 12 PM to 12 AM</span>`
        : half === "summary"
          ? `<span class="pp-rc-title-range">— Summary</span>`
          : ""
    // Only the first page of a multi-page section gets the anchor id
    // so deep links + nav rail land on the start of the section.
    const sectionId = (isFirstHalf === false) ? "" : ` id="section-${esc(key)}"`
    const card = SELF_HEADER.has(key)
      ? body
      : `<div class="print-card${showPbChip ? " has-pb-chip" : ""}">
          ${pbChip}
          <h2 class="print-section-h2${cfg.iconFill === false ? " is-stroke" : ""}"><span style="color:${esc(cfg.iconColor || "#6366f1")};display:inline-flex;align-items:center;">${sectionIconHtml(cfg.icon)}</span> ${sectionNum}. ${esc(cfg.label)}${titleSuffix}</h2>
          ${body}
        </div>`
    return `
      <section class="print-page" data-page="${key}"${sectionId}>
        ${this._renderBgIcons(pageNum)}
        ${this._renderPageHeader(pageNum)}
        ${card}
        ${chrome.sideStrip}
        ${chrome.confidential}
        ${this._renderNavRail(key)}
      </section>`
  }

  // Toggles plan.meta.pageBreaks[<key>] and re-renders so the next
  // pagination pass respects the new state. Mirrors production's
  // togglePageBreak() at pcp_builder.html#4097.
  togglePageBreak(event) {
    event.preventDefault()
    event.stopPropagation()
    const btn = event.currentTarget
    const key = btn.dataset.pbKey
    if (!key) return
    // Fire the chip pop animation BEFORE the re-render yanks the DOM.
    // 280ms holds the bounce while we yield to the browser, then we
    // mutate state + re-render so the new chip state lands smoothly.
    btn.classList.add("is-firing")
    setTimeout(() => {
      // Capture where the clicked chip sits in the viewport so we can pin it
      // back after render — which rebuilds the whole .print-page stack inside
      // .en-print-scroll and would otherwise reset the scroll to the top.
      const scroller  = this.hasPagesTarget ? this.pagesTarget : null
      const preTop    = btn.getBoundingClientRect().top
      const preScroll = scroller ? scroller.scrollTop : 0

      if (!this.plan.meta) this.plan.meta = {}
      if (!this.plan.meta.pageBreaks) this.plan.meta.pageBreaks = {}
      if (this.plan.meta.pageBreaks[key]) {
        delete this.plan.meta.pageBreaks[key]
      } else {
        this.plan.meta.pageBreaks[key] = true
      }
      this._savePageBreaks()   // authoritative — a removed break must actually stick
      this.render()
      requestAnimationFrame(() => {
        if (!scroller) return
        // Re-find the SAME chip (it kept its data-pb-key) and shift the
        // scroll so it lands at the same viewport position as before.
        const chip = scroller.querySelector(`[data-pb-key="${key.replace(/"/g, '\\"')}"]`)
        if (chip) scroller.scrollTop += (chip.getBoundingClientRect().top - preTop)
        else      scroller.scrollTop = preScroll
      })
    }, 280)
  }

  _savePlan() {
    savePlan(this.plan)
  }

  // Persist the page-break map AUTHORITATIVELY. savePlan() is a MERGE, and a
  // merge can never delete a key — so a break the user cleared/removed would
  // silently come back on the next load ("pagination never gets saved"). Load
  // the current plan fresh (to keep everyone else's latest edits) and replace
  // just meta.pageBreaks with our exact current set. Returns true on success.
  _savePageBreaks() {
    const stored = loadPlan() || {}
    if (!stored.meta) stored.meta = {}
    stored.meta.pageBreaks = { ...(this.plan?.meta?.pageBreaks || {}) }
    return replacePlan(stored)
  }

  // Explicit "Save pagination" — persists the current page breaks (adds AND
  // removes) so the layout you set in the preview sticks on reload / PDF.
  savePagination(event) {
    event?.preventDefault?.()
    const ok = this._savePageBreaks()   // writes the exact current break set (empty map clears all)
    if (this.hasPbMenuTarget) this.pbMenuTarget.hidden = true
    this._flashSaved(ok ? "Pagination saved ✓" : "Couldn’t save — try again")
  }

  // Brief centered confirmation toast.
  _flashSaved(msg) {
    let bar = document.getElementById("pp-save-toast")
    if (!bar) {
      bar = document.createElement("div")
      bar.id = "pp-save-toast"
      bar.style.cssText =
        "position:fixed;bottom:20px;left:50%;transform:translateX(-50%);z-index:9999;" +
        "background:#0f172a;color:#fff;padding:9px 16px;border-radius:9px;" +
        "font:600 13px/1.2 ui-sans-serif,system-ui;box-shadow:0 8px 24px rgba(0,0,0,.28);" +
        "opacity:0;transition:opacity .15s;pointer-events:none;"
      document.body.appendChild(bar)
    }
    bar.textContent = msg
    bar.style.opacity = "1"
    clearTimeout(this._saveToastTimer)
    this._saveToastTimer = setTimeout(() => { bar.style.opacity = "0" }, 1800)
  }

  // Auto-fit Behavior Communication Chart column widths.
  //
  // Iterative-probe optimizer: build off-screen replicas of the chart
  // with candidate (col1%, col2%, col3%, col4%) tuples, measure rendered
  // height, and walk toward the layout that shortens the chart the most.
  // Starts with 5% increments and refines to 2% then 1% — same strategy
  // as the legacy autoFitCommChartColWidths() at pcp_builder.html#11589
  // but adapted to the CSS-grid (.pp-comm-row) markup the native renderer
  // emits.
  autoFitCommChart(event) {
    event?.preventDefault?.()
    event?.stopPropagation?.()
    const rows = (this.plan?.about && this.plan.about.communicationChart) || []
    if (!rows.length) return

    const _escAttr = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, c => (
      { "&":"&amp;", "<":"&lt;", ">":"&gt;", '"':"&quot;", "'":"&#39;" }[c]
    ))

    // The probe matches the live renderer's wrapper width so measured
    // heights map 1:1 to what the user will see.
    const probeWidth = (() => {
      const liveRow = document.querySelector(".pp-comm-row")
      return liveRow ? liveRow.getBoundingClientRect().width : 7 * 96 // 7in fallback
    })()
    const probe = document.createElement("div")
    probe.style.cssText = `position:fixed;left:-30000px;top:0;visibility:hidden;width:${probeWidth}px;`
    probe.className = "en-print-frame"  // pick up the same theme vars
    document.body.appendChild(probe)

    const buildHTML = (p) => {
      const gs = `grid-template-columns:${p.map(v => v + "%").join(" ")}`
      const head = `<div class="pp-comm-row pp-comm-head" style="${gs}"><div>When this happens…</div><div>I do this…</div><div>It means…</div><div>I want you to…</div></div>`
      const body = rows.map(r => `<div class="pp-comm-row pp-comm-body" style="${gs}"><div>${_escAttr(r.happening || "")}</div><div class="pp-comm-bold">${_escAttr(r.behavior || "")}</div><div class="pp-comm-meaning">${_escAttr(r.meaning || "")}</div><div class="pp-comm-support">${_escAttr(r.support || "")}</div></div>`).join("")
      return `<div class="pp-comm-wrap" data-probe="t">${head}${body}</div>`
    }
    const measure = (p) => {
      probe.innerHTML = buildHTML(p)
      return probe.querySelector("[data-probe]").offsetHeight
    }

    const MIN = 10, MAX = 60
    const clampNorm = (arr) => {
      const a = arr.map(v => Math.max(MIN, Math.min(MAX, v)))
      const s = a.reduce((x, y) => x + y, 0)
      return a.map(v => +(v / s * 100).toFixed(1))
    }

    const initial = (Array.isArray(this.plan?.meta?.commChartColPcts) && this.plan.meta.commChartColPcts.length === 4)
      ? this.plan.meta.commChartColPcts
      : [18, 30, 18, 34]
    let best = clampNorm(initial.slice())
    let bestH = measure(best)
    const steps = [5, 2, 1]
    let probes = 1
    const maxProbes = 400

    for (const step of steps) {
      let improved = true
      let iter = 0
      while (improved && iter < 20 && probes < maxProbes) {
        improved = false
        iter++
        for (let from = 0; from < 4 && probes < maxProbes; from++) {
          for (let to = 0; to < 4 && probes < maxProbes; to++) {
            if (from === to) continue
            if (best[from] - step < MIN) continue
            if (best[to] + step > MAX) continue
            const cand = best.slice()
            cand[from] -= step; cand[to] += step
            const h = measure(cand); probes++
            const spreadBest = Math.max(...best) - Math.min(...best)
            const spreadCand = Math.max(...cand) - Math.min(...cand)
            if (h < bestH - 2 || (Math.abs(h - bestH) <= 2 && spreadCand < spreadBest)) {
              bestH = h; best = cand; improved = true
            }
          }
        }
      }
    }
    document.body.removeChild(probe)

    if (!this.plan.meta) this.plan.meta = {}
    this.plan.meta.commChartColPcts = clampNorm(best)
    this._savePlan()
    this.render()
  }

  // Drag-to-resize a Behavior Communication Chart column boundary.
  //
  // Wired on mousedown / touchstart of each .pp-comm-resize handle
  // (which sit on the right edge of header cells 0, 1, 2). Dragging
  // the handle shifts width between cell N (data-col-idx) and cell
  // N+1 — same model as the legacy startBCColResize() at
  // pcp_builder.html#11694, adapted to the CSS-grid markup.
  //
  // While dragging, every .pp-comm-row gets the same inline
  // grid-template-columns so the layout updates in place without a
  // re-render. Drop persists final pcts to plan.meta.commChartColPcts.
  startCommChartColResize(event) {
    event.preventDefault?.()
    event.stopPropagation?.()
    const handle = event.currentTarget
    const leftIdx = parseInt(handle.dataset.colIdx, 10)
    if (Number.isNaN(leftIdx) || leftIdx < 0 || leftIdx > 2) return

    const row = handle.closest(".pp-comm-row")
    if (!row) return
    const rowWidth = row.getBoundingClientRect().width
    if (!rowWidth) return

    const initial = (Array.isArray(this.plan?.meta?.commChartColPcts) && this.plan.meta.commChartColPcts.length === 4)
      ? this.plan.meta.commChartColPcts.slice()
      : [18, 30, 18, 34]
    const startSum = initial[leftIdx] + initial[leftIdx + 1]
    const startX = (event.touches ? event.touches[0].clientX : event.clientX) || 0
    const MIN_PCT = 8

    handle.classList.add("dragging")
    document.body.style.userSelect = "none"
    document.body.style.cursor = "col-resize"

    const rows = document.querySelectorAll(".pp-comm-row")
    const applyPcts = (pcts) => {
      const gs = `grid-template-columns: ${pcts.map(p => p + "%").join(" ")}`
      rows.forEach(r => {
        // Preserve any prior style attrs by setting only the relevant prop
        r.style.gridTemplateColumns = pcts.map(p => p + "%").join(" ")
      })
    }
    let lastPcts = initial.slice()

    const onMove = (e) => {
      const x = (e.touches ? e.touches[0].clientX : e.clientX) || startX
      const deltaPct = ((x - startX) / rowWidth) * 100
      let leftPct  = initial[leftIdx]     + deltaPct
      let rightPct = initial[leftIdx + 1] - deltaPct
      if (leftPct  < MIN_PCT) { leftPct  = MIN_PCT; rightPct = startSum - MIN_PCT }
      if (rightPct < MIN_PCT) { rightPct = MIN_PCT; leftPct  = startSum - MIN_PCT }
      const cur = initial.slice()
      cur[leftIdx]     = leftPct
      cur[leftIdx + 1] = rightPct
      lastPcts = cur
      applyPcts(cur)
    }
    const onUp = () => {
      document.removeEventListener("mousemove", onMove)
      document.removeEventListener("mouseup",   onUp)
      document.removeEventListener("touchmove", onMove, { passive: false })
      document.removeEventListener("touchend",  onUp)
      handle.classList.remove("dragging")
      document.body.style.userSelect = ""
      document.body.style.cursor = ""
      // Persist final pcts. Round to 1 decimal so the saved tuple
      // sums close to 100 without floating-point drift.
      if (!this.plan.meta) this.plan.meta = {}
      this.plan.meta.commChartColPcts = lastPcts.map(v => +(+v).toFixed(1))
      this._savePlan()
    }
    document.addEventListener("mousemove", onMove)
    document.addEventListener("mouseup",   onUp)
    document.addEventListener("touchmove", onMove, { passive: false })
    document.addEventListener("touchend",  onUp)
  }

  // Pagination menu — opens an overlay listing every configured break
  // with a remove button per entry, plus a Clear All option. Mirrors
  // production's right-click context menu (pcp_builder.html#4134).
  togglePbMenu(event) {
    event?.preventDefault?.()
    event?.stopPropagation?.()
    if (!this.hasPbMenuTarget) return
    const wasOpen = !this.pbMenuTarget.hidden
    this.pbMenuTarget.hidden = wasOpen
    if (!wasOpen) {
      this._refreshPbMenu()
      // Close on outside click.
      this._onDocClick = (e) => {
        if (!this.pbMenuTarget.contains(e.target) && e.target !== this.pbMenuBtnTarget) {
          this.pbMenuTarget.hidden = true
          document.removeEventListener("click", this._onDocClick, true)
        }
      }
      setTimeout(() => document.addEventListener("click", this._onDocClick, true), 0)
    }
  }

  _refreshPbMenu() {
    if (!this.hasPbMenuListTarget) return
    const breaks = this.plan?.meta?.pageBreaks || {}
    const entries = Object.keys(breaks).filter(k => breaks[k])
    if (entries.length === 0) {
      this.pbMenuListTarget.innerHTML = `<li class="en-pp-menu-empty">No page breaks configured</li>`
      return
    }
    const PB_LABELS = {
      about_lives: "About 1.1 Who Lives With [FirstName]",
      about_comm:  "About 1.2 Communication Style",
      about_school:"About 1.3 School Details",
      about_work:  "About 1.4 Work/Business Details",
      wt_activities:"Strengths 4.1 Favorite Activities",
      wt_places:   "Strengths 4.2 Favorite Places",
      wt_events:   "Strengths 4.3 Special Events",
      wt_foods:    "Strengths 4.4 Favorite Foods",
      wt_possessions:"Strengths 4.5 Strengths",
      wt_admired:  "Strengths 4.6 Qualities Admired",
      nsf_triggers:"Challenges 5.1 Things That Upset [FirstName]",
      nsf_sensory: "Challenges 5.2 Sensory Sensitivities",
      nsf_situations:"Challenges 5.3 Situations I'd Rather Skip",
      nsf_foods:   "Challenges 5.4 Foods I Dislike",
      nsf_environments:"Challenges 5.5 Places That Don't Work",
      dil_morning: "Day-in-Life Weekday Morning Time",
      dil_day:     "Day-in-Life Weekday School Time",
      dil_afternoon:"Day-in-Life Weekday After School",
      dil_night:   "Day-in-Life Weekday Before Bed",
      dil_afterBed:"Day-in-Life Weekday After Bed",
      dil_wkMorning:"Day-in-Life Weekend Morning Time",
      dil_wkDay:   "Day-in-Life Weekend Home & Community",
      dil_wkNight: "Day-in-Life Weekend Before Bed",
      dil_wkAfterBed:"Day-in-Life Weekend After Bed",
      imp_to:      "Important 7.1 Important TO [FirstName]",
      imp_for:     "Important 7.2 Important FOR [FirstName]",
      bd_hopes:    "Big Dreams 8.1 Hopes",
      bd_dreams:   "Big Dreams 8.2 Big Dreams",
      sm_qualities:"Support Match 10.1 Qualities",
      sm_skills:   "Support Match 10.2 Skills Needed",
      sm_personality:"Support Match 10.3 Personality Match",
      sm_dealBreakers:"Support Match 10.4 Deal Breakers",
      work_works:  "What Works 11.1 What's Working",
      work_notWorking:"What Works 11.2 What's not working",
      cs_plain:    "Current Services 14 — manual entries"
    }
    const labelFor = (k) => {
      if (PB_LABELS[k]) return PB_LABELS[k]
      if (k.startsWith("sec_")) {
        const key = k.slice(4)
        return SECTIONS[key]?.label || key
      }
      if (k.startsWith("barrier_")) return "Barrier #" + k.slice(8)
      if (k.startsWith("goal_"))    return "Goal #" + k.slice(5)
      if (k.startsWith("unmet_"))   return "Unmet need #" + k.slice(6)
      // cs_<service_type_slug> — current-services linked card chip.
      // Recover the source type by un-slugging the underscores so the
      // menu reads "Current Services — Speech Therapy" instead of
      // "cs_speech_therapy".
      if (k.startsWith("cs_")) {
        const slug = k.slice(3).replace(/_/g, " ")
          .replace(/\b\w/g, c => c.toUpperCase())
        return `Current Services — ${slug}`
      }
      return k
    }
    this.pbMenuListTarget.innerHTML = entries.map(k => `
      <li class="en-pp-menu-item">
        <span class="en-pp-menu-itemlabel"><i data-lucide="arrow-down-to-line"></i> Break before ${esc(labelFor(k))}</span>
        <button type="button" class="en-pp-menu-remove"
                data-action="print-preview#removePageBreak"
                data-pb-key="${esc(k)}"
                title="Remove this page break">
          <i data-lucide="x"></i>
        </button>
      </li>`).join("")
    renderIcons(this.element)
  }

  removePageBreak(event) {
    event.preventDefault()
    event.stopPropagation()
    const key = event.currentTarget?.dataset?.pbKey
    if (!key || !this.plan?.meta?.pageBreaks) return
    delete this.plan.meta.pageBreaks[key]
    this._savePlan()
    this.render()
    this._refreshPbMenu()
  }

  // TOC click behaviour:
  //   - plain click  → anchor-scroll within the preview to #section-<key>
  //     (default browser handling of the wrapping <a href="#section-…">)
  //   - Ctrl/Cmd/Shift+click → open the matching editor section in a new
  //     tab so the user can edit straight from the TOC
  // Earlier this had its modifier check inverted, so plain clicks were
  // navigating away instead of scrolling the preview. (SCOP-004D fix.)
  jumpToEditor(event) {
    // Skip if the click landed on a chip / nested button.
    if (event.target.closest(".dil-pb-chip")) return
    // Plain click: do nothing, let the href anchor scroll in place.
    if (!(event.metaKey || event.ctrlKey || event.shiftKey)) {
      // Smooth-scroll fallback for browsers that ignore CSS scroll-behavior.
      const key = event.currentTarget?.dataset?.sectionKey
      if (!key) return
      const target = document.getElementById(`section-${key}`)
      if (target) {
        event.preventDefault()
        target.scrollIntoView({ behavior: "smooth", block: "start" })
      }
      return
    }
    const key = event.currentTarget.dataset.sectionKey
    if (!key) return
    event.preventDefault()
    window.open(`/editor/native/${encodeURIComponent(key)}`, "_blank", "noopener")
  }

  // Cross-ref chip click (currentServices section). Default browser
  // hash navigation scrolls the document, but the preview lives
  // inside a nested .en-print-scroll container — so the page never
  // moved when the user clicked "Goal 13.5". We intercept the click,
  // call scrollIntoView on the target ourselves, and add the :target
  // highlight ring by toggling location.hash to the matching ID.
  jumpToRef(event) {
    const id = event.currentTarget?.dataset?.targetId
    if (!id) return
    event.preventDefault()
    const target = this.element.querySelector(`#${CSS.escape(id)}`) ||
                   document.getElementById(id)
    if (!target) return
    target.scrollIntoView({ behavior: "smooth", block: "center" })
    // Re-setting location.hash keeps the :target highlight ring on
    // the destination card without triggering the browser's own
    // jump-to-anchor (we've already scrolled).
    if (history.replaceState) {
      history.replaceState(null, "", `#${id}`)
    }
  }

  // Scroll to the element named by the current URL hash after the async
  // render (two rAFs so layout has settled). :target styling attaches
  // automatically since the hash is already set.
  _scrollToInitialHash() {
    const id = (window.location.hash || "").replace(/^#/, "")
    if (!id) return
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const el = document.getElementById(id)
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" })
    }))
  }

  clearAllPageBreaks(event) {
    event?.preventDefault?.()
    if (!this.plan?.meta) return
    this.plan.meta.pageBreaks = {}
    this._savePageBreaks()   // authoritative — clearing must actually persist (merge can't delete)
    this.render()
    if (this.hasPbMenuTarget) this.pbMenuTarget.hidden = true
  }

  _participantName() {
    const c = this.plan.client || {}
    return (c.name && c.name.trim()) ||
           [c.firstName, c.lastName].filter(Boolean).join(" ") ||
           "Participant"
  }

  _load() {
    // PDF-render path: when this page is fetched by headless Chrome
    // via /internal/native_pdf_render the plan is pre-injected as a
    // global. Use it instead of localStorage (which the headless
    // browser doesn't share with the user's tab).
    if (window.PCPM_PRELOAD_PLAN) return window.PCPM_PRELOAD_PLAN
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      return raw ? JSON.parse(raw) : {}
    } catch (_) { return {} }
  }
}
